// Currency + language constants shared by the app, plus a lightweight
// translation layer (no react-intl / i18next dependency).
//
// Usage:
//   import { t, setLocale, useLocale } from "./lib/i18n";
//   t("auth.login")          // → "登入" (zh-HK) or "Log in" (en)
//   setLocale("en")          // switches and persists to localStorage
//   const l = useLocale()    // hook for components to re-render on change
//
// Add keys to STRINGS as code migrates from hard-coded literals. A
// missing key falls back to zh-HK, then to the key itself, so a
// partially-translated screen stays functional.

import { useEffect, useState } from "react";

const STRINGS = {
  "zh-HK": {
    "lang.name": "繁體中文",
    "common.cancel": "取消", "common.confirm": "確認", "common.save": "儲存",
    "common.delete": "刪除", "common.edit": "編輯", "common.back": "返回",
    "common.next": "下一步", "common.loading": "載入中…", "common.error": "錯誤",
    "common.retry": "重試", "common.close": "關閉", "common.search": "搜尋",
    "common.add": "新增", "common.copy": "複製", "common.copied": "已複製",
    "common.logout": "登出", "common.settings": "設定", "common.today": "今天",
    "auth.login": "登入", "auth.register": "註冊", "auth.email": "電郵",
    "auth.password": "密碼", "auth.phone": "電話", "auth.continue": "繼續",
    "auth.googleSignin": "使用 Google 登入",
    "tab.home": "主頁", "tab.book": "預約", "tab.pets": "毛孩",
    "tab.history": "紀錄", "tab.profile": "我的",
    "booking.selectPet": "選擇毛孩", "booking.selectService": "選擇服務",
    "booking.selectStaff": "選擇美容師", "booking.selectTime": "選擇時段",
    "booking.confirm": "確認預約", "booking.success": "預約成功",
    "booking.total": "總金額", "booking.deposit": "訂金",
    "status.pending": "待開始", "status.checkedin": "已到店",
    "status.grooming": "美容中", "status.done": "已完成",
    "status.cancelled": "已取消", "status.noshow": "未到店",
    // Customer home
    "home.wallet": "錢包", "home.points": "積分", "home.grooms": "美容",
    "home.upcoming": "即將到來", "home.history": "美容紀錄",
    "home.noUpcoming": "暫無預約", "home.bookNow": "立即預約",
    "home.times": "次",
    // Customer book flow
    "book.pickPet": "選擇毛孩", "book.pickService": "需要什麼服務？",
    "book.pickStaff": "選擇美容師", "book.anyStaff": "任何美容師",
    "book.pickTime": "選擇時段", "book.summary": "確認預約細節",
    "book.subtotal": "小計", "book.totalAmount": "總金額",
    "book.usePoints": "使用積分抵扣", "book.confirmBtn": "確認預約",
    "book.addPet": "新增另一隻毛孩",
    // Profile
    "profile.editProfile": "編輯個人資料", "profile.name": "姓名",
    "profile.changeShop": "切換店舖", "profile.signOut": "登出",
    // History
    "hist.all": "全部", "hist.viewReport": "查看報告",
    "hist.cancelBooking": "取消預約", "hist.rate": "評價",
    // Owner-side settings
    "settings.title": "設定", "settings.language": "語言",
    "settings.shopBasic": "基本資料", "settings.shopBrand": "品牌與主題",
    "settings.shopHours": "營業時間", "settings.shopPolicy": "政策與功能",
    "settings.payment": "收款方式", "settings.analytics": "分析",
    "settings.reviews": "評價", "settings.messaging": "通訊",
    "settings.terms": "條款與聲明", "settings.activity": "活動記錄",
    "settings.system": "系統與備份",
    "settings.group.shop": "店鋪", "settings.group.ops": "營運",
    // Owner-side tabs
    "owner.tab.dashboard": "主頁", "owner.tab.schedule": "日歷",
    "owner.tab.crm": "客戶", "owner.tab.staff": "員工",
    "owner.tab.payroll": "薪資", "owner.tab.reports": "報表",
    "owner.tab.settings": "設定",
    // Weekday short labels
    "day.0": "日", "day.1": "一", "day.2": "二", "day.3": "三",
    "day.4": "四", "day.5": "五", "day.6": "六",
    // Common owner verbs
    "owner.appointmentsToday": "今日預約",
    "owner.revenueToday": "今日營收",
    "owner.shopOpen": "今日營業中", "owner.shopClosed": "今日休息中",
    // Pet form
    "pet.name": "名字", "pet.weight": "體重", "pet.weightKg": "體重 (kg)",
    "pet.breed": "品種", "pet.birthday": "生日", "pet.type": "類型",
    "pet.dog": "🐶 狗狗", "pet.cat": "🐱 貓貓",
    "pet.coat": "毛長", "pet.coatShort": "短毛", "pet.coatLong": "長毛", "pet.coatMedium": "中毛",
    "pet.allergies": "過敏", "pet.vaccineDate": "上次疫苗日",
    "pet.vetNote": "獸醫備註", "pet.add": "新增毛孩",
    "pet.edit": "編輯毛孩",
    // Booking summary
    "summary.mainService": "主服務", "summary.addons": "加購",
    "summary.spa": "SPA", "summary.surcharge": "附加費",
    "summary.discount": "折扣", "summary.paid": "已付", "summary.due": "待收",
    "summary.depositPaid": "已付訂金", "summary.settled": "已結清 ✓",
    "summary.points": "積分", "summary.pointsEarn": "完成後可獲得",
    // Walk-in
    "walkin.title": "即場到店登記",
    "walkin.newCustomer": "新增客戶",
    "walkin.searchCustomer": "搜尋客戶（姓名／電話）",
    "walkin.discount": "折扣",
    "walkin.reason": "原因（選填）",
    "walkin.staffRemark": "員工備註",
    "walkin.useWallet": "使用儲值扣款",
    "walkin.submit": "確認登記",
    "walkin.submitStart": "登記 & 立即開始美容",
    "walkin.submitAndAnother": "儲存並登記同一客戶另一隻毛孩",
    // Toasts (used by toast() across the app)
    "toast.saved": "已儲存",
    "toast.deleted": "已刪除",
    "toast.copyOk": "已複製",
    "toast.copyFail": "複製失敗",
    "toast.saveFail": "儲存失敗",
    "toast.networkErr": "網絡錯誤，請稍後再試",
    // Report field labels (with emoji baked in for the form headers)
    "report.skin": "皮膚 🩺", "report.ear": "耳朵 👂",
    "report.teeth": "牙齒 🦷", "report.mood": "情緒 😊",
    "report.nails": "指甲 💅", "report.analGlands": "肛門腺",
    "report.parasites": "寄生蟲 🔍", "report.eyeDischarge": "眼睛分泌",
    "report.matting": "打結程度 🪮",
    // Common chip values shared across fields
    "chip.normal": "正常", "chip.good": "良好", "chip.none": "無",
    "chip.notNeeded": "不需要", "chip.notFound": "無發現",
    "chip.trimmed": "已修剪", "chip.expressed": "已擠",
    // Skin chips
    "chip.skin.dry": "乾燥", "chip.skin.flakes": "有皮屑",
    "chip.skin.rash": "紅斑", "chip.skin.oily": "油性",
    "chip.skin.inflamed": "發炎",
    // Ear
    "chip.ear.discharge": "有分泌物", "chip.ear.odor": "異味",
    "chip.ear.red": "紅腫", "chip.ear.mites": "耳螨疑似",
    // Teeth
    "chip.teeth.mild": "輕度牙石", "chip.teeth.heavy": "重度牙石",
    "chip.teeth.needsClean": "建議洗牙", "chip.teeth.missing": "脫落牙齒",
    // Mood
    "chip.mood.nervous": "緊張", "chip.mood.excited": "興奮",
    "chip.mood.scared": "害怕", "chip.mood.aggressive": "攻擊性",
    // Nails
    "chip.nails.long": "過長未剪", "chip.nails.broken": "斷裂",
    // Anal glands
    "chip.ag.inflamed": "發炎", "chip.ag.blocked": "堵塞",
    // Parasites
    "chip.para.fleas": "跳蚤", "chip.para.ticks": "蜱蟲",
    "chip.para.mites": "耳螨",
    // Eye discharge
    "chip.eye.little": "少量", "chip.eye.much": "眼屎多",
    "chip.eye.red": "發紅", "chip.eye.abnormal": "異常",
    // Matting
    "chip.mat.mild": "輕微", "chip.mat.medium": "中度",
    "chip.mat.severe": "嚴重需剃毛",
    // Service / addon / staff edit sheets
    "svc.sheet.edit": "服務 — 編輯", "svc.sheet.add": "服務 — 新增",
    "svc.sheet.spaEdit": "SPA 服務 — 編輯", "svc.sheet.spaAdd": "SPA 服務 — 新增",
    "svc.del": "刪除服務", "svc.name": "服務名稱",
    "svc.basePrice": "基本價格 ($)", "svc.description": "描述",
    "svc.duration": "預估時間（分鐘）",
    "addon.edit": "加購 — 編輯", "addon.add": "加購 — 新增",
    "addon.name": "加購名稱", "addon.price": "價格 ($)",
    "addon.del": "刪除加購",
    "staff.edit": "員工 — 編輯", "staff.add": "員工 — 新增",
    "staff.name": "姓名", "staff.role": "職位",
    "staff.commissionMode": "服務抽成方式",
    "staff.joinDate": "入職日期", "staff.leaveDate": "離職日期",
    "staff.del": "刪除員工",
    "common.required": "必填",
    "ph.searchApt": "搜尋預約...",
    "ph.searchCustomer": "搜尋客戶姓名 / 電話...",
    "ph.searchNamePhone": "搜尋姓名或電話...",
    "ph.searchGlobal": "搜尋客戶、預約、寵物...",
    "ph.searchTenant": "搜尋商戶名稱...",
    // EditApt sheet
    "apt.detail": "預約詳情", "apt.edit": "編輯預約",
    "apt.surcharge": "附加費用", "apt.surchargeAdd": "加收費用",
    "apt.discount": "折扣", "apt.discountOpt": "折扣（選填）",
    "apt.staffNote": "員工備註（會在預約卡顯示）",
    "apt.del": "永久刪除此預約",
    "apt.delConfirm": "確定永久刪除此預約？此操作無法復原（如想保留紀錄，請改用「取消預約」）。",
    "apt.unsaved": "有未儲存的修改，確定離開？",
    // Booking time/date step
    "book.pickDate": "選擇日期",
    "book.availableSlots": "可選時段",
    "book.noSlotsToday": "今日已滿，請改選其他日期",
    "book.confirm": "確認預約",
    "book.depositNow": "立即支付訂金",
    "book.usePointsLabel": "使用積分抵扣",
    "book.confirmBookingBtn": "立即預約",
    // Customer edit sheet
    "cust.edit": "編輯客戶資料",
    "cust.name": "姓名", "cust.phone": "電話", "cust.phone2": "備用電話",
    "cust.email": "電郵", "cust.address": "地址", "cust.tags": "標籤",
    // Validation toasts
    "val.enterName": "請填寫姓名",
    "val.enterEmail": "請填寫 Email",
    "val.enterPhone": "請輸入有效電話號碼",
    "val.enterFirstNameBreed": "請填寫名字和品種",
    "val.enterPetName": "請輸入名字",
    "val.enterBreed": "請輸入品種",
    "val.enterWeight": "請輸入重量",
    "val.enterAmount": "請輸入有效金額",
    "val.enterNewCustName": "請填寫新客戶姓名",
    "val.pickCustomer": "請選擇或新增客戶",
    "val.fillPetInfo": "請填寫毛孩姓名、品種、體重",
    "val.pickPet": "請選擇或新增毛孩",
    "val.pickService": "請選擇服務",
    "val.pickStaff": "請選擇美容師",
    "val.pickTime": "請選擇開始時間",
    "val.pickDate": "請選擇日期",
    "val.enterServiceName": "請填寫服務名稱",
    "val.enterAddonName": "請填寫加購名稱",
    "val.enterInvName": "請填寫耗材名稱",
    "val.enterPromoCode": "請輸入優惠碼",
    "val.enterPackageName": "請輸入名稱",
    "val.enterPetNameRequired": "請填寫名字",
    "val.petBirthdayFuture": "生日不可以遲過今日，請檢查日期",
    "val.petBirthdayTooOld": "毛孩年齡睇落太大，請檢查生日年份係咪填錯",
    // Common booking nav buttons
    "btn.next": "下一步", "btn.back": "返回",
    "btn.bookNow": "立即預約",
    "btn.continueGoogle": "使用 Google 繼續",
    // Payment sheet
    "pay.title": "收款 💰", "pay.notFound": "找不到預約",
    "pay.pet": "毛孩", "pay.svcDefault": "美容服務", "pay.remove": "移除",
    "pay.surchargePh": "附加費名稱（例：打結處理 / 大型犬 / 安撫費）",
    "pay.addSpaAddon": "+ 加 SPA / 加購", "pay.addSurcharge": "+ 加附加費",
    "pay.noAvailable": "暫無可加項目", "pay.addonLabel": "加購",
    "pay.subtotal": "小計", "pay.discount": "折扣",
    "pay.depositPaid": "已收訂金", "pay.balanceDue": "結餘應收",
    "pay.cancel": "取消", "pay.continue": "繼續 →",
    "pay.methodFallback": "收款方式", "pay.cash": "現金 💵",
    "pay.back": "← 返回", "pay.pickMethod": "選擇付款方式",
    "pay.noMethods": "未啟用任何收款方式 — 暫用「現金」。請去 設定 → 收款 → 收款方式 啟用。",
    "pay.due": "應付金額", "pay.failed": "收款失敗：",
    "pay.dup": "收款已紀錄（防止重複）",
    "pay.collected": "已收款", "pay.paidTick": "已收款 ✓",
    "pay.confirm": "確認收款",
    // EditApt
    "edit.notSaved": "有未儲存的修改，確定離開？",
    "edit.lockedPast": "已過往預約 · 狀態已鎖定（仍可編輯報告 / 補上相片）",
    "edit.time": "時間", "edit.date": "日期", "edit.slot": "時段",
    "edit.staff": "負責員工", "edit.service": "服務",
    "edit.changeSvc": "更改主服務", "edit.note": "員工備註",
    "edit.del": "永久刪除此預約",
    "edit.cancelApt": "取消預約", "edit.restoreApt": "復原預約",
    "edit.history": "過往紀錄", "edit.records": "收款紀錄",
    "edit.no記錄": "暫無收款紀錄",
    // Walk-in
    "wi.title": "即場到店登記 🚶",
    "wi.newCust": "新增客戶", "wi.pickCust": "選擇客戶",
    "wi.pickPet": "選擇毛孩", "wi.addPet": "新增毛孩",
    "wi.svc": "主服務", "wi.spa": "SPA", "wi.addons": "加購",
    "wi.package": "使用套票", "wi.walletUse": "從儲值扣款",
    "wi.deposit": "訂金", "wi.start": "登記並開始美容", "wi.regOnly": "只登記",
    // Marketing landing — rewritten in standard written Chinese so HK
    // + Taiwan owners can both read it (was Cantonese before).
    "mk.nav.login": "登入",
    "mk.nav.startFree": "免費開店 →",
    "mk.hero.badge": "專為寵物美容店打造",
    "mk.hero.titleLine1": "告別紙筆與 Excel，",
    "mk.hero.titleLine2": "美容店一個 App 全搞定",
    "mk.hero.subtitle": "客戶自助預約 · 拖拉式日程 · 自動 WhatsApp · 智慧定價 · 套票錢包 · 自動計薪。",
    "mk.hero.subtitleLine2": "專為香港 / 台灣 / 澳門 寵物美容店設計。",
    "mk.hero.ctaPrimary": "免費開店",
    "mk.hero.ctaSecondary": "已有帳號？登入",
    "mk.hero.bullets": "✓ 免信用卡 · ✓ 30 秒開店 · ✓ 永久免費基本版",
    "mk.hero.mockToday": "今日預約",
    "mk.painsHeading.label": "為何選擇 PettoBook？",
    "mk.painsHeading.title": "美容店日常痛點 — 我們已經幫您解決 ✨",
    "mk.painsHeading.sub": "由業界人士共同設計，6 個常見痛點 → 6 個內建解決方案。",
    "mk.pain1.tag": "排程",
    "mk.pain1.pain": "白板 + Excel 排程，改期就撞單",
    "mk.pain1.detail": "客戶臨時改期，用紙筆劃掉重寫；另一位美容師沒看到變動，結果兩單同時到店。",
    "mk.pain1.fix": "拖拉式日程，自動防止衝突",
    "mk.pain1.b1": "預約可拖拉即時改期、改派美容師",
    "mk.pain1.b2": "系統自動偵測時段衝突，不允許儲存",
    "mk.pain1.b3": "客戶會自動收到 WhatsApp 改期通知",
    "mk.pain2.tag": "預約",
    "mk.pain2.pain": "電話與 WhatsApp 訊息接不停",
    "mk.pain2.detail": "每天花 1-2 小時逐個確認預約；忙起來就會漏接，客戶等久就轉去別家。",
    "mk.pain2.fix": "客戶 24 小時自助預約",
    "mk.pain2.b1": "一條連結，貼到 IG / FB / WhatsApp 即可",
    "mk.pain2.b2": "客戶自行選擇服務、美容師、時段，預約即進入日曆",
    "mk.pain2.b3": "新預約即時推播至店家手機",
    "mk.pain3.tag": "定價",
    "mk.pain3.pain": "計價需要查表，新人經常算錯",
    "mk.pain3.detail": "同一隻狗，3kg 與 8kg 收費不同；長毛 / 短毛 / 打結各有附加費。靠人腦記，永遠有出錯。",
    "mk.pain3.fix": "智慧定價矩陣，輸入體重即顯示價格",
    "mk.pain3.b1": "按品種 / 體重 / 毛長自動計算價格",
    "mk.pain3.b2": "SPA、加購、附加費一鍵加入",
    "mk.pain3.b3": "折扣、套票扣減自動處理",
    "mk.pain4.tag": "薪資",
    "mk.pain4.pain": "月結薪資要算到深夜",
    "mk.pain4.detail": "每位美容師的抽成都不同，還要算指定服務、套票抽成、扣減罰款，每月結帳要兩三天。",
    "mk.pain4.fix": "自動計薪 + 抽成 + 排班統計",
    "mk.pain4.b1": "按服務 / 套票分別設定抽成 %",
    "mk.pain4.b2": "工時、加班、扣減、轉帳紀錄一頁清楚",
    "mk.pain4.b3": "可隨時匯出工資單給員工確認",
    "mk.pain5.tag": "套票",
    "mk.pain5.pain": "紙本套票 / 積分卡，遺失就整單虧本",
    "mk.pain5.detail": "客戶說卡遺失、用過幾次也記不清楚——員工查不到紀錄，最後通常選擇讓步。",
    "mk.pain5.fix": "電子套票 + 積分 + 儲值錢包",
    "mk.pain5.b1": "套票次數即時扣減，雲端紀錄不會遺失",
    "mk.pain5.b2": "積分自動派發，可兌換現金折扣或贈品",
    "mk.pain5.b3": "客戶可在自己 App 隨時查看餘額",
    "mk.pain6.tag": "私隱與安全",
    "mk.pain6.pain": "客戶資料散落在多個群組與 Excel",
    "mk.pain6.detail": "WhatsApp、備忘錄、Excel、印出的登記簿——員工離職就有外洩風險，又不符合 GDPR。",
    "mk.pain6.fix": "雲端集中、權限分級、自動備份",
    "mk.pain6.b1": "員工角色 + 服務權限獨立設定",
    "mk.pain6.b2": "每日自動備份 + 7 日時間點還原",
    "mk.pain6.b3": "GDPR 一鍵下載 / 刪除個人資料",
    "mk.cmp.label": "對比",
    "mk.cmp.title": "為什麼選 PettoBook，而不是紙筆 / 通用預約 SaaS？",
    "mk.cmp.colPaper": "📓 紙筆 / Excel",
    "mk.cmp.colGeneric": "🌐 通用 SaaS",
    "mk.cmp.colUs": "🐾 PettoBook",
    "mk.cmp.r1": "按毛長 / 體重智慧定價",
    "mk.cmp.r2": "寵物獨立檔案 + 健康紀錄",
    "mk.cmp.r3": "套票 / 積分 / 儲值錢包",
    "mk.cmp.r4": "美容師抽成自動計薪",
    "mk.cmp.r5": "WhatsApp 預約通知",
    "mk.cmp.r6": "客戶自助預約連結",
    "mk.cmp.r7": "GDPR 數據保護",
    "mk.cmp.r8": "繁中介面 + 港 / 台 / 澳支援",
    "mk.cmp.legend": "✓ 內建 · △ 部分支援 / 需另購 · ✗ 不支援",
    "mk.how.label": "3 步開店",
    "mk.how.title": "從零開店到收到第一單，比泡杯奶茶還快 🧋",
    "mk.how.s1.title": "30 秒開店",
    "mk.how.s1.body": "選方案、設定店名與預約連結，系統自動建立 4 個基本服務、3 個 SPA、3 項加購，即可營業。",
    "mk.how.s2.title": "分享預約連結",
    "mk.how.s2.body": "pettobook.com/shop/your-shop 貼到 WhatsApp、IG、Facebook，客戶點開即可自助預約。",
    "mk.how.s3.title": "管理日程與客戶",
    "mk.how.s3.body": "預約自動進入日曆並通知客戶；每隻毛孩都有獨立檔案、照片與健康備註。",
    "mk.price.label": "方案",
    "mk.price.title": "由小店到連鎖，都有適合方案 💰",
    "mk.price.sub": "可隨時升級或降級，無綁約",
    "mk.price.popular": "最受歡迎 🌟",
    "mk.price.free": "免費",
    "mk.price.permonth": "/月",
    "mk.price.unlimitedStaff": "員工不限",
    "mk.price.maxStaffPrefix": "最多 ",
    "mk.price.maxStaffSuffix": " 位美容師",
    "mk.price.btnFree": "免費開始",
    "mk.price.btnChoose": "選擇方案",
    "mk.price.f.scheduling": "拖拉式日程",
    "mk.price.f.crm": "客戶 CRM",
    "mk.price.f.whatsapp": "WhatsApp / SMS / Email",
    "mk.price.f.analytics": "經營分析",
    "mk.price.f.payroll": "薪資與抽成計算",
    "mk.price.f.posLite": "收銀 POS（100 件商品）",
    "mk.price.f.posStandard": "收銀 POS：庫存 / 退款 / 交更（500 件商品）",
    "mk.price.f.posFull": "收銀 POS 全功能：採購 / AI（商品無上限）",
    "mk.price.f.smartPricing": "智慧定價矩陣",
    "mk.price.f.recurring": "定期預約",
    "mk.cta.title": "準備好讓店面更專業了嗎？",
    "mk.cta.sub": "30 秒註冊，基本功能永久免費。已有 100+ 寵物美容店信賴。",
    "mk.cta.btn": "立即免費開店",
    "mk.foot.copy": "© PettoBook · 為毛孩而設計",
    "mk.foot.privacy": "私隱政策",
    "mk.foot.terms": "服務條款",
    "mk.foot.contact": "聯絡我們",
    "mk.mock.headerToday": "📅 今日日曆",
    "mk.mock.headerTodaySub": "今天 · 5 單",
    "mk.mock.bookingHeader": "🐾 Furmily Pet Salon",
    "mk.mock.bookingHeaderSub": "客戶的自助預約頁面",
    "mk.mock.step1": "1. 選擇服務",
    "mk.mock.step2": "2. 選擇時段",
    "mk.mock.confirmBtn": "確認預約",
    "mk.mock.pricingHeader": "✂️ 沖涼剪毛 — Momo",
    "mk.mock.pricingSub": "智慧定價已套用",
    "mk.mock.pricingDog": "貴婦犬 · 4kg · 長毛",
    "mk.mock.autoDetect": "自動辨識",
    "mk.mock.basePrice": "基本價",
    "mk.mock.longCoatFee": "長毛附加",
    "mk.mock.mattingFee": "打結處理",
    "mk.mock.spaA": "加 SPA - A",
    "mk.mock.balanceDue": "結餘應收",
    "mk.mock.collect": "💵 收款",
    "mk.mock.payrollHeader": "💰 6 月薪資 — Jason",
    "mk.mock.payrollSub": "6 月 1 - 30 日",
    "mk.mock.basePay": "基本工資",
    "mk.mock.svcComm": "服務抽成 (15%)",
    "mk.mock.pkgComm": "套票抽成 (8%)",
    "mk.mock.ot": "加班 12h × $80",
    "mk.mock.deduct": "扣減 (請假 1 日)",
    "mk.mock.netPay": "應發工資",
    "mk.mock.walletHeader": "🎁 客戶卡 — Momo",
    "mk.mock.walletSub": "Poodle · 4kg",
    "mk.mock.pkgVisitsCards": "6 次 / 2 張",
    "mk.mock.pkgList": "套票紀錄",
    "mk.mock.pkg10": "10 次套票",
    "mk.mock.left4": "剩 4 次",
    "mk.mock.expires1": "到期 2026-12-31",
    "mk.mock.pkgSpa6": "SPA 6 次卡",
    "mk.mock.left2": "剩 2 次",
    "mk.mock.expires2": "到期 2027-03-15",
    "mk.mock.walletEarn": "💎 完成本次預約可獲 +35 pt",
    "mk.mock.secHeader": "🛡️ 系統 & 備份",
    "mk.mock.secSub": "雲端安全 · GDPR",
    "mk.mock.backupOn": "✓ 雲端自動備份已啟用",
    "mk.mock.backupSub": "每日備份 · 7 日時間點還原",
    "mk.mock.staffPerms": "員工權限",
    "mk.mock.roleMgr": "店長 · 全部權限",
    "mk.mock.roleGroom": "美容師 · 只看自己預約",
    "mk.mock.roleFront": "前台 · 排程 + 收款",
    "mk.mock.exportData": "📥 下載資料",
    "mk.mock.deleteAcc": "🗑️ 刪除帳戶",
    // Public booking — visitor wizard
    "pub.bookNow": "立即預約",
    "pub.about": "關於我哋", "pub.contact": "聯絡", "pub.team": "美容師團隊",
    "pub.services": "服務", "pub.spaSection": "SPA", "pub.addonsSection": "加購",
    "pub.hours": "營業時間", "pub.closedToday": "今日休息",
    "pub.anyStaff": "任何美容師", "pub.review": "確認預約",
    "pub.signupOrGuest": "登入 / 註冊 / 訪客",
    "pub.guestName": "您的姓名", "pub.guestPhone": "電話 (+852)",
    "pub.haveAccount": "已有帳號", "pub.newAccount": "新客戶",
    "pub.googleContinue": "用 Google 繼續", "pub.emailLogin": "用電郵登入",
    "pub.visitor": "以訪客預約", "pub.confirmed": "預約已確認 🎉",
    "pub.viewBooking": "查看預約",
    // CustomerApp
    "cust.tabHome": "首頁", "cust.tabBook": "預約",
    "cust.tabPets": "毛孩", "cust.tabHist": "紀錄", "cust.tabMe": "我的",
    "cust.helloMorning": "早晨", "cust.helloAfternoon": "午安", "cust.helloEvening": "晚安",
    "cust.nextVisit": "下次預約", "cust.viewAll": "全部",
    "cust.bookAgain": "再次預約",
    "cust.pkgTitle": "我嘅套票", "cust.pkgRemain": "剩餘", "cust.pkgVisits": "次",
    "cust.pkgExpiry": "到期", "cust.buyPkg": "購買套票",
    "cust.changeShop": "切換店舖", "cust.signOut": "登出",
    "cust.editName": "編輯姓名",
    // ── extended set ──
    "common.other": "其他", "common.free": "免費", "common.unnamed": "(未命名)",
    "common.unassigned": "未指派", "common.saved": "已儲存 ✓",
    "common.savedShort": "已儲存", "common.optional": "（選填）",
    "common.pickDate": "選擇日期", "common.pickTime": "選擇時間",
    "common.zoomIn": "放大查看", "common.or": "或",
    "edit.editBtn": "編輯", "edit.bookAgain": "再次預約",
    "edit.report": "報告", "edit.fillReport": "填報告",
    "edit.noLabel": "🏷️ 無標籤", "edit.customLabel": "✏️ 自訂…",
    "edit.customLabelPrompt": "自訂標籤（留空清除）",
    "edit.editPet": "編輯毛孩", "edit.editCust": "編輯客戶",
    "edit.addNote": "+ 註意事項",
    "edit.addNotePrompt": "加入新註意事項（例：兇、會咬、皮膚敏感）",
    "edit.addNoteTitle": "加入毛孩註意事項",
    "edit.removeNote": "移除註意事項",
    "edit.noteAdded": "已加入註意事項",
    "edit.avatarUploaded": "毛孩頭像已上載 📷",
    "edit.uploadFail": "上載失敗：",
    "edit.pastLocked": "已過往預約 · 狀態已鎖定",
    "edit.pastLockedToast": "已過往預約，唔可以再更改狀態",
    "edit.markAs": "標記為「", "edit.andNotify": "」並通知客戶",
    "edit.statusBackPrompt1": "確定要將狀態退回「",
    "edit.statusBackPrompt2": "」？\n（會清除之後的時間戳，客戶不會收到通知）",
    "edit.statusBackPaidWarn": "⚠️ 此預約已收款 ${amt}。\n回退唔會自動退款 — 收款紀錄會保留，EOD 報表仍會計入。\n如需退款，請先按「取消收款」沖銷紀錄。\n\n確定繼續回退？",
    "edit.fillReportToast": "請填寫健康報告",
    "edit.statusUpdated1": "已更新為「", "edit.statusUpdated2": "」並通知客戶",
    "edit.checkin": "到店", "edit.grooming": "美容中",
    "edit.done": "完成", "edit.doneWithReport": "完成並寫報告",
    "edit.cancelled": "已取消", "edit.noshow": "未到店",
    "edit.discardPrompt": "確定放棄所有修改？",
    "edit.packageFail": "套票扣減失敗",
    "edit.retryRedeem": "🔄 重試扣套票",
    "edit.clearAlert": "✕ 清除提示",
    "edit.notBoundToPkg": "此預約未綁定套票",
    "edit.retryRedeemOk": "已重新扣套票 ✓",
    "edit.retryFail": "重試失敗：",
    "edit.noOtherHist": "暫無其他預約紀錄",
    "edit.healthReport": "健康報告",
    "edit.col.date": "日期", "edit.col.slot": "時段",
    "edit.col.svc": "服務", "edit.col.staff": "員工",
    "edit.col.groomingReq": "美容要求", "edit.col.intNote": "內部備註",
    "edit.col.groomerNote": "美容師叮嚀", "edit.col.cancelReason": "取消原因",
    "edit.beforeShot": "📷 美容前", "edit.afterShot": "✨ 美容後",
    "edit.spaShot": "💧 水療",
    "edit.discountOverCap1": "折扣 ", "edit.discountOverCap2": "% 超過你的權限上限 ",
    "edit.discountOverCap3": "%，請聯絡店主授權",
    "edit.staffConflict1": "該員工於 ", "edit.staffConflict2": " 已有預約，無法改期",
    "edit.manualAdj": "手動調整",
    "edit.svcChanged1": "主服務 $", "edit.discAdj": "折扣已調整",
    "edit.reschedTo": "改期 → ", "edit.staffChanged": "換員工",
    "edit.petNoteUpdated": "寵物備註已更新",
    "edit.rescheduledTitle": "預約已改期 📅",
    "edit.rescheduledBody": "您的預約已改至 ",
    "edit.preFilled": "已預填 ",
    "wi.title2": "即場到店登記",
    "wi.startNow": "登記 → 即刻開始美容",
    "wi.walletDeduct": "儲值扣款",
    "wi.subtotal": "總計", "wi.balance": "尾款",
    "wi.duration": "⏱️ 預計時長（每隻狗可不同）",
    "wi.customDur": "已自訂", "wi.svcName": "服務名稱",
    "wi.spaName": "SPA 名稱", "wi.basePrice": "底價 / 未設定時顯示價格 ($)",
    "wi.priceMode": "計價方式", "wi.priceBySize": "按體型定價",
    "wi.priceMatrix": "體型 × 毛髮長度定價",
    "wi.weightFrom": "起計體重 (kg)",
    "wi.newSize": "新增體型",
    "wi.delete": "刪除", "wi.addonsRow": "加購 ×",
    "wi.allergiesNone": "過敏 (無)",
    "wi.allergiesEg": "例：犬隻較活躍，請加倍小心…",
    "wi.spaEg": "例：舒緩肌膚 SPA：溫泉鹽浴 + 深層滋潤護理，約 30 分鐘。",
    "wi.sizeEg": "例：小型",
    "wi.coatShort": "短毛", "wi.coatLong": "長毛", "wi.coatMed": "中毛",
    "wi.sizeSmall": "小型", "wi.sizeMed": "中型",
    "wi.sizeLarge": "大型", "wi.sizeXL": "超大", "wi.sizeGiant": "巨型",
    "wi.dog": "🐶 狗", "wi.cat": "🐱 貓", "wi.small": "🐇 小動物",
    "wi.name": "名字 *", "wi.breed": "品種 *",
    "wi.phone": "電話 *", "wi.fullName": "姓名 *",
    "wi.weight": "體重 kg *",
    "wi.newBreed": "新品種",
    "wi.tagVip": "VIP老客", "wi.tagFriend": "員工親友",
    "wi.tagCompensation": "補償", "wi.tagTrial": "試做",
    "wi.tagEvent": "活動", "wi.tagReferral": "朋友介紹",
    "wi.custom": "自訂", "wi.row": "行", "wi.no": "第",
    "wi.minGtMax": "」最小值大於最大值，請對調",
    "wi.negWeight": "」體重不可為負數",
    "wi.kgNoMatch": "kg 冇對應價，會用底價 $",
    "wi.slotTaken": "已佔用",
    "wi.overlap1": ") 同「", "wi.overlap2": ") 重疊",
    "wi.petsCount": " 隻毛孩",
    "wi.surchargeAdd": "+ 附加費",
    "wi.surchargeName": "名稱（例：打結處理）",
    "wi.preset": "預設",
    "pub.welcome": "歡迎光臨 🐾",
    "pub.signupPrompt": "請登入或註冊以開始預約",
    "pub.loginContinue": "登入繼續預約",
    "pub.loginOrSignup": "登入或註冊",
    "pub.loginFail": "登入失敗",
    "pub.googleFail": "Google 登入失敗",
    "pub.appleFail": "Apple 登入失敗",
    "pub.appleSignin": "使用 Apple 登入",
    "pub.continueWith": "繼續以 ",
    "pub.emailGoogle": "電郵 / Google · 30 秒",
    "pub.newAccountStart": "建立新帳戶以開始預約",
    "pub.shopSchedule": "店家檔期一目了然",
    "pub.multiPet": "支援多隻毛孩同行 🐾",
    "pub.bookSuccess": "完成！等候到店指引",
    "pub.maxReward": "已達最高獎勵 🎉",
    "pub.rewards": "獎賞",
    "pub.svcRemoved": "服務已下架，請重新選擇服務",
    "pub.savePetFail": "毛孩資料儲存失敗",
    "pub.bookFail": "預約失敗",
    "pub.bookNotSaved": "預約未儲存成功（資料庫無回傳），請重新嘗試或聯絡店家",
    "pub.promoFail": "優惠碼未能即時兌換，請到店時告知店家以套用折扣。",
    "pub.loginBeforeBook": "請先登入再預約",
    "pub.elderRequired": "高齡寵物需於確認頁簽署免責聲明",
    "pub.discount50": "$50 折扣",
    "pub.newCustOnly": "　·　只限新客戶",
    "pub.todayOpen": " · 今日",
    "pub.included": " 內含",
    "pub.daysAgo": " 天前",
    "pub.lastVisit": "上次 ",
    "pub.fillForPrice": "填寫資料後我們可以計算準確價格",
    "pub.confirmBtn": "確認預約",
    "pub.confirmDeposit": "確認 + 付訂金",
    "pub.payDeposit": "付訂金",
    "pub.tax": "稅",
    "pub.submitting": "提交中…",
    "pub.pickSvc": "請選擇主要服務",
    "pub.pickPet": "請選擇或填寫毛孩",
    "pub.pickDate": "請選擇日期",
    "pub.pickTime": "請選擇時間",
    "pub.enterName": "請輸入名字",
    "pub.enterBreed": "請輸入品種",
    "pub.enterWeight": "請輸入重量",
    "pub.stepPet": "選毛孩 + 服務",
    "pub.stepTime": "揀美容師 + 時段",
    "pub.choosePet": "選擇您的毛孩，或新增一隻",
    "pub.namePh": "如：Fluffy",
    "pub.weightPh": "如：5.5",
    "pub.phonePh": "電話（例 9123 4567）",
    "pub.privacyAccept": "私隱政策",
    "pub.none": "無",
    "pub.holiday": "休息",
    "pub.dog": "狗", "pub.cat": "貓",
    "pub.rabbit": "兔", "pub.hamster": "倉鼠", "pub.otherPet": "其他",
    "pub.coatShort": "短毛", "pub.coatLong": "長毛", "pub.coatMed": "中毛",
    "pub.langTrad": "繁",
    "cust.helloMorning2": "早晨", "cust.bookFail": "預約建立失敗，請到店補登記",
    "cust.svcDownloaded": "個人資料已下載 ✓",
    "cust.downloadData": "下載個人資料", "cust.deleteAccount": "刪除帳戶",
    "cust.logout": "登出帳號",
    "cust.downloadFail": "下載失敗",
    "cust.savePhoneFail": "儲存電話失敗：",
    "cust.savedShort": "已儲存",
    "cust.saveFail": "儲存失敗",
    "cust.deleteFail": "刪除失敗，請聯絡客服",
    "cust.wallet": "錢包餘額",
    "cust.walletLow": "錢包餘額不足",
    "cust.signSig": "免責聲明已簽署",
    "cust.elderSign": "高齡寵物需簽署免責聲明",
    "cust.shareFail": "分享失敗：",
    "cust.viewAgain": "再睇一次",
    "cust.topupWallet": "你好，我想為錢包儲值：",
    "cust.buyPkgMsg": "你好，我想購買套票：",
    "cust.recentBook": "近期預約",
    "cust.transactions": "交易明細",
    "cust.myRewards": "🎟️ 我的獎勵券",
    "cust.privacyRights": "🔐 個人資料權利",
    "cust.recurringBook": "定期預約設定",
    "cust.depositHistory": "訂金與消費歷史",
    "cust.spaSection": "SPA 服務 💆",
    "cust.luxAddons": "來點奢華加購吧 ✨",
    "cust.beforeAfter": " 美容前後對比 ✨",
    "cust.styleHint": "✂️ 偏好造型: ",
    "cust.vetNote": "📋 獸醫備注",
    "cust.allergyInfo": "過敏資訊",
    "cust.lastVaccine": "最近疫苗",
    "cust.depositTopup": "充值錢包後記錄會顯示在這裡",
    "cust.regPetFirst": "先登記你的毛孩 🐾",
    "cust.notifyShop": "✅ 已通知店家，等待回覆付款方式",
    "cust.waitingPay": "WhatsApp 已開啟，等待店家確認收款",
    "cust.contactShop": "WhatsApp 聯絡店家",
    "cust.linkedOk": "✅ 已成功連結 ",
    "cust.shareReview": "分享你的美好體驗...",
    "cust.encMsg": "留言鼓勵",
    "cust.deposit": "訂金",
    "cust.svc": "主服務",
    "cust.feeLabel": " (手續費 $",
    "cust.expires": " 到期",
    "cust.daysValid": " 日有效",
    "cust.weeks": " 星期",
    "cust.times": " 次",
    "cust.deletedPet": "(已刪除毛孩)",
    "cust.encNicknamePh": "例: 奶茶",
    "cust.usePointsRedeem": "使用積分折抵 (",
    "cust.pointsLabel": " 積分）",
    "cust.exchangePts": " 積分兌換",
    "cust.rewardPts": " 積分 🎉",
    "cust.copyPaste": "上一步/下一步",
    "cust.lastGroom": "上次美容 ",
    "cust.styleEg": "例：盡量剪短，腳部留少少毛、面部圓型修剪",
    "cust.kgPriceHint": "kg) 智能計算專屬價格",
    "cust.priceFrom": "$X 起",
    "cust.fpsLabel": "FPS 轉數快 ⚡",
    "cust.spaFree": "SPA 免費",
    "cust.spaFreePkg": "免費 (套票包含)",
    "cust.spaFreeUpsell": "免費 SPA/加購",
    "cust.spaFreeHigh": "免費 SPA 高級護理",
    "cust.rewardCoupon": "兌換券",
    "cust.transferred": " 已轉帳 $",
    "cust.contactPhone": "聯絡電話 *",
    "cust.companion": " (同行)",
    "cust.refillSoon": "快用完？續購 ",
    "cust.maxWeight1": "本店最大接受體重為 ",
    "cust.maxWeight2": "kg，",
    "cust.elderSignReq": "高齡寵物需簽署免責聲明",
    "cust.appointmentReschd": " 的預約已改期至 ",
    "cust.updateOk": " 的資料已更新！",
    "cust.addedOk": " 已新增 ✓",
    "cust.addedPickSvc": " 已新增，請選擇服務",
    "cust.svcAddedOk": " 已成功新增！",
    "cust.oldPet": " 隻毛孩嘅舊資料",
    "cust.nicknameEg": "例：奶茶",
    "cust.confirmAdd": "✨ 確認新增",
    "cust.addCount": " 已新增 ✓",
    "cust.medCoat": "中毛", "cust.midDog": "中型犬",
    "cust.nicePoint": "不錯 🙂",
    "cust.encPh": "例: 奶茶",
    "cust.encEg2": "例：奶茶",
    "apt.viewMore": "查看更多", "apt.surchargePh": "名稱（例：打結處理）",
    "apt.labelCustom": "自訂",
    "apt.labelPending": "待確認", "apt.labelConfirmed": "已確認",
    "apt.labelPaid": "已付款", "apt.labelLate": "遲到",
    "apt.labelWait": "等通知", "apt.labelVip": "VIP",
    "apt.labelRush": "加急", "apt.labelOther": "其他",
    "apt.statusPending": "待開始", "apt.statusCheckedin": "已到店",
    "apt.statusGrooming": "美容中", "apt.statusDone": "完成",
    "apt.statusCancelled": "已取消", "apt.statusNoshow": "未到店",
    "apt.yourPet": "您嘅毛孩",
    "apt.surcharge": "附加費",
    "apt.payRecord": "暫無收款紀錄",
    "apt.otherMethod": "其他",
    "apt.via": "經手：",
    "apt.cash": "現金 💵", "apt.card": "信用卡 💳",
    "apt.fps": "FPS / PayMe 📱", "apt.walletM": "儲值錢包 💰",
    "apt.pkgRemain": "張",
    "apt.pkgVisits": "次/",
    "apt.noPkg": "無套票",
    "apt.editStart": "編輯開始時間",
    "apt.walkin": "即場", "apt.unclaimed": "未認領",
    // Customer-side iteration 1 (en parity batch)
    "cust.book.title": "預約服務",
    "cust.book.summary": "確認預約細節",
    "cust.book.depositDue": "應付訂金",
    "cust.book.totalDue": "應付總額",
    "cust.book.walletDeduct": "錢包扣減",
    "cust.book.useWallet": "使用錢包餘額",
    "cust.book.walletBalance": "錢包餘額",
    "cust.book.usePoints": "使用積分折抵",
    "cust.book.pointsDiscount": "積分折抵",
    "cust.book.voucherDiscount": "兌換券折抵",
    "cust.book.actualDeposit": "實付訂金",
    "cust.book.depositMethod": "訂金付款方式",
    "cust.book.markPaid": "✅ 已轉帳，提交預約",
    "cust.book.payLater": "稍後再付，到店時付款",
    "cust.book.wallet.notEnough": "錢包餘額不足",
    "cust.book.qrInstructions": "用以下任一方式付款後，撳「✅ 已轉帳並提交預約」。",
    "cust.book.useVoucher": "🎟️ 使用兌換券（可選）",
    "cust.book.usePackage": "📦 使用套票（可選）",
    "cust.book.notUsing": "唔使用",
    "cust.book.success": "預約成功！",
    "cust.book.successSub": "期待見到{petName} 🐾",
    "cust.book.rescheduleSuccess": "預約已成功改期！🎉",
    "cust.book.cancelSuccess": "預約已取消",
    "cust.book.bookNow": "立即預約",
    "cust.book.confirmAndPay": "確認並付款",
    "cust.book.markComplete": "已轉帳通知店家",
    "cust.book.alreadyNotified": "已通知店家確認",
    "cust.book.waNotConfigured": "店家未設定 WhatsApp，請直接聯絡店家",
    "cust.book.policyAccepted": "我已閱讀並同意條款",
    "cust.book.sendReceipt": "WhatsApp 發送收據截圖",
    "cust.book.petAge.senior": "高齡寵物需簽署免責聲明",
    "cust.book.petAge.tooYoung": "未夠 4 個月大不接受預約",
    "cust.book.overWeight": "超過接受體重",
    "cust.book.breedBlocked": "此品種暫不接受預約",
    "cust.history.title": "美容紀錄",
    "cust.history.upcoming": "即將到來",
    "cust.history.past": "歷史紀錄",
    "cust.history.empty": "暫無預約",
    "cust.history.emptySub": "完成第一個預約後紀錄會在這裡顯示",
    "cust.history.rate": "評分",
    "cust.history.viewReport": "查看報告",
    "cust.history.reschedule": "改期",
    "cust.history.cancel": "取消預約",
    "cust.rewards.title": "我的禮遇",
    "cust.rewards.stamps": "印章",
    "cust.rewards.vouchers": "獎勵券",
    "cust.rewards.points": "積分",
    "cust.rewards.empty": "暫無禮遇",
    "cust.rewards.emptySub": "完成預約後會自動儲分及解鎖獎勵",
    "cust.rewards.unlocked": "已解鎖獎賞",
    "cust.rewards.useNow": "立即使用 →",
    "cust.rewards.shopGifts": "🎟️ 店鋪贈送嘅獎勵券",
    "cust.rewards.useAtBooking": "下次預約時揀「使用獎勵券」即可",
    "cust.wallet.balance": "虛擬錢包餘額",
    "cust.wallet.topup": "申請儲值 💰",
    "cust.wallet.history": "交易紀錄",
    "cust.wallet.empty": "暫無交易紀錄",
    "cust.profile.contact": "聯絡資料",
    "cust.profile.security": "帳戶安全",
    "cust.profile.passwordChange": "更改密碼",
    "cust.profile.dataRights": "🔐 個人資料權利",
    "cust.profile.exportData": "📥 下載我嘅資料",
    "cust.profile.deleteAccount": "🗑️ 刪除帳戶",
    "cust.profile.deleteWarn": "永久刪除帳戶？資料無法恢復。",
    "cust.profile.deletedToast": "帳戶已永久刪除",
    "cust.profile.dataDownloaded": "資料已下載 ✓",
    "cust.toast.saved": "已儲存 ✓",
    "cust.toast.savedShort": "已儲存",
    "cust.toast.saveFailed": "儲存失敗",
    "cust.toast.copied": "已複製 ✓",
    "cust.toast.deleted": "已刪除",
    "cust.toast.rateThanks": "多謝你嘅評分 ⭐",
    "cust.toast.welcome": "歡迎",
    "cust.confirm.cancelBooking": "確認取消預約？\n• 客戶會收到通知\n• 訂金按店鋪退款政策處理",
    "cust.confirm.deleteAccount": "確認永久刪除帳戶？所有資料無法恢復。",
    "cust.label.contact": "📞 聯絡資料",
    "cust.label.address": "地址",
    "cust.label.email": "電郵",
    "cust.label.phone": "電話",
    "cust.label.editProfile": "編輯資料",
    "cust.label.recentBookings": "近期預約",
    "cust.label.depositHistory": "訂金與消費歷史",
    "cust.label.recurringSettings": "定期預約設定",
    "cust.label.noRecurring": "未設定定期預約",
    "cust.label.dataExport": "下載我嘅資料",
    "cust.label.dataDelete": "永久刪除我嘅帳戶",
    "cust.empty.noPets": "暫無毛孩",
    "cust.empty.noPetsSub": "點擊「新增」加入第一隻毛孩",
    // Iteration 2 — history list / pet editor / profile detail
    "cust.hist.recentBookings": "近期預約",
    "cust.hist.recurring": "定期預約",
    "cust.hist.noMatch": "沒有符合的預約",
    "cust.hist.noMatchSub": "點擊下方預約按鈕來新增預約吧",
    "cust.hist.deletedPet": "(已刪除毛孩)",
    "cust.hist.addons": "✨ 加購：",
    "cust.hist.cancelBtn": "取消預約",
    "cust.hist.rescheduleBtn": "改期",
    "cust.hist.rateBtn": "評價美容師",
    "cust.hist.viewReport": "查看報告",
    "cust.hist.cancelReason": "原因：",
    "cust.pets.addFirst": "新增您的第一隻毛孩 🐾",
    "cust.pets.addFirstSub": "填寫資料後就可以開始預約",
    "cust.pets.type": "類型",
    "cust.pets.typeDog": "狗",
    "cust.pets.typeCat": "貓",
    "cust.pets.typeRabbit": "兔",
    "cust.pets.typeHamster": "倉鼠",
    "cust.pets.weight": "重量（公斤）",
    "cust.pets.weightPh": "例：5.5",
    "cust.pets.breed": "品種",
    "cust.pets.coat": "毛髮長度",
    "cust.pets.short": "短毛",
    "cust.pets.long": "長毛",
    "cust.pets.allergyInfo": "過敏資訊",
    "cust.pets.allergyPh": "無 / 薰衣草等",
    "cust.pets.vaccineDate": "最近疫苗日期",
    "cust.pets.vetNote": "獸醫備註",
    "cust.pets.vetNotePh": "如需特別注意事項",
    "cust.pets.editBtn": "編輯",
    "cust.pets.deleteBtn": "刪除",
    "cust.pets.confirmDelete": "確定刪除呢隻毛孩？",
    "cust.pets.addBtn": "新增毛孩",
    "cust.pets.saveBtn": "💾 儲存修改",
    "cust.pets.newBtn": "✨ 確認新增",
    "cust.pets.profilePhoto": "毛孩頭像 📸",
    "cust.pets.allergy": "過敏",
    "cust.pets.vaccine": "疫苗",
    "cust.pets.notRecorded": "未記錄",
    "cust.pets.noAllergy": "無",
    "cust.profile.accountId": "帳號 ID",
    "cust.profile.shopBranch": "所屬門市",
    "cust.profile.tags": "客戶標籤",
    "cust.profile.notSet": "—",
    "cust.profile.viewPolicy": "查看私隱政策",
    "cust.profile.changeLanguage": "🌐 語言",
    "cust.recurring.title": "設定定期預約",
    "cust.recurring.selectPet": "毛孩",
    "cust.recurring.selectService": "服務",
    "cust.recurring.selectStaff": "美容師",
    "cust.recurring.selectDay": "星期",
    "cust.recurring.selectTime": "時間",
    "cust.recurring.interval": "週期",
    "cust.recurring.weekly": "每週",
    "cust.recurring.monthly": "每月",
    "cust.recurring.saveBtn": "儲存定期預約",
    "cust.recurring.savedToast": "已新增定期預約 ✓",
    "cust.cancel.reason": "取消原因",
    "cust.cancel.reasonPh": "請告訴我們原因（可選）",
    "cust.cancel.others": "其他",
    "cust.cancel.submit": "確認取消",
    "cust.cancel.refunded": "已退還",
    "cust.rate.title": "為美容師評分",
    "cust.rate.sub": "你嘅評價幫到其他客戶 ❤️",
    "cust.rate.commentPh": "留言鼓勵（可選）",
    "cust.rate.submit": "提交評價",
    "cust.report.title": "美容報告",
    "cust.report.close": "關閉",
    // ── Iteration 3 — Owner Dashboard / Calendar / Walk-in / EditApt ──
    "own.dash.appointmentsToday": "今日預約",
    "own.dash.revenueToday": "今日營業額",
    "own.dash.lowStock": "庫存警示",
    "own.dash.staleBanner": "📋 待結帳預約",
    "own.dash.staleBannerStale": "張過往預約仍未標記完成。",
    "own.dash.staleBannerUnpaid": "張已完成預約未收尾款。",
    "own.dash.staleBannerTap": "撳入處理。",
    "own.dash.walkinBtn": "即場到店",
    "own.schedule.day": "日",
    "own.schedule.week": "週",
    "own.schedule.month": "月",
    "own.schedule.agenda": "列表",
    "own.schedule.swipeHint": "← 滑動切換日期 · 兩指縮放",
    "own.schedule.noStaff": "已隱藏所有員工",
    "own.schedule.unassigned": "未指派",
    "own.schedule.prevWeek": "上一週",
    "own.schedule.nextWeek": "下一週",
    "own.schedule.allEmpty": "顯示此日期所有時段已滿",
    "own.schedule.conflict": "⚠ 時段衝突",
    "own.schedule.confirmReschedule": "確認改期？",
    "own.schedule.rescheduleSuccess": "已改期 ✓",
    "own.schedule.rescheduleFailed": "改期失敗",
    "own.schedule.bookSuccess": "預約已建立 ✓",
    "own.cal.todayMarker": "今日",
    "own.cal.hr": "時",
    "own.cal.legend.pending": "待處理",
    "own.cal.legend.checkedin": "已到店",
    "own.cal.legend.grooming": "美容中",
    "own.cal.legend.done": "完成",
    "own.cal.legend.event": "事件",
    "own.cal.collected": "已收",
    "own.cal.unpaid": "未付",
    "own.cal.partial": "部分",
    "own.cal.paid": "已付",
    "own.cal.package": "套票",
    "own.walkin.title": "即場到店",
    "own.walkin.selectCustomer": "選擇客戶",
    "own.walkin.newCustomer": "新客戶",
    "own.walkin.selectPet": "選擇毛孩",
    "own.walkin.newPet": "新增毛孩",
    "own.walkin.selectService": "選擇服務",
    "own.walkin.selectStaff": "選擇美容師",
    "own.walkin.startTime": "開始時間",
    "own.walkin.addons": "加購（可選）",
    "own.walkin.spa": "SPA（可選）",
    "own.walkin.customDuration": "自訂時長",
    "own.walkin.discount": "折扣",
    "own.walkin.discountReason": "折扣原因",
    "own.walkin.staffRemark": "員工備註",
    "own.walkin.useWallet": "扣減錢包",
    "own.walkin.usePackage": "使用套票",
    "own.walkin.startGrooming": "開始美容",
    "own.walkin.savedAsPending": "已登記 — 待處理",
    "own.walkin.savedAsCheckedin": "已登記 — 到店中",
    "own.walkin.savedAsGrooming": "已登記 — 美容中",
    "own.walkin.amountTotal": "總計",
    "own.walkin.allergyWarn": "⚠️ 過敏：",
    "own.walkin.confirmAdd": "確認新增即場預約？",
    "own.walkin.confirmAddBtn": "確認新增",
    "own.walkin.cancelBtn": "取消",
    "own.walkin.searchCustPh": "輸入姓名／電話搜尋",
    "own.walkin.noPet": "此客戶未有毛孩，可即場新增",
    "own.walkin.timeConflict": "該美容師此時段已有預約",
    "own.editApt.title": "預約詳情",
    "own.editApt.editMode": "編輯模式",
    "own.editApt.saveEdit": "儲存修改",
    "own.editApt.cancelEdit": "放棄修改",
    "own.editApt.unsavedConfirm": "有未儲存嘅變更，確認離開？",
    "own.editApt.label": "標籤",
    "own.editApt.noLabel": "（無標籤）",
    "own.editApt.customLabelPrompt": "輸入自訂標籤",
    "own.editApt.markAs": "標記為 ",
    "own.editApt.andNotify": " 並通知客戶",
    "own.editApt.pastLocked": "已過往預約 · 狀態已鎖定",
    "own.editApt.pastLockedToast": "已過往預約，無法修改狀態",
    "own.editApt.statusUpdated": "狀態已更新",
    "own.editApt.report": "美容報告",
    "own.editApt.skin": "皮膚",
    "own.editApt.ear": "耳朵",
    "own.editApt.teeth": "牙齒",
    "own.editApt.mood": "情緒",
    "own.editApt.nails": "指甲",
    "own.editApt.analGlands": "肛門腺",
    "own.editApt.parasites": "寄生蟲",
    "own.editApt.eyeDischarge": "眼分泌",
    "own.editApt.matting": "打結程度",
    "own.editApt.notes": "美容師叮嚀",
    "own.editApt.photoUpload": "上載照片",
    "own.editApt.report.save": "儲存報告",
    "own.editApt.report.send": "儲存並通知客戶",
    "own.editApt.checkin": "登到",
    "own.editApt.startGroom": "開始美容",
    "own.editApt.markDone": "標記完成",
    "own.editApt.markNoShow": "未到店",
    "own.editApt.cancelBooking": "取消預約",
    // ── Iteration 4 — CRM / Wallet admin / Packages / Vouchers ──
    "own.crm.title": "顧客",
    "own.crm.search": "搜尋客戶...",
    "own.crm.empty": "暫無客戶",
    "own.crm.emptySub": "完成第一個預約後，客戶會自動加入。",
    "own.crm.tab.info": "基本資料",
    "own.crm.tab.records": "美容紀錄",
    "own.crm.tab.wallet": "錢包記錄",
    "own.crm.tab.ledger": "💰 交易明細",
    "own.crm.totalSpent": "總消費",
    "own.crm.groomCount": "美容次數",
    "own.crm.contact": "📞 聯絡資料",
    "own.crm.visitor": "訪客帳戶（無 Email 登入）",
    "own.crm.editProfile": "編輯資料",
    "own.crm.blockBtn": "🚫 封鎖客戶",
    "own.crm.unblockBtn": "🚫 解除封鎖",
    "own.crm.blocked": "🚫 已被封鎖",
    "own.crm.blockReason": "原因：",
    "own.crm.confirmBlock": "封鎖客戶？\n佢喺呢間店之後唔可以再預約。其他店唔受影響。\n\n原因（可選）：",
    "own.crm.confirmUnblock": "解除封鎖客戶？",
    "own.crm.blockedToast": "已封鎖客戶 🚫",
    "own.crm.unblockedToast": "已解除封鎖 ✓",
    "own.crm.disableBtn": "停用",
    "own.crm.deleteBtn": "永久刪除",
    "own.crm.confirmDisable": "確定要停用客戶「{name}」？\n預約紀錄保留，但客戶無法再登入或預約（可隨時恢復）。",
    "own.crm.disabledToast": "已停用客戶",
    "own.crm.addWallet": "手動加值錢包",
    "own.crm.sellPackage": "📦 送贈／販售套票",
    "own.crm.managePackage": "📋 管理客戶套票",
    "own.crm.noWalletTx": "暫無交易記錄",
    "own.crm.noWalletTxSub": "尚未有任何錢包交易",
    "own.crm.walletDeducted": "錢包扣減",
    "own.crm.pointsDeducted": "積分扣減",
    "own.crm.vouchersUsed": "已用券",
    "own.crm.ledger.empty": "暫無交易紀錄",
    "own.crm.ledger.emptySub": "客戶尚未消費錢包／積分／券",
    "own.topup.title": "手動加值錢包",
    "own.topup.amount": "金額",
    "own.topup.note": "備註",
    "own.topup.method": "付款方式",
    "own.topup.save": "確認加值",
    "own.topup.savedToast": "已加值 ${amount} ✓",
    "own.topup.deductBtn": "扣減錢包",
    "own.topup.deductConfirm": "扣減客戶錢包？",
    "own.topup.deductReason": "扣減原因",
    "own.pkg.activeFilter": "有效",
    "own.pkg.expiredFilter": "已過期",
    "own.pkg.spentFilter": "已用完",
    "own.pkg.refundedFilter": "已退款",
    "own.pkg.allFilter": "全部",
    "own.pkg.empty": "暫無套票",
    "own.pkg.remaining": "剩 ",
    "own.pkg.visits": "次",
    "own.pkg.expires": "到期",
    "own.pkg.expired": "已過期",
    "own.pkg.refunded": "已退款",
    "own.pkg.gift": "🎁 免費贈送",
    "own.pkg.usageLog": "📜 紀錄",
    "own.pkg.extend30": "📅 +30日",
    "own.pkg.extendCustom": "📅 自訂延期",
    "own.pkg.restore1": "↩️ 還原 1 次",
    "own.pkg.deduct1": "➖ 扣 1 次",
    "own.pkg.refundBtn": "💸 退款",
    "own.pkg.confirmRestore": "還原 1 次（用於誤扣回滾）？",
    "own.pkg.confirmDeduct": "再扣 1 次？（例：同次服務多做一項，需額外扣套票）",
    "own.pkg.restoredToast": "已還原 1 次 ✓",
    "own.pkg.deductedToast": "已扣 1 次 ✓",
    "own.pkg.extendedToast": "已延長 {days} 日 ✓",
    "own.pkg.extendPrompt": "延長多少日？",
    "own.pkg.refundPrompt": "退款金額？",
    "own.pkg.refundReason": "退款原因",
    "own.pkg.refundedToast": "已退款 ${amount} ✓",
    "own.sell.title": "送贈／販售套票",
    "own.sell.intro": "為 {name} 加入套票。客戶會在 App 看到剩餘次數及到期日。",
    "own.sell.selectPackage": "選擇套票",
    "own.sell.selectPet": "選擇毛孩",
    "own.sell.totalVisits": "總次數",
    "own.sell.expires": "有效期",
    "own.sell.paymentMethod": "付款方式",
    "own.sell.amountPaid": "實收金額",
    "own.sell.giftToggle": "免費贈送（無需收款）",
    "own.sell.submitBtn": "確認",
    "own.sell.successPkg": "已送出套票 ✓",
    "own.sell.successPaid": "已收款 ${amount} ✓",
    "own.voucher.title": "兌換券",
    "own.voucher.empty": "暫無兌換券",
    "own.voucher.unused": "未使用",
    "own.voucher.used": "已使用",
    "own.voucher.usedOn": "已用於 ",
    // ── Iteration 5 — Owner Settings ──
    "own.set.policy.wallet": "啟用電子錢包",
    "own.set.policy.walletSub": "關閉後，客戶 App 唔會顯示「錢包」分頁同增值按鈕。客戶現有餘額會保留紀錄。",
    "own.set.policy.loyalty": "啟用積分回贈",
    "own.set.policy.loyaltySub": "關閉後，客戶 App 隱藏「積分」顯示，預約完成不再發放積分。",
    "own.set.policy.earnRule": "消費 $X 賺 1 分",
    "own.set.policy.redeemRule": "1 分 = $Y 現金",
    "own.set.policy.example": "💡 例：消費 $100 → 賺 {n} 分 → 抵現金 ${cash}",
    "own.set.policy.review": "啟用客戶評分",
    "own.set.policy.reviewSub": "關閉後，客戶 App 唔會出現評分提示，店鋪後台亦會隱藏「評價」分頁。",
    "own.set.policy.healthReport": "啟用美容後健康報告",
    "own.set.policy.healthReportSub": "啟用後撳「完成」會跳出健康報告填寫表。",
    "own.set.policy.maxWeight": "⚖️ 最大接受體重 (kg)",
    "own.set.policy.maxWeightSub": "客戶毛孩體重超過此值無法預約。空白／0 表示冇限制。",
    "own.set.policy.brandRemove": "🏷️ 移除 PettoBook 品牌",
    "own.set.policy.brandRemoveSub": "啟用後，客戶預約頁底部、寵物前後對比圖、客戶 App 都唔再顯示「Powered by PettoBook」。",
    "own.set.policy.brandRemoveProBadge": "👑 需要專業版方案",
    "own.set.brand.title": "店鋪專屬主題色 🎨",
    "own.set.brand.logo": "店鋪標誌",
    "own.set.brand.banner": "店鋪橫額",
    "own.set.brand.tagline": "店鋪標語",
    "own.set.brand.about": "關於我們",
    "own.set.brand.website": "網址",
    "own.set.brand.instagram": "Instagram",
    "own.set.brand.facebook": "Facebook",
    "own.set.brand.gallery": "店鋪相冊",
    "own.set.hours.businessHours": "營業時間",
    "own.set.hours.closed": "公休",
    "own.set.hours.allDay": "全天",
    "own.set.hours.holidays": "公休日設定 🏖️",
    "own.set.hours.holidaysSub": "公眾假期會喺月曆顯示假期名作參考，並唔會自動休息。",
    "own.set.hours.addHoliday": "新增休假日",
    "own.set.hours.removeHoliday": "移除",
    "own.set.msg.autoCommTitle": "💬 客戶自動通訊",
    "own.set.msg.autoCommSub": "預約確認、改期、完成通知會自動由 PettoBook 代你發送。",
    "own.set.msg.channelEmail": "📧 Email",
    "own.set.msg.channelSms": "📱 SMS",
    "own.set.msg.emailDesc": "建議：收據、提醒、報告（永遠免費）",
    "own.set.msg.smsDesc": "長輩客戶都收得到，每則約 $0.05",
    "own.set.msg.smsPaywall": "🔒 需要基本版或專業版方案",
    "own.set.msg.upgradeRequired": "此功能需要付費版，請先升級",
    "own.set.msg.advanced": "🛠️ 進階設定（自訂 API 憑證）",
    "own.set.msg.advancedHint": "Email 同 SMS 通知由 PettoBook 平台代寄，唔需要設定。下面係店鋪自家 WhatsApp Business 號碼。",
    "own.set.msg.waBYOK": "💬 WhatsApp Business（自設）",
    "own.set.msg.waPhoneId": "WhatsApp Phone Number ID",
    "own.set.msg.waToken": "Access Token",
    "own.set.msg.waFromName": "顯示名稱（選填）",
    "own.set.msg.waTemplate": "預設訊息範本名（選填）",
    "own.set.msg.saveBtn": "儲存設定",
    "own.set.msg.sendTestBtn": "📲 寄測試到自己",
    "own.set.msg.savedToast": "通訊設定已儲存",
    "own.set.msg.saveFailed": "儲存失敗",
    "own.set.msg.testSent": "✅ 測試訊息已發送",
    "own.set.msg.testFailed": "發送失敗",
    "own.set.msg.perTriggerCard": "⚡ 各通知獨立設定",
    "own.set.msg.perTriggerSub": "逐個事件揀 channel 同開關（預約確認、改期、提醒……）",
    "own.set.msg.defaultChannel": "預設 channel（所有通知）",
    "own.set.msg.perEvent": "個別事件",
    "own.set.msg.perEventHint": "左邊 toggle：開關呢個通知；右邊：揀 channel（留空＝跟預設）",
    "own.set.msg.trig.booking": "📅 預約確認",
    "own.set.msg.trig.reschedule": "🔁 改期通知",
    "own.set.msg.trig.cancel": "❌ 取消通知",
    "own.set.msg.trig.dayBefore": "⏰ 前一日提醒",
    "own.set.msg.trig.groomingDone": "🎉 美容完成",
    "own.set.msg.trig.useDefault": "（預設）",
    "own.set.msg.trig.both": "📧 + 📱 兩者都寄",
    "own.set.tpl.title": "📝 自訂訊息範本",
    "own.set.tpl.intro": "每個事件嘅自動訊息可以自訂內容。空白格表示使用預設範本。",
    "own.set.tpl.dynamicFields": "支援以下動態欄位：",
    "own.set.tpl.customized": "已自訂",
    "own.set.tpl.restoreDefault": "還原預設",
    "own.set.tpl.save": "儲存",
    "own.set.tpl.savedToast": "已儲存 ✓",
    "own.set.tpl.emailSubject": "📧 Email 主旨",
    "own.set.tpl.messageBody": "💬 訊息內文",
    // ── Iteration 6 — Cross-cutting toasts / confirms / errors ──
    "tx.saved": "已儲存 ✓",
    "tx.saveFailed": "儲存失敗",
    "tx.deleted": "已刪除",
    "tx.deleteFailed": "刪除失敗",
    "tx.copied": "已複製",
    "tx.uploadOk": "已上載",
    "tx.uploadFailed": "上載失敗",
    "tx.networkError": "網絡錯誤，請稍後再試",
    "tx.actionFailed": "操作失敗",
    "tx.confirm": "確定",
    "tx.cancel": "取消",
    "tx.yes": "係",
    "tx.no": "唔係",
    "tx.permissionDenied": "權限不足",
    "tx.required": "請填寫此欄位",
    "tx.invalidFormat": "格式不正確",
    "tx.emailInvalid": "Email 格式不正確",
    "tx.phoneInvalid": "電話格式不正確",
    "tx.passwordTooShort": "密碼太短",
    "tx.csvNoData": "CSV 沒有資料 — 請至少填一行",
    "tx.csvParseFailed": "CSV 解析失敗",
    "tx.confirmDelete": "確定刪除？",
    "tx.confirmCancel": "確定取消？",
    "tx.unsavedChanges": "有未儲存嘅變更，確認離開？",
    "tx.staffSaved": "員工資料已更新 ✓",
    "tx.shopDeleted": "商戶已刪除",
    "tx.enableFailed": "啟用失敗，請稍後再試",
    "tx.qrUploaded": "QR 已上載",
    "tx.emailSettingsSaved": "Email 設定已儲存",
    "tx.smsSettingsSaved": "SMS 設定已儲存",
    "tx.waSettingsSaved": "WhatsApp 設定已儲存",
    "tx.testEmailSent": "✅ 測試 Email 已發送",
    "tx.testSmsSent": "✅ 測試 SMS 已發送",
    "tx.testMsgSent": "✅ 測試訊息已發送",
    "tx.shareFailed": "分享失敗",
    "tx.linkedSuccess": "✅ 已成功連結",
    "tx.disconnectFailed": "取消連接失敗",
    "tx.delPayoutConfirm": "刪除呢筆出薪紀錄？",
    "tx.delDeductConfirm": "刪除呢項調整？",
    "tx.delEventTplConfirm": "刪除此事件範本？",
    "tx.delEventConfirm": "刪除此事件？",
    "tx.delPayMethodConfirm": "確定刪除此收款方式？",
    "tx.deleted": "已刪除",
    "tx.gcalDisconnectConfirm": "取消連接 Google Calendar？現有同步事件唔會刪除。",
    "tx.gcalDisconnectedToast": "已取消連接 Google Calendar",
    "tx.icalRevokeConfirm": "撤銷後現有訂閱連結會即時失效。確定？",
    "tx.icalGenToast": "已產生月曆訂閱連結 ✅",
    "tx.icalRevokedToast": "已撤銷月曆訂閱",
    "tx.icalGenFailed": "產生連結失敗",
    "tx.icalRevokeFailed": "撤銷失敗",
    "tx.googleAuthFailed": "Google 授權失敗",
    "tx.payoutToast": "已記錄出薪 ${amount} ✓",
    "tx.payoutFailed": "失敗",
    "tx.gcalTitle": "🔗 Google Calendar 即時同步",
    "tx.gcalSub": "授權後，PettoBook 嘅預約會即時寫入你嘅 Google Calendar。改期 / 取消 15 秒內自動更新（iCal 訂閱要等 Google 4-24 小時 poll）。",
    "tx.gcalConnected": "✓ 已連接",
    "tx.gcalDisconnect": "取消連接",
    "tx.gcalConnect": "連接 Google Calendar →",
    "tx.staffLeaveLabelSick": "🤒 病假",
    "tx.staffLeaveLabelPersonal": "📌 事假",
    "tx.staffFallback": "員工",
    "tx.custFallback": "客戶",
    "tx.unassigned": "未指派",
    "tx.staffOnLeaveConfirm": "注意：{name} 今日已標記為 {label}。\n\n仍然要建立預約？",
    "tx.delSvcAffectsApts": "刪除「{name}」會影響 {n} 個未來預約，仍要繼續嗎？",
    "tx.delSvcConfirm": "確定刪除「{name}」？",
    "tx.svcFallback": "此服務",
    "tx.rescheduleConfirm": "確認改期 {pet} ({cust})？\n\n原時段：{fromDate} {fromTime} · {fromStaff}\n新時段：{toDate} {toTime} · {toStaff}",
    "tx.enableStaffConfirm": "啟用 {name}？該員工會重新出現喺日歷、預約分配同薪金統計。",
    "tx.disableStaffConfirm": "停用 {name}？已停用嘅員工唔會接收新預約，但舊預約同歷史保留。",
    "tx.updateFailed": "更新失敗",
    "tx.pkgRestore1Confirm": "還原 1 次？",
    "tx.pkgDeduct1Confirm": "再扣 1 次？（例：同次服務多做一項）",
    "tx.pkgExtend30Toast": "已延 30 日 ✓",
    "tx.pkgExtend30Btn": "+30 日",
    "tx.pkgRestoredToast": "已還原 ✓",
    "tx.pkgDeductedToast": "已扣 1 次 ✓",
    "tx.pkgRestoreBtn": "↩️ 還原",
    "tx.pkgDeductBtn": "➖ 扣 1 次",
    "tx.pkgRestoreReason": "手動還原 1 次",
    "tx.pkgDeductReason": "手動扣 1 次",
    "tx.opFailed": "失敗",
    "fpc.title": "請設定您的密碼",
    "fpc.sub": "店家為您建立了帳戶。請設定一個只有您知道的密碼，以保護您的個人資料。",
    "fpc.newPw": "新密碼（至少 6 位）",
    "fpc.confirmPw": "確認新密碼",
    "fpc.pwTooShort": "密碼至少 6 位",
    "fpc.pwMismatch": "兩次輸入的密碼不一致",
    "fpc.updatedToast": "密碼已更新 ✅",
    "fpc.updateFailed": "更新密碼失敗",
    "fpc.saving": "儲存中...",
    "fpc.confirmBtn": "確認並登入",
    "fpc.later": "稍後再改",
    "undo.btn": "撤銷",
    "tx.popupBlocked": "瀏覽器封鎖了彈出視窗，請允許後再試",
    "settings.title": "設定",
    "settings.group.shop": "店鋪",
    "settings.group.ops": "營運",
    "settings.group.reports": "報表",
    "settings.group.stamp": "印花卡",
    "settings.group.customerAssets": "客戶資產管理",
    "settings.packagesAdmin": "📦 套票管理",
    "settings.walletsAdmin": "💳 儲值卡管理",
    "settings.pointsAdmin": "⭐ 積分管理",
    "settings.group.advanced": "進階",
    "settings.analytics": "分析",
    "settings.reviews": "評價",
    "settings.pointsSetup": "印花卡設定",
    "settings.activity": "活動記錄",
    "settings.support": "💬 客戶支援",
    "settings.systemBackup": "系統與備份",
    "settings.events": "📌 事件管理",
    "set.basic.langMovedHint": "💡 介面語言（中文／English）已搬到「設定 → 系統與備份 → 🌐」即時切換。貨幣設定即時生效 — 影響「$」符號同所有金額顯示。",
    "tx.googleAuthFailed": "Google 授權失敗",
    "tx.eventDeleted": "事件已刪除",
    "tx.eventUpdated": "事件已更新 📌",
    "tx.eventSaveFailed": "儲存事件失敗",
    "tx.noteSaved": "備注已儲存",
    "tx.signed": "免責聲明已簽署",
    "tx.announcementPublished": "公告已發布並通知所有客戶",
    "tx.refundFailed": "退款失敗",
    "tx.cancelFailed": "取消失敗",
    "tx.refundCancelFailed": "取消收款失敗",
    "tx.refunded": "已退還",
    "tx.topupFailed": "加值失敗",
    "tx.savePhoneFailed": "儲存電話失敗",
    "tx.uploadFailedToast": "上傳失敗",
    "tx.walletDeductFailed": "儲值扣款未能寫入 DB，請手動調整",
    "tx.packageDelisted": "套票已下架，請重新整理頁面",
    "tx.packageCashOnly": "套票期間不可使用現金 / 百分比折扣券",
    "tx.confirmRemoveStaff": "確定要移除呢位員工？\n• 佢嘅排班 / 預約紀錄會保留\n• 你需要重新指派未來嘅預約\n• 員工帳號會被禁用，唔可以再登入",
    "tx.confirmDeleteShop": "確定要永久刪除呢間商戶？所有數據包括客戶、預約、員工、套票紀錄都會永久消失，無法復原。",
    "tx.confirmCancelBooking": "確定取消預約？\n\n• 客戶會收到通知\n• 套票次數退還\n• 印花 / 積分撤銷",
    // ── Iteration 7 — EditApt sheet detail + Walk-in form internals ──
    "ea.title": "預約詳情",
    "ea.editMode": "編輯",
    "ea.editSave": "完成編輯",
    "ea.label": "標籤",
    "ea.noLabel": "（無標籤）",
    "ea.staffNote": "員工備註",
    "ea.editStartTime": "編輯開始時間",
    "ea.bookingId": "預約 ID",
    "ea.deposit": "已收訂金",
    "ea.depositPending": "⏳ 訂金待確認",
    "ea.depositConfirm": "✓ 確認收訂金",
    "ea.depositUncancel": "取消",
    "ea.depositCompose": "組成：",
    "ea.depositPaid": "✓ 已收訂金",
    "ea.cash": "現金",
    "ea.walletShort": "錢包",
    "ea.pointsShort": "積分",
    "ea.voucher": "券",
    "ea.totalCharge": "總收費",
    "ea.originalPrice": "原價",
    "ea.actualReceive": "實收",
    "ea.cashOwed": "應收現金",
    "ea.discount": "折扣",
    "ea.discountAmount": "折扣金額",
    "ea.discountReason": "折扣原因",
    "ea.addDiscount": "新增折扣",
    "ea.removeDiscount": "移除折扣",
    "ea.discountPercent": "百分比",
    "ea.discountFixed": "固定金額",
    "ea.addSurcharge": "新增附加費",
    "ea.surchargeName": "項目名稱",
    "ea.voucherUsed": "🎟️ 兌換券折抵",
    "ea.payButton": "收款 ",
    "ea.payDoneBtn": "已收齊款 · 已鎖定",
    "ea.refundReopen": "↩️ 退款／重開收款",
    "ea.refundConfirm": "⚠️ 此預約已收齊款，確定要退款／重開？",
    "ea.refundedToast": "已退款 ${amount} · 收款重開",
    "ea.payLockedToast": "已過往預約 · 狀態已鎖定",
    "ea.paidFullConfirm": "⚠️ 此預約已收齊款。\n\n撳「確定」會記低退款並重新打開「未收款」。\n如冇打算退款，請按「取消」。",
    "ea.cancelPayConfirm": "取消收款？\n會記低 ${net} refund，唔會雙計營收。",
    "ea.cancelPayMarkToast": "已取消收款標記",
    "ea.cancelDepositConfirm": "確定要取消訂金已收狀態？",
    "ea.noshowConfirm": "確認標記為「未到店」？\n\n{pet} · {date} {time}\n\n• 套票次數自動退還\n• 印花卡 / 積分自動撤銷\n• 訂金按店鋪政策處理",
    "ea.cancelAptPromptTitle": "取消預約原因（會通知客戶）：",
    "ea.cancelAptPromptDefault": "店家取消",
    "ea.cancelAptConfirm": "確認取消預約？\n\n{pet} · {date} {time}\n\n• 客戶會收到通知\n• 套票次數退還\n• 印花 / 積分撤銷\n• {feeLine}",
    "ea.cancelAptFeeLine": "扣 ${fee} 手續費",
    "ea.cancelAptFullRefund": "全額退款",
    "ea.discardEditsBack": "放棄修改返回詳情？",
    "ea.backToDetail": "← 返回詳情",
    "ea.unsavedBanner": "有未儲存的修改，請按下方「儲存」",
    "ea.markNoShowBtn": "⚠️ 標記為未到店",
    "ea.cancelAptBtn": "❌ 取消預約",
    "ea.statusBar": "狀態",
    "ea.statusPending": "待處理",
    "ea.statusCheckedin": "已到店",
    "ea.statusGrooming": "美容中",
    "ea.statusDone": "完成",
    "ea.statusCancelled": "已取消",
    "ea.statusNoShow": "未到店",
    "ea.confirmStatus": "標記為 ",
    "ea.statusNotify": "並通知客戶",
    "ea.report.title": "美容報告",
    "ea.report.skin": "皮膚",
    "ea.report.ear": "耳朵",
    "ea.report.teeth": "牙齒",
    "ea.report.mood": "情緒",
    "ea.report.nails": "指甲",
    "ea.report.analGlands": "肛門腺",
    "ea.report.parasites": "寄生蟲",
    "ea.report.eyeDischarge": "眼分泌",
    "ea.report.matting": "打結程度",
    "ea.report.notes": "美容師叮嚀",
    "ea.report.photos": "美容照片",
    "ea.report.normal": "正常",
    "ea.report.attention": "需注意",
    "ea.report.abnormal": "異常",
    "ea.report.none": "無",
    "ea.report.mild": "輕微",
    "ea.report.severe": "嚴重",
    "ea.report.uploadBtn": "上載照片",
    "ea.report.saveBtn": "儲存報告",
    "ea.report.sendBtn": "儲存並通知客戶",
    "ea.report.savedToast": "報告已儲存",
    "ea.unlock.locked": "已過往預約 · 狀態鎖定",
    "ea.unlock.unlocked": "暫時解鎖中",
    "ea.unlock.lockedSub": "防止意外修改舊單。需補回收款或改狀態請解鎖。",
    "ea.unlock.unlockedSub": "關閉視窗後會自動重新鎖定",
    "ea.unlock.btn": "🔓 解鎖",
    "ea.unlock.relock": "重新鎖定",
    "ea.unlock.unlockedToast": "已解鎖過往預約 · 可修改 🔓",
    "ea.unlock.relockedToast": "已重新鎖定 🔒",
    "ea.cancelBooking": "❌ 取消預約",
    "ea.cancelReason": "取消原因",
    "ea.cancelReasonPh": "為何取消？",
    "ea.cancelOther": "其他",
    "ea.cancelSubmit": "確認取消",
    "ea.lateFee": "手續費",
    "ea.refunded": "已退還",
    "ea.checkinTime": "登到時間",
    "ea.startTime": "開始時間",
    "ea.completedTime": "完成時間",
    "wi.title": "即場到店",
    "wi.searchCustPh": "輸入姓名／電話搜尋",
    "wi.newCustomer": "新客戶",
    "wi.newCustName": "客戶姓名",
    "wi.newCustPhone": "電話",
    "wi.newPet": "新增毛孩",
    "wi.newPetName": "毛孩名稱",
    "wi.newPetBreed": "品種",
    "wi.newPetWeight": "重量 (kg)",
    "wi.newPetType": "類型",
    "wi.newPetCoat": "毛髮長度",
    "wi.allergies": "過敏資訊",
    "wi.allergiesPh": "無 / 例：對洗毛精過敏",
    "wi.service": "服務",
    "wi.servicePicked": "已選服務",
    "wi.staff": "美容師",
    "wi.time": "時段",
    "wi.duration": "時長 (分鐘)",
    "wi.customDuration": "自訂時長",
    "wi.addons": "加購",
    "wi.spaServices": "SPA 服務",
    "wi.noAddons": "暫無加購",
    "wi.discount": "折扣",
    "wi.discountAmt": "折扣金額",
    "wi.discountType": "折扣方式",
    "wi.discountReason": "折扣原因",
    "wi.discountReasonPh": "例：VIP / 員工親屬 / 體驗價",
    "wi.staffRemark": "員工備註",
    "wi.staffRemarkPh": "對員工嘅特別提醒",
    "wi.useWallet": "扣減錢包",
    "wi.usePackage": "使用套票",
    "wi.total": "總計",
    "wi.startNowBtn": "立即開始美容",
    "wi.checkedInBtn": "登到唔開始",
    "wi.savedToast": "即場預約已登記",
    "wi.timeConflict": "該美容師此時段已有預約",
    "wi.serviceRequired": "請選擇服務",
    "wi.petRequired": "請選擇毛孩",
    "wi.customerRequired": "請選擇客戶",
    "wi.discountTooLarge": "折扣金額不能大於原價",
    "wi.noStaff": "未有可用員工",
    // ── Iteration 8 — Staff / Payroll / Commission ──
    "st.title": "員工",
    "st.addStaff": "新增員工",
    "st.searchPh": "搜尋員工…",
    "st.empty": "暫無員工",
    "st.emptySub": "撳「新增員工」加入第一位",
    "st.editTitle": "員工資料",
    "st.editNewTitle": "新增員工",
    "st.tabBasic": "基本",
    "st.tabService": "服務",
    "st.tabTime": "時間",
    "st.tabLeave": "休假",
    "st.tabPermissions": "權限",
    "st.name": "姓名",
    "st.phone": "電話",
    "st.email": "電郵",
    "st.password": "密碼",
    "st.role": "角色",
    "st.roleOwner": "店主",
    "st.roleAdmin": "管理員",
    "st.roleStaff": "員工",
    "st.jobType": "聘用類型",
    "st.fullTime": "全職",
    "st.partTime": "兼職",
    "st.settleMode": "結算方式",
    "st.settleDaily": "日結",
    "st.settleMonthly": "月結",
    "st.mpfEnabled": "啟用 MPF",
    "st.joinDate": "入職日期",
    "st.leaveDate": "離職日期",
    "st.baseSalary": "底薪",
    "st.hourlyRate": "時薪",
    "st.commissionMode": "抽成方式",
    "st.commissionPercent": "百分比",
    "st.commissionFixed": "固定金額",
    "st.commissionRate": "抽成率 (%)",
    "st.commissionEnabled": "啟用抽成",
    "st.commissionTiers": "分級抽成",
    "st.commissionTiersHint": "以下表格：營收每超過 [起始] 元，超過部分按 [%] 抽成",
    "st.addTier": "+ 加一級",
    "st.tierFrom": "起始",
    "st.tierRate": "%",
    "st.serviceCommission": "服務抽成",
    "st.serviceCommissionHint": "每個服務獨立設定抽成率（覆蓋預設）",
    "st.packageCommission": "套票銷售抽成 (%)",
    "st.packageCommissionHint": "員工每售出一張套票，按售價計嘅 %",
    "st.packageRedemptionCommission": "套票美容抽成 (%)",
    "st.packageRedemptionCommissionHint": "每次套票美容，按每次認列收入嘅 %",
    "st.canService": "可提供服務",
    "st.canServiceHint": "未勾選的服務，呢個員工唔會出現喺顧客預約頁",
    "st.weeklySchedule": "每週排班",
    "st.workDay": "上班",
    "st.offDay": "休息",
    "st.dayHours": "工作時間",
    "st.startTime": "開始",
    "st.endTime": "結束",
    "st.timeOff": "請假",
    "st.addTimeOff": "+ 新增請假",
    "st.timeOffType": "類型",
    "st.timeOffSick": "病假",
    "st.timeOffPersonal": "事假",
    "st.timeOffAnnual": "年假",
    "st.timeOffOther": "其他",
    "st.timeOffStart": "開始日期",
    "st.timeOffEnd": "結束日期",
    "st.timeOffReason": "原因",
    "st.permissions": "權限",
    "st.permViewRevenue": "查看營收",
    "st.permManageCRM": "管理客戶",
    "st.permManageStaff": "管理員工",
    "st.permManageSettings": "管理設定",
    "st.permManagePackages": "管理套票",
    "st.permManageReports": "查看報表",
    "st.saveBtn": "儲存",
    "st.deleteBtn": "刪除員工",
    "st.savedToast": "員工已儲存 ✓",
    "st.deleteConfirm": "確定要移除呢位員工？",
    "st.deletedToast": "員工已移除",
    "st.permViewWage": "查看薪金",
    "st.permPosTab": "收銀台",
    "st.permRefund": "退款",
    "st.staff.todayBookings": "今日預約",
    "st.staff.workHrs": "工時",
    "st.staff.tipsToday": "今日小費",
    "st.staff.payroll.title": "薪金",
    "st.staff.payroll.headerBasic": "底薪",
    "st.staff.payroll.headerHourly": "時薪",
    "st.staff.payroll.headerCommission": "抽成",
    "st.staff.payroll.headerTip": "小費",
    "st.staff.payroll.headerDeduct": "扣減",
    "st.staff.payroll.headerNet": "實發",
    "st.staff.payroll.recordWage": "紀錄今日薪金",
    "st.staff.payroll.payoutOne": "出薪 (今日)",
    "st.staff.payroll.payoutMonth": "出薪 (本月)",
    "st.staff.payroll.payMethod": "支付方式",
    "st.staff.payroll.cash": "現金",
    "st.staff.payroll.transfer": "轉帳",
    "st.staff.payroll.paidDaily": "✅ 已付日結",
    "st.staff.payroll.paidMonthly": "已付月結",
    "st.staff.payroll.unpaid": "⏳ 未付",
    "st.staff.payroll.unpaidMonthly": "未付（月結時支付）",
    "st.staff.payroll.empty": "呢日未有薪金紀錄",
    "st.staff.payroll.totalToday": "今日合計",
    "st.staff.payroll.totalMonth": "本月合計",
    "st.staff.payroll.from": "由",
    "st.staff.payroll.to": "至",
    "st.staff.payroll.dateRange": "日期範圍",
    "st.staff.payroll.saveDayBtn": "✓ 紀錄今日薪金",
    "st.staff.payroll.savedToast": "薪金已紀錄 ✓",
    "st.staff.payroll.payoutToast": "已出薪 ✓",
    "st.staff.payroll.confirmPayout": "確認支付？",
    "st.staff.payroll.bonus": "額外獎金",
    "st.staff.payroll.deduction": "扣減",
    "st.staff.payroll.addBonusBtn": "+ 獎金 / 扣減",
    "st.staff.payroll.adjKind.bonus": "獎金",
    "st.staff.payroll.adjKind.late": "遲到",
    "st.staff.payroll.adjKind.accident": "意外",
    "st.staff.payroll.adjKind.uniform": "制服",
    "st.staff.payroll.adjKind.advance": "預支",
    "st.staff.payroll.adjKind.fine": "罰款",
    "st.staff.payroll.adjKind.other": "其他",
  },
  "en": {
    "lang.name": "English",
    "common.cancel": "Cancel", "common.confirm": "Confirm", "common.save": "Save",
    "common.delete": "Delete", "common.edit": "Edit", "common.back": "Back",
    "common.next": "Next", "common.loading": "Loading…", "common.error": "Error",
    "common.retry": "Retry", "common.close": "Close", "common.search": "Search",
    "common.add": "Add", "common.copy": "Copy", "common.copied": "Copied",
    "common.logout": "Log out", "common.settings": "Settings", "common.today": "Today",
    "auth.login": "Log in", "auth.register": "Sign up", "auth.email": "Email",
    "auth.password": "Password", "auth.phone": "Phone", "auth.continue": "Continue",
    "auth.googleSignin": "Continue with Google",
    "tab.home": "Home", "tab.book": "Book", "tab.pets": "Pets",
    "tab.history": "History", "tab.profile": "Profile",
    "booking.selectPet": "Choose your pet", "booking.selectService": "Choose a service",
    "booking.selectStaff": "Choose a groomer", "booking.selectTime": "Choose a time",
    "booking.confirm": "Confirm booking", "booking.success": "Booking confirmed",
    "booking.total": "Total", "booking.deposit": "Deposit",
    "status.pending": "Pending", "status.checkedin": "Checked in",
    "status.grooming": "Grooming", "status.done": "Done",
    "status.cancelled": "Cancelled", "status.noshow": "No-show",
    "home.wallet": "Wallet", "home.points": "Points", "home.grooms": "Grooms",
    "home.upcoming": "Upcoming", "home.history": "Past visits",
    "home.noUpcoming": "No upcoming bookings", "home.bookNow": "Book now",
    "home.times": "",
    "book.pickPet": "Choose your pet", "book.pickService": "Pick a service",
    "book.pickStaff": "Choose a groomer", "book.anyStaff": "Any groomer",
    "book.pickTime": "Pick a time", "book.summary": "Review & confirm",
    "book.subtotal": "Subtotal", "book.totalAmount": "Total",
    "book.usePoints": "Use points", "book.confirmBtn": "Confirm booking",
    "book.addPet": "Add another pet",
    "profile.editProfile": "Edit profile", "profile.name": "Name",
    "profile.changeShop": "Switch shop", "profile.signOut": "Sign out",
    "hist.all": "All", "hist.viewReport": "View report",
    "hist.cancelBooking": "Cancel", "hist.rate": "Rate",
    "settings.title": "Settings", "settings.language": "Language",
    "settings.shopBasic": "Basics", "settings.shopBrand": "Branding & theme",
    "settings.shopHours": "Hours", "settings.shopPolicy": "Policies & features",
    "settings.payment": "Payment", "settings.analytics": "Analytics",
    "settings.reviews": "Reviews", "settings.messaging": "Messaging",
    "settings.terms": "Legal & terms", "settings.activity": "Activity log",
    "settings.system": "System & backup",
    "settings.group.shop": "Shop", "settings.group.ops": "Operations",
    "owner.tab.dashboard": "Dashboard", "owner.tab.schedule": "Schedule",
    "owner.tab.crm": "Customers", "owner.tab.staff": "Staff",
    "owner.tab.payroll": "Payroll", "owner.tab.reports": "Reports",
    "owner.tab.settings": "Settings",
    "day.0": "Sun", "day.1": "Mon", "day.2": "Tue", "day.3": "Wed",
    "day.4": "Thu", "day.5": "Fri", "day.6": "Sat",
    "owner.appointmentsToday": "Today's bookings",
    "owner.revenueToday": "Today's revenue",
    "owner.shopOpen": "Open today", "owner.shopClosed": "Closed today",
    "pet.name": "Name", "pet.weight": "Weight", "pet.weightKg": "Weight (kg)",
    "pet.breed": "Breed", "pet.birthday": "Birthday", "pet.type": "Type",
    "pet.dog": "🐶 Dog", "pet.cat": "🐱 Cat",
    "pet.coat": "Coat", "pet.coatShort": "Short", "pet.coatLong": "Long", "pet.coatMedium": "Medium",
    "pet.allergies": "Allergies", "pet.vaccineDate": "Last vaccine",
    "pet.vetNote": "Vet notes", "pet.add": "Add pet",
    "pet.edit": "Edit pet",
    "summary.mainService": "Service", "summary.addons": "Add-ons",
    "summary.spa": "SPA", "summary.surcharge": "Surcharge",
    "summary.discount": "Discount", "summary.paid": "Paid", "summary.due": "Due",
    "summary.depositPaid": "Deposit paid", "summary.settled": "Settled ✓",
    "summary.points": "Points", "summary.pointsEarn": "You'll earn",
    "walkin.title": "Walk-in registration",
    "walkin.newCustomer": "New customer",
    "walkin.searchCustomer": "Search customer (name / phone)",
    "walkin.discount": "Discount",
    "walkin.reason": "Reason (optional)",
    "walkin.staffRemark": "Staff note",
    "walkin.useWallet": "Use wallet balance",
    "walkin.submit": "Confirm registration",
    "walkin.submitStart": "Register & start grooming",
    "walkin.submitAndAnother": "Save & book another pet for the same customer",
    "toast.saved": "Saved",
    "toast.deleted": "Deleted",
    "toast.copyOk": "Copied",
    "toast.copyFail": "Copy failed",
    "toast.saveFail": "Save failed",
    "toast.networkErr": "Network error, please try again",
    "report.skin": "Skin 🩺", "report.ear": "Ears 👂",
    "report.teeth": "Teeth 🦷", "report.mood": "Mood 😊",
    "report.nails": "Nails 💅", "report.analGlands": "Anal glands",
    "report.parasites": "Parasites 🔍", "report.eyeDischarge": "Eye discharge",
    "report.matting": "Matting 🪮",
    "chip.normal": "Normal", "chip.good": "Good", "chip.none": "None",
    "chip.notNeeded": "Not needed", "chip.notFound": "None found",
    "chip.trimmed": "Trimmed", "chip.expressed": "Expressed",
    "chip.skin.dry": "Dry", "chip.skin.flakes": "Flakes",
    "chip.skin.rash": "Rash", "chip.skin.oily": "Oily",
    "chip.skin.inflamed": "Inflamed",
    "chip.ear.discharge": "Discharge", "chip.ear.odor": "Odor",
    "chip.ear.red": "Red/swollen", "chip.ear.mites": "Possible mites",
    "chip.teeth.mild": "Light tartar", "chip.teeth.heavy": "Heavy tartar",
    "chip.teeth.needsClean": "Needs cleaning", "chip.teeth.missing": "Missing teeth",
    "chip.mood.nervous": "Nervous", "chip.mood.excited": "Excited",
    "chip.mood.scared": "Scared", "chip.mood.aggressive": "Aggressive",
    "chip.nails.long": "Overgrown", "chip.nails.broken": "Broken",
    "chip.ag.inflamed": "Inflamed", "chip.ag.blocked": "Blocked",
    "chip.para.fleas": "Fleas", "chip.para.ticks": "Ticks",
    "chip.para.mites": "Mites",
    "chip.eye.little": "Mild", "chip.eye.much": "Heavy",
    "chip.eye.red": "Red", "chip.eye.abnormal": "Abnormal",
    "chip.mat.mild": "Mild", "chip.mat.medium": "Moderate",
    "chip.mat.severe": "Severe (shave needed)",
    "svc.sheet.edit": "Service — Edit", "svc.sheet.add": "Service — Add",
    "svc.sheet.spaEdit": "SPA — Edit", "svc.sheet.spaAdd": "SPA — Add",
    "svc.del": "Delete service", "svc.name": "Service name",
    "svc.basePrice": "Base price ($)", "svc.description": "Description",
    "svc.duration": "Estimated time (min)",
    "addon.edit": "Add-on — Edit", "addon.add": "Add-on — Add",
    "addon.name": "Add-on name", "addon.price": "Price ($)",
    "addon.del": "Delete add-on",
    "staff.edit": "Staff — Edit", "staff.add": "Staff — Add",
    "staff.name": "Name", "staff.role": "Role",
    "staff.commissionMode": "Commission mode",
    "staff.joinDate": "Join date", "staff.leaveDate": "Leave date",
    "staff.del": "Delete staff",
    "common.required": "Required",
    "ph.searchApt": "Search bookings…",
    "ph.searchCustomer": "Search customer name / phone…",
    "ph.searchNamePhone": "Search name or phone…",
    "ph.searchGlobal": "Search customers, bookings, pets…",
    "ph.searchTenant": "Search shop name…",
    "apt.detail": "Booking details", "apt.edit": "Edit booking",
    "apt.surcharge": "Surcharge", "apt.surchargeAdd": "Add surcharge",
    "apt.discount": "Discount", "apt.discountOpt": "Discount (optional)",
    "apt.staffNote": "Staff note (visible on booking card)",
    "apt.del": "Delete this booking permanently",
    "apt.delConfirm": "Permanently delete this booking? This cannot be undone — use Cancel to keep a record.",
    "apt.unsaved": "You have unsaved changes. Leave?",
    "book.pickDate": "Pick a date",
    "book.availableSlots": "Available times",
    "book.noSlotsToday": "Fully booked today — pick another date",
    "book.confirm": "Confirm booking",
    "book.depositNow": "Pay deposit now",
    "book.usePointsLabel": "Use points",
    "book.confirmBookingBtn": "Book now",
    "cust.edit": "Edit customer",
    "cust.name": "Name", "cust.phone": "Phone", "cust.phone2": "Alt phone",
    "cust.email": "Email", "cust.address": "Address", "cust.tags": "Tags",
    "val.enterName": "Please enter a name",
    "val.enterEmail": "Please enter an email",
    "val.enterPhone": "Please enter a valid phone number",
    "val.enterFirstNameBreed": "Please enter name and breed",
    "val.enterPetName": "Please enter the pet's name",
    "val.enterBreed": "Please enter the breed",
    "val.enterWeight": "Please enter the weight",
    "val.enterAmount": "Please enter a valid amount",
    "val.enterNewCustName": "Please enter the new customer's name",
    "val.pickCustomer": "Please pick or add a customer",
    "val.fillPetInfo": "Please fill in pet name, breed, weight",
    "val.pickPet": "Please pick or add a pet",
    "val.pickService": "Please pick a service",
    "val.pickStaff": "Please pick a groomer",
    "val.pickTime": "Please pick a start time",
    "val.pickDate": "Please pick a date",
    "val.enterServiceName": "Please enter a service name",
    "val.enterAddonName": "Please enter an add-on name",
    "val.enterInvName": "Please enter an item name",
    "val.enterPromoCode": "Please enter a promo code",
    "val.enterPackageName": "Please enter a name",
    "val.enterPetNameRequired": "Please enter the pet's name",
    "val.petBirthdayFuture": "Birthday can't be in the future — please check the date",
    "val.petBirthdayTooOld": "That birth year looks too old for a pet — please double-check it",
    "btn.next": "Next", "btn.back": "Back",
    "btn.bookNow": "Book now",
    "btn.continueGoogle": "Continue with Google",
    // Payment sheet
    "pay.title": "Checkout 💰", "pay.notFound": "Booking not found",
    "pay.pet": "Pet", "pay.svcDefault": "Grooming service", "pay.remove": "Remove",
    "pay.surchargePh": "Surcharge name (e.g. matting, large breed, calming fee)",
    "pay.addSpaAddon": "+ Add SPA / Add-on", "pay.addSurcharge": "+ Add surcharge",
    "pay.noAvailable": "No more items to add", "pay.addonLabel": "Add-on",
    "pay.subtotal": "Subtotal", "pay.discount": "Discount",
    "pay.depositPaid": "Deposit paid", "pay.balanceDue": "Balance due",
    "pay.cancel": "Cancel", "pay.continue": "Continue →",
    "pay.methodFallback": "Payment method", "pay.cash": "Cash 💵",
    "pay.back": "← Back", "pay.pickMethod": "Choose payment method",
    "pay.noMethods": "No payment methods enabled — using Cash. Enable methods at Settings → Payment.",
    "pay.due": "Amount due", "pay.failed": "Payment failed: ",
    "pay.dup": "Payment recorded (deduplicated)",
    "pay.collected": "Collected", "pay.paidTick": "Paid ✓",
    "pay.confirm": "Confirm payment",
    // EditApt
    "edit.notSaved": "You have unsaved changes. Leave anyway?",
    "edit.lockedPast": "Past booking · status locked (you can still edit the report / upload photos)",
    "edit.time": "Time", "edit.date": "Date", "edit.slot": "Slot",
    "edit.staff": "Groomer", "edit.service": "Service",
    "edit.changeSvc": "Change main service", "edit.note": "Staff note",
    "edit.del": "Delete this booking permanently",
    "edit.cancelApt": "Cancel booking", "edit.restoreApt": "Restore booking",
    "edit.history": "History", "edit.records": "Payments",
    "edit.no記錄": "No payment records yet",
    // Walk-in
    "wi.title": "Walk-in registration 🚶",
    "wi.newCust": "New customer", "wi.pickCust": "Pick customer",
    "wi.pickPet": "Pick pet", "wi.addPet": "Add pet",
    "wi.svc": "Main service", "wi.spa": "SPA", "wi.addons": "Add-ons",
    "wi.package": "Use package", "wi.walletUse": "Deduct from wallet",
    "wi.deposit": "Deposit", "wi.start": "Register & start grooming", "wi.regOnly": "Register only",
    // Marketing landing — English mirror
    "mk.nav.login": "Log in",
    "mk.nav.startFree": "Start free →",
    "mk.hero.badge": "Built for pet groomers",
    "mk.hero.titleLine1": "Drop the paper and Excel.",
    "mk.hero.titleLine2": "Run your salon from one app.",
    "mk.hero.subtitle": "Self-service booking · drag-drop schedule · auto WhatsApp · smart pricing · packages & wallet · auto payroll.",
    "mk.hero.subtitleLine2": "Built for pet salons in Hong Kong, Taiwan and Macau.",
    "mk.hero.ctaPrimary": "Start free",
    "mk.hero.ctaSecondary": "Have an account? Log in",
    "mk.hero.bullets": "✓ No credit card · ✓ 30-second setup · ✓ Free plan forever",
    "mk.hero.mockToday": "Today's bookings",
    "mk.painsHeading.label": "Why switch to PettoBook?",
    "mk.painsHeading.title": "Every daily pain point — already solved ✨",
    "mk.painsHeading.sub": "Co-designed with industry pros. 6 common pains → 6 built-in fixes.",
    "mk.pain1.tag": "Schedule",
    "mk.pain1.pain": "Whiteboard + Excel = double-bookings",
    "mk.pain1.detail": "Customer reschedules at the last minute. You cross out the old slot in your notebook, but the other groomer doesn't see it — two pets show up at once.",
    "mk.pain1.fix": "Drag-drop calendar, auto conflict-detection",
    "mk.pain1.b1": "Reschedule or reassign by dragging the booking",
    "mk.pain1.b2": "System catches time clashes and blocks the save",
    "mk.pain1.b3": "Customer gets a WhatsApp reschedule notification automatically",
    "mk.pain2.tag": "Booking",
    "mk.pain2.pain": "Phone & WhatsApp queue never empties",
    "mk.pain2.detail": "1-2 hours every day confirming bookings by hand. Miss a call once, the customer walks to your competitor.",
    "mk.pain2.fix": "24/7 self-service booking",
    "mk.pain2.b1": "One link, drop it on IG / FB / WhatsApp",
    "mk.pain2.b2": "Customer picks service, groomer, time — booking lands in your calendar",
    "mk.pain2.b3": "Instant push to your phone for every new booking",
    "mk.pain3.tag": "Pricing",
    "mk.pain3.pain": "Pricing tables = trainee mistakes",
    "mk.pain3.detail": "Same dog, 3kg vs 8kg = different price. Long coat, matting, breed surcharges. Asking staff to remember it all is a complaint factory.",
    "mk.pain3.fix": "Smart pricing matrix — enter the weight, see the price",
    "mk.pain3.b1": "Auto-priced by breed / weight / coat",
    "mk.pain3.b2": "One-tap SPA / add-on / surcharge",
    "mk.pain3.b3": "Discounts and package redemptions applied automatically",
    "mk.pain4.tag": "Payroll",
    "mk.pain4.pain": "Payroll runs till midnight",
    "mk.pain4.detail": "Every groomer has a different commission %, plus per-service rates, package commissions, deductions. Closing payroll eats 2-3 days every month.",
    "mk.pain4.fix": "Auto payroll + commission + roster",
    "mk.pain4.b1": "Different commission % per service / package",
    "mk.pain4.b2": "Hours, OT, deductions, transfers — all on one page",
    "mk.pain4.b3": "Export payslips for staff confirmation any time",
    "mk.pain5.tag": "Packages",
    "mk.pain5.pain": "Paper punch cards = lost = lost revenue",
    "mk.pain5.detail": "Customer claims they lost the punch card and can't remember how many visits they used. No record means you usually just write it off.",
    "mk.pain5.fix": "Digital packages + points + stored-value wallet",
    "mk.pain5.b1": "Visits auto-deducted, cloud record never lost",
    "mk.pain5.b2": "Points auto-awarded, redeemable for discounts or gifts",
    "mk.pain5.b3": "Customers check balances in their own app any time",
    "mk.pain6.tag": "Privacy & security",
    "mk.pain6.pain": "Customer data scattered across chats & spreadsheets",
    "mk.pain6.detail": "WhatsApp, Notes, Excel, printed register books. When staff leave, your data walks with them — and you're not GDPR-compliant either.",
    "mk.pain6.fix": "Centralised cloud, role-based access, auto backup",
    "mk.pain6.b1": "Per-staff role + service permissions",
    "mk.pain6.b2": "Daily auto-backup + 7-day point-in-time restore",
    "mk.pain6.b3": "One-tap GDPR export / delete",
    "mk.cmp.label": "Compare",
    "mk.cmp.title": "Why PettoBook over paper or generic SaaS?",
    "mk.cmp.colPaper": "📓 Paper / Excel",
    "mk.cmp.colGeneric": "🌐 Generic SaaS",
    "mk.cmp.colUs": "🐾 PettoBook",
    "mk.cmp.r1": "Smart pricing by coat / weight",
    "mk.cmp.r2": "Pet profile + health record",
    "mk.cmp.r3": "Package / points / stored-value wallet",
    "mk.cmp.r4": "Auto payroll + commission",
    "mk.cmp.r5": "WhatsApp booking notifications",
    "mk.cmp.r6": "Self-booking link",
    "mk.cmp.r7": "GDPR data protection",
    "mk.cmp.r8": "Traditional Chinese UI + HK / TW / MO support",
    "mk.cmp.legend": "✓ Built-in · △ Partial / paid · ✗ None",
    "mk.how.label": "3-step setup",
    "mk.how.title": "Zero to first booking — faster than brewing tea 🧋",
    "mk.how.s1.title": "30-second setup",
    "mk.how.s1.body": "Pick a plan, set your shop name and booking link. We seed 4 services, 3 SPAs, 3 add-ons — you're live.",
    "mk.how.s2.title": "Share the booking link",
    "mk.how.s2.body": "Drop pettobook.com/shop/your-shop on WhatsApp, IG, Facebook. Customers click in and book themselves.",
    "mk.how.s3.title": "Run schedule & customers",
    "mk.how.s3.body": "Bookings sync to your calendar and notify customers automatically. Every pet has their own profile, photos and health notes.",
    "mk.price.label": "Plans",
    "mk.price.title": "Plans for solo shops to chains 💰",
    "mk.price.sub": "Upgrade or downgrade any time. No lock-in.",
    "mk.price.popular": "Most popular 🌟",
    "mk.price.free": "Free",
    "mk.price.permonth": "/mo",
    "mk.price.unlimitedStaff": "Unlimited staff",
    "mk.price.maxStaffPrefix": "Up to ",
    "mk.price.maxStaffSuffix": " groomers",
    "mk.price.btnFree": "Start free",
    "mk.price.btnChoose": "Choose plan",
    "mk.price.f.scheduling": "Drag-drop scheduling",
    "mk.price.f.crm": "Customer CRM",
    "mk.price.f.whatsapp": "WhatsApp / SMS / Email",
    "mk.price.f.analytics": "Reports & analytics",
    "mk.price.f.payroll": "Payroll & commission",
    "mk.price.f.posLite": "POS (100 products)",
    "mk.price.f.posStandard": "POS: stock, refunds, shifts (500 products)",
    "mk.price.f.posFull": "Full POS: purchasing, AI (unlimited products)",
    "mk.price.f.smartPricing": "Smart pricing matrix",
    "mk.price.f.recurring": "Recurring bookings",
    "mk.cta.title": "Ready to level up your salon?",
    "mk.cta.sub": "30-second signup. Basic plan free forever. Trusted by 100+ pet salons.",
    "mk.cta.btn": "Start free now",
    "mk.foot.copy": "© PettoBook · Designed for pets",
    "mk.foot.privacy": "Privacy",
    "mk.foot.terms": "Terms",
    "mk.foot.contact": "Contact us",
    "mk.mock.headerToday": "📅 Today's calendar",
    "mk.mock.headerTodaySub": "Today · 5 bookings",
    "mk.mock.bookingHeader": "🐾 Furmily Pet Salon",
    "mk.mock.bookingHeaderSub": "Customer self-booking page",
    "mk.mock.step1": "1. Pick service",
    "mk.mock.step2": "2. Pick time",
    "mk.mock.confirmBtn": "Confirm",
    "mk.mock.pricingHeader": "✂️ Bath & Cut — Momo",
    "mk.mock.pricingSub": "Smart pricing applied",
    "mk.mock.pricingDog": "Poodle · 4kg · Long coat",
    "mk.mock.autoDetect": "Auto",
    "mk.mock.basePrice": "Base",
    "mk.mock.longCoatFee": "Long coat",
    "mk.mock.mattingFee": "Matting",
    "mk.mock.spaA": "Add SPA - A",
    "mk.mock.balanceDue": "Balance due",
    "mk.mock.collect": "💵 Collect",
    "mk.mock.payrollHeader": "💰 June payroll — Jason",
    "mk.mock.payrollSub": "Jun 1 - 30",
    "mk.mock.basePay": "Base salary",
    "mk.mock.svcComm": "Service commission (15%)",
    "mk.mock.pkgComm": "Package commission (8%)",
    "mk.mock.ot": "OT 12h × $80",
    "mk.mock.deduct": "Deduction (1 day off)",
    "mk.mock.netPay": "Net pay",
    "mk.mock.walletHeader": "🎁 Customer — Momo",
    "mk.mock.walletSub": "Poodle · 4kg",
    "mk.mock.pkgVisitsCards": "6 visits / 2 cards",
    "mk.mock.pkgList": "Packages",
    "mk.mock.pkg10": "10-visit package",
    "mk.mock.left4": "4 left",
    "mk.mock.expires1": "Expires 2026-12-31",
    "mk.mock.pkgSpa6": "SPA 6-visit card",
    "mk.mock.left2": "2 left",
    "mk.mock.expires2": "Expires 2027-03-15",
    "mk.mock.walletEarn": "💎 Earn +35 pt on this visit",
    "mk.mock.secHeader": "🛡️ System & backup",
    "mk.mock.secSub": "Cloud security · GDPR",
    "mk.mock.backupOn": "✓ Auto cloud backup on",
    "mk.mock.backupSub": "Daily backup · 7-day PITR",
    "mk.mock.staffPerms": "Staff permissions",
    "mk.mock.roleMgr": "Manager · Full access",
    "mk.mock.roleGroom": "Groomer · Own bookings only",
    "mk.mock.roleFront": "Front desk · Schedule + payment",
    "mk.mock.exportData": "📥 Export data",
    "mk.mock.deleteAcc": "🗑️ Delete account",
    // Public booking
    "pub.bookNow": "Book now",
    "pub.about": "About us", "pub.contact": "Contact", "pub.team": "Our groomers",
    "pub.services": "Services", "pub.spaSection": "SPA", "pub.addonsSection": "Add-ons",
    "pub.hours": "Hours", "pub.closedToday": "Closed today",
    "pub.anyStaff": "Any groomer", "pub.review": "Review & confirm",
    "pub.signupOrGuest": "Log in / Sign up / Guest",
    "pub.guestName": "Your name", "pub.guestPhone": "Phone (+852)",
    "pub.haveAccount": "I have an account", "pub.newAccount": "New customer",
    "pub.googleContinue": "Continue with Google", "pub.emailLogin": "Log in with email",
    "pub.visitor": "Book as guest", "pub.confirmed": "Booking confirmed 🎉",
    "pub.viewBooking": "View booking",
    // CustomerApp
    "cust.tabHome": "Home", "cust.tabBook": "Book",
    "cust.tabPets": "Pets", "cust.tabHist": "History", "cust.tabMe": "Me",
    "cust.helloMorning": "Good morning", "cust.helloAfternoon": "Good afternoon", "cust.helloEvening": "Good evening",
    "cust.nextVisit": "Next booking", "cust.viewAll": "View all",
    "cust.bookAgain": "Book again",
    "cust.pkgTitle": "My packages", "cust.pkgRemain": "Remaining", "cust.pkgVisits": " visits",
    "cust.pkgExpiry": "Expires", "cust.buyPkg": "Buy package",
    "cust.changeShop": "Switch shop", "cust.signOut": "Sign out",
    "cust.editName": "Edit name",
    // ── extended set ──
    "common.other": "Other", "common.free": "Free", "common.unnamed": "(Unnamed)",
    "common.unassigned": "Unassigned", "common.saved": "Saved ✓",
    "common.savedShort": "Saved", "common.optional": " (optional)",
    "common.pickDate": "Pick a date", "common.pickTime": "Pick a time",
    "common.zoomIn": "Zoom in", "common.or": "or",
    "edit.editBtn": "Edit", "edit.bookAgain": "Book again",
    "edit.report": "Report", "edit.fillReport": "Add report",
    "edit.noLabel": "🏷️ No label", "edit.customLabel": "✏️ Custom…",
    "edit.customLabelPrompt": "Custom label (blank to clear)",
    "edit.editPet": "Edit pet", "edit.editCust": "Edit customer",
    "edit.addNote": "+ Alert note",
    "edit.addNotePrompt": "Add an alert note (e.g. bites, skin allergy)",
    "edit.addNoteTitle": "Add pet alert note",
    "edit.removeNote": "Remove alert",
    "edit.noteAdded": "Alert note added",
    "edit.avatarUploaded": "Pet avatar uploaded 📷",
    "edit.uploadFail": "Upload failed: ",
    "edit.pastLocked": "Past booking · status locked",
    "edit.pastLockedToast": "Past booking — status can no longer change",
    "edit.markAs": "Mark as \"", "edit.andNotify": "\" and notify the customer",
    "edit.statusBackPrompt1": "Roll status back to \"",
    "edit.statusBackPrompt2": "\"?\n(Clears later timestamps; customer is not notified.)",
    "edit.statusBackPaidWarn": "⚠️ This booking is already paid (${amt}).\nReverting will NOT auto-refund — the payment log stays and EOD will keep counting it.\nIf you want to refund, void it first with \"Cancel payment\".\n\nContinue with the revert?",
    "edit.fillReportToast": "Please fill in the health report",
    "edit.statusUpdated1": "Updated to \"", "edit.statusUpdated2": "\" and customer notified",
    "edit.checkin": "Checked in", "edit.grooming": "Grooming",
    "edit.done": "Done", "edit.doneWithReport": "Done & write report",
    "edit.cancelled": "Cancelled", "edit.noshow": "No-show",
    "edit.discardPrompt": "Discard all changes?",
    "edit.packageFail": "Package redemption failed",
    "edit.retryRedeem": "🔄 Retry redeem",
    "edit.clearAlert": "✕ Dismiss",
    "edit.notBoundToPkg": "No package linked to this booking",
    "edit.retryRedeemOk": "Package redeemed ✓",
    "edit.retryFail": "Retry failed: ",
    "edit.noOtherHist": "No other booking history",
    "edit.healthReport": "Health report",
    "edit.col.date": "Date", "edit.col.slot": "Slot",
    "edit.col.svc": "Service", "edit.col.staff": "Staff",
    "edit.col.groomingReq": "Request", "edit.col.intNote": "Internal note",
    "edit.col.groomerNote": "Groomer note", "edit.col.cancelReason": "Cancel reason",
    "edit.beforeShot": "📷 Before", "edit.afterShot": "✨ After",
    "edit.spaShot": "💧 SPA",
    "edit.discountOverCap1": "Discount ", "edit.discountOverCap2": "% exceeds your cap ",
    "edit.discountOverCap3": "%, ask the owner to authorise",
    "edit.staffConflict1": "Staff already booked at ", "edit.staffConflict2": " — cannot reschedule",
    "edit.manualAdj": "Manual adjustment",
    "edit.svcChanged1": "Main service $", "edit.discAdj": "Discount adjusted",
    "edit.reschedTo": "Rescheduled → ", "edit.staffChanged": "Staff changed",
    "edit.petNoteUpdated": "Pet note updated",
    "edit.rescheduledTitle": "Booking rescheduled 📅",
    "edit.rescheduledBody": "Your booking is now at ",
    "edit.preFilled": "Pre-filled ",
    "wi.title2": "Walk-in registration",
    "wi.startNow": "Register → start grooming now",
    "wi.walletDeduct": "Wallet deduction",
    "wi.subtotal": "Subtotal", "wi.balance": "Balance",
    "wi.duration": "⏱️ Estimated duration (per dog)",
    "wi.customDur": "Custom", "wi.svcName": "Service name",
    "wi.spaName": "SPA name", "wi.basePrice": "Base price / shown when unset ($)",
    "wi.priceMode": "Pricing mode", "wi.priceBySize": "Price by size",
    "wi.priceMatrix": "Size × coat length pricing",
    "wi.weightFrom": "Weight from (kg)",
    "wi.newSize": "Add size",
    "wi.delete": "Delete", "wi.addonsRow": "Add-ons ×",
    "wi.allergiesNone": "Allergies (none)",
    "wi.allergiesEg": "e.g. Lively dog — extra care needed…",
    "wi.spaEg": "e.g. Calming skin SPA: warm salt bath + deep moisture, ~30 min.",
    "wi.sizeEg": "e.g. Small",
    "wi.coatShort": "Short", "wi.coatLong": "Long", "wi.coatMed": "Medium",
    "wi.sizeSmall": "Small", "wi.sizeMed": "Medium",
    "wi.sizeLarge": "Large", "wi.sizeXL": "XL", "wi.sizeGiant": "Giant",
    "wi.dog": "🐶 Dog", "wi.cat": "🐱 Cat", "wi.small": "🐇 Small animal",
    "wi.name": "Name *", "wi.breed": "Breed *",
    "wi.phone": "Phone *", "wi.fullName": "Name *",
    "wi.weight": "Weight kg *",
    "wi.newBreed": "New breed",
    "wi.tagVip": "VIP", "wi.tagFriend": "Staff/family",
    "wi.tagCompensation": "Compensation", "wi.tagTrial": "Trial",
    "wi.tagEvent": "Event", "wi.tagReferral": "Referral",
    "wi.custom": "Custom", "wi.row": "row", "wi.no": "#",
    "wi.minGtMax": "\" min is greater than max — swap them",
    "wi.negWeight": "\" weight cannot be negative",
    "wi.kgNoMatch": "kg has no matching tier, using base $",
    "wi.slotTaken": "Taken",
    "wi.overlap1": ") and \"", "wi.overlap2": ") overlap",
    "wi.petsCount": " pets",
    "wi.surchargeAdd": "+ Surcharge",
    "wi.surchargeName": "Name (e.g. matting fee)",
    "wi.preset": "Preset",
    "pub.welcome": "Welcome 🐾",
    "pub.signupPrompt": "Log in or sign up to start booking",
    "pub.loginContinue": "Log in to continue",
    "pub.loginOrSignup": "Log in / Sign up",
    "pub.loginFail": "Login failed",
    "pub.googleFail": "Google sign-in failed",
    "pub.appleFail": "Apple sign-in failed",
    "pub.appleSignin": "Sign in with Apple",
    "pub.continueWith": "Continue with ",
    "pub.emailGoogle": "Email / Google · 30s",
    "pub.newAccountStart": "Create a new account to book",
    "pub.shopSchedule": "See the shop schedule at a glance",
    "pub.multiPet": "Multiple pets supported 🐾",
    "pub.bookSuccess": "Done! Awaiting drop-off",
    "pub.maxReward": "Max reward reached 🎉",
    "pub.rewards": "Rewards",
    "pub.svcRemoved": "Service no longer available — pick another",
    "pub.savePetFail": "Failed to save pet info",
    "pub.bookFail": "Booking failed",
    "pub.bookNotSaved": "Booking not saved — please try again or contact the shop",
    "pub.promoFail": "Promo code could not be applied — show it at the shop on arrival.",
    "pub.loginBeforeBook": "Please log in before booking",
    "pub.elderRequired": "Senior pets need a waiver on the confirm step",
    "pub.discount50": "$50 off",
    "pub.newCustOnly": "　·　New customers only",
    "pub.todayOpen": " · Today",
    "pub.included": " included",
    "pub.daysAgo": " days ago",
    "pub.lastVisit": "Last visit ",
    "pub.fillForPrice": "Fill the form and we'll show an exact price",
    "pub.confirmBtn": "Confirm booking",
    "pub.confirmDeposit": "Confirm + Pay deposit",
    "pub.payDeposit": "Pay deposit",
    "pub.tax": "Tax",
    "pub.submitting": "Submitting…",
    "pub.pickSvc": "Please pick a main service",
    "pub.pickPet": "Please pick or add a pet",
    "pub.pickDate": "Please pick a date",
    "pub.pickTime": "Please pick a time",
    "pub.enterName": "Please enter a name",
    "pub.enterBreed": "Please enter a breed",
    "pub.enterWeight": "Please enter a weight",
    "pub.stepPet": "Pet + Service",
    "pub.stepTime": "Groomer + Time",
    "pub.choosePet": "Pick your pet or add a new one",
    "pub.namePh": "e.g. Fluffy",
    "pub.weightPh": "e.g. 5.5",
    "pub.phonePh": "Phone (e.g. 9123 4567)",
    "pub.privacyAccept": "Privacy Policy",
    "pub.none": "None",
    "pub.holiday": "Closed",
    "pub.dog": "Dog", "pub.cat": "Cat",
    "pub.rabbit": "Rabbit", "pub.hamster": "Hamster", "pub.otherPet": "Other",
    "pub.coatShort": "Short", "pub.coatLong": "Long", "pub.coatMed": "Medium",
    "pub.langTrad": "ZH",
    "cust.helloMorning2": "Good morning", "cust.bookFail": "Booking failed — please register at the shop",
    "cust.svcDownloaded": "Profile downloaded ✓",
    "cust.downloadData": "Download my data", "cust.deleteAccount": "Delete account",
    "cust.logout": "Log out",
    "cust.downloadFail": "Download failed",
    "cust.savePhoneFail": "Failed to save phone: ",
    "cust.savedShort": "Saved",
    "cust.saveFail": "Save failed",
    "cust.deleteFail": "Delete failed — please contact support",
    "cust.wallet": "Wallet balance",
    "cust.walletLow": "Insufficient wallet balance",
    "cust.signSig": "Waiver signed",
    "cust.elderSign": "Senior pet waiver required",
    "cust.shareFail": "Share failed: ",
    "cust.viewAgain": "View again",
    "cust.topupWallet": "Hi, I'd like to top up my wallet: ",
    "cust.buyPkgMsg": "Hi, I'd like to buy a package: ",
    "cust.recentBook": "Recent bookings",
    "cust.transactions": "Transactions",
    "cust.myRewards": "🎟️ My coupons",
    "cust.privacyRights": "🔐 Privacy & data",
    "cust.recurringBook": "Recurring bookings",
    "cust.depositHistory": "Deposit & spend history",
    "cust.spaSection": "SPA services 💆",
    "cust.luxAddons": "Treat-yourself add-ons ✨",
    "cust.beforeAfter": " Before & after ✨",
    "cust.styleHint": "✂️ Preferred style: ",
    "cust.vetNote": "📋 Vet notes",
    "cust.allergyInfo": "Allergies",
    "cust.lastVaccine": "Last vaccine",
    "cust.depositTopup": "Top up your wallet to see history here",
    "cust.regPetFirst": "Register your pet first 🐾",
    "cust.notifyShop": "✅ Shop notified, awaiting payment instructions",
    "cust.waitingPay": "WhatsApp opened — waiting on payment confirmation",
    "cust.contactShop": "Contact shop on WhatsApp",
    "cust.linkedOk": "✅ Linked successfully ",
    "cust.shareReview": "Share your experience...",
    "cust.encMsg": "Encouragement",
    "cust.deposit": "Deposit",
    "cust.svc": "Service",
    "cust.feeLabel": " (fee $",
    "cust.expires": " expires",
    "cust.daysValid": " days valid",
    "cust.weeks": " weeks",
    "cust.times": " times",
    "cust.deletedPet": "(Pet deleted)",
    "cust.encNicknamePh": "e.g. Milky",
    "cust.usePointsRedeem": "Use points (",
    "cust.pointsLabel": " pts)",
    "cust.exchangePts": " pts redeem",
    "cust.rewardPts": " pts 🎉",
    "cust.copyPaste": "Prev/Next",
    "cust.lastGroom": "Last groom ",
    "cust.styleEg": "e.g. Trim short overall, keep paws fluffy, round face",
    "cust.kgPriceHint": "kg) for an exact price",
    "cust.priceFrom": "$X+",
    "cust.fpsLabel": "FPS / FastPay ⚡",
    "cust.spaFree": "SPA free",
    "cust.spaFreePkg": "Free (package)",
    "cust.spaFreeUpsell": "Free SPA/Add-on",
    "cust.spaFreeHigh": "Free premium SPA",
    "cust.rewardCoupon": "Coupon",
    "cust.transferred": " transferred $",
    "cust.contactPhone": "Phone *",
    "cust.companion": " (companion)",
    "cust.refillSoon": "Running low? Top up ",
    "cust.maxWeight1": "Max accepted weight is ",
    "cust.maxWeight2": "kg, ",
    "cust.elderSignReq": "Senior pet waiver required",
    "cust.appointmentReschd": "'s booking has been moved to ",
    "cust.updateOk": "'s info updated!",
    "cust.addedOk": " added ✓",
    "cust.addedPickSvc": " added — please pick a service",
    "cust.svcAddedOk": " added successfully!",
    "cust.oldPet": " pets' previous data",
    "cust.nicknameEg": "e.g. Milky",
    "cust.confirmAdd": "✨ Confirm add",
    "cust.addCount": " added ✓",
    "cust.medCoat": "Medium", "cust.midDog": "Medium dog",
    "cust.nicePoint": "Good 🙂",
    "cust.encPh": "e.g. Milky",
    "cust.encEg2": "e.g. Milky",
    "apt.viewMore": "View more", "apt.surchargePh": "Name (e.g. matting fee)",
    "apt.labelCustom": "Custom",
    "apt.labelPending": "Pending", "apt.labelConfirmed": "Confirmed",
    "apt.labelPaid": "Paid", "apt.labelLate": "Late",
    "apt.labelWait": "Waiting", "apt.labelVip": "VIP",
    "apt.labelRush": "Rush", "apt.labelOther": "Other",
    "apt.statusPending": "Pending", "apt.statusCheckedin": "Checked in",
    "apt.statusGrooming": "Grooming", "apt.statusDone": "Done",
    "apt.statusCancelled": "Cancelled", "apt.statusNoshow": "No-show",
    "apt.yourPet": "your pet",
    "apt.surcharge": "Surcharge",
    "apt.payRecord": "No payment records yet",
    "apt.otherMethod": "Other",
    "apt.via": "By: ",
    "apt.cash": "Cash 💵", "apt.card": "Card 💳",
    "apt.fps": "FPS / PayMe 📱", "apt.walletM": "Wallet 💰",
    "apt.pkgRemain": " packages",
    "apt.pkgVisits": " visits / ",
    "apt.noPkg": "No package",
    "apt.editStart": "Edit start time",
    "apt.walkin": "Walk-in", "apt.unclaimed": "Unclaimed",
    // Customer-side iteration 1
    "cust.book.title": "Book a service",
    "cust.book.summary": "Review & confirm",
    "cust.book.depositDue": "Deposit due",
    "cust.book.totalDue": "Total due",
    "cust.book.walletDeduct": "Wallet deduction",
    "cust.book.useWallet": "Use wallet balance",
    "cust.book.walletBalance": "Wallet balance",
    "cust.book.usePoints": "Apply points",
    "cust.book.pointsDiscount": "Points discount",
    "cust.book.voucherDiscount": "Voucher discount",
    "cust.book.actualDeposit": "Deposit to pay",
    "cust.book.depositMethod": "Deposit payment method",
    "cust.book.markPaid": "✅ Paid — submit booking",
    "cust.book.payLater": "Pay later — at the shop",
    "cust.book.wallet.notEnough": "Wallet balance is short",
    "cust.book.qrInstructions": "Pay with one of these, then tap \"Paid — submit booking\".",
    "cust.book.useVoucher": "🎟️ Use a voucher (optional)",
    "cust.book.usePackage": "📦 Use a package (optional)",
    "cust.book.notUsing": "Don't use",
    "cust.book.success": "Booking confirmed!",
    "cust.book.successSub": "See you and {petName} soon 🐾",
    "cust.book.rescheduleSuccess": "Booking rescheduled 🎉",
    "cust.book.cancelSuccess": "Booking cancelled",
    "cust.book.bookNow": "Book now",
    "cust.book.confirmAndPay": "Confirm & pay",
    "cust.book.markComplete": "I've paid — notify shop",
    "cust.book.alreadyNotified": "Shop notified — awaiting confirmation",
    "cust.book.waNotConfigured": "Shop hasn't set up WhatsApp — contact directly",
    "cust.book.policyAccepted": "I have read and agree to the terms",
    "cust.book.sendReceipt": "Send receipt via WhatsApp",
    "cust.book.petAge.senior": "Senior pets require a liability waiver",
    "cust.book.petAge.tooYoung": "Pets under 4 months can't be booked",
    "cust.book.overWeight": "Over weight limit",
    "cust.book.breedBlocked": "This breed isn't accepted",
    "cust.history.title": "Booking history",
    "cust.history.upcoming": "Upcoming",
    "cust.history.past": "Past visits",
    "cust.history.empty": "No bookings yet",
    "cust.history.emptySub": "Your bookings will show up here.",
    "cust.history.rate": "Rate",
    "cust.history.viewReport": "View report",
    "cust.history.reschedule": "Reschedule",
    "cust.history.cancel": "Cancel booking",
    "cust.rewards.title": "Rewards",
    "cust.rewards.stamps": "Stamps",
    "cust.rewards.vouchers": "Vouchers",
    "cust.rewards.points": "Points",
    "cust.rewards.empty": "Nothing here yet",
    "cust.rewards.emptySub": "Complete a booking to start earning.",
    "cust.rewards.unlocked": "Unlocked rewards",
    "cust.rewards.useNow": "Use →",
    "cust.rewards.shopGifts": "🎟️ Shop-issued vouchers",
    "cust.rewards.useAtBooking": "Pick \"Use voucher\" at your next booking.",
    "cust.wallet.balance": "Wallet balance",
    "cust.wallet.topup": "Top up 💰",
    "cust.wallet.history": "Transactions",
    "cust.wallet.empty": "No transactions yet",
    "cust.profile.contact": "Contact",
    "cust.profile.security": "Account security",
    "cust.profile.passwordChange": "Change password",
    "cust.profile.dataRights": "🔐 Data rights",
    "cust.profile.exportData": "📥 Export my data",
    "cust.profile.deleteAccount": "🗑️ Delete account",
    "cust.profile.deleteWarn": "Permanently delete account? This can't be undone.",
    "cust.profile.deletedToast": "Account permanently deleted",
    "cust.profile.dataDownloaded": "Data downloaded ✓",
    "cust.toast.saved": "Saved ✓",
    "cust.toast.savedShort": "Saved",
    "cust.toast.saveFailed": "Save failed",
    "cust.toast.copied": "Copied ✓",
    "cust.toast.deleted": "Deleted",
    "cust.toast.rateThanks": "Thanks for rating ⭐",
    "cust.toast.welcome": "Welcome",
    "cust.confirm.cancelBooking": "Cancel this booking?\n• The shop will be notified\n• Deposit refunded per shop policy",
    "cust.confirm.deleteAccount": "Permanently delete account? All data is gone forever.",
    "cust.label.contact": "📞 Contact",
    "cust.label.address": "Address",
    "cust.label.email": "Email",
    "cust.label.phone": "Phone",
    "cust.label.editProfile": "Edit profile",
    "cust.label.recentBookings": "Recent bookings",
    "cust.label.depositHistory": "Deposit & spending history",
    "cust.label.recurringSettings": "Recurring bookings",
    "cust.label.noRecurring": "No recurring bookings set",
    "cust.label.dataExport": "Export my data",
    "cust.label.dataDelete": "Permanently delete my account",
    "cust.empty.noPets": "No pets yet",
    "cust.empty.noPetsSub": "Tap \"Add\" to register your first pet.",
    // Iteration 2 — history / pets / profile
    "cust.hist.recentBookings": "Recent bookings",
    "cust.hist.recurring": "Recurring",
    "cust.hist.noMatch": "No matching bookings",
    "cust.hist.noMatchSub": "Tap the book button below to create one.",
    "cust.hist.deletedPet": "(deleted pet)",
    "cust.hist.addons": "✨ Add-ons: ",
    "cust.hist.cancelBtn": "Cancel",
    "cust.hist.rescheduleBtn": "Reschedule",
    "cust.hist.rateBtn": "Rate groomer",
    "cust.hist.viewReport": "View report",
    "cust.hist.cancelReason": "Reason: ",
    "cust.pets.addFirst": "Add your first pet 🐾",
    "cust.pets.addFirstSub": "Fill in the details, then start booking.",
    "cust.pets.type": "Type",
    "cust.pets.typeDog": "Dog",
    "cust.pets.typeCat": "Cat",
    "cust.pets.typeRabbit": "Rabbit",
    "cust.pets.typeHamster": "Hamster",
    "cust.pets.weight": "Weight (kg)",
    "cust.pets.weightPh": "e.g. 5.5",
    "cust.pets.breed": "Breed",
    "cust.pets.coat": "Coat length",
    "cust.pets.short": "Short",
    "cust.pets.long": "Long",
    "cust.pets.allergyInfo": "Allergies",
    "cust.pets.allergyPh": "e.g. None / lavender",
    "cust.pets.vaccineDate": "Last vaccination",
    "cust.pets.vetNote": "Vet note",
    "cust.pets.vetNotePh": "Special care notes",
    "cust.pets.editBtn": "Edit",
    "cust.pets.deleteBtn": "Delete",
    "cust.pets.confirmDelete": "Delete this pet?",
    "cust.pets.addBtn": "Add pet",
    "cust.pets.saveBtn": "💾 Save changes",
    "cust.pets.newBtn": "✨ Add",
    "cust.pets.profilePhoto": "Profile photo 📸",
    "cust.pets.allergy": "Allergies",
    "cust.pets.vaccine": "Vaccine",
    "cust.pets.notRecorded": "Not recorded",
    "cust.pets.noAllergy": "None",
    "cust.profile.accountId": "Account ID",
    "cust.profile.shopBranch": "Home shop",
    "cust.profile.tags": "Tags",
    "cust.profile.notSet": "—",
    "cust.profile.viewPolicy": "View privacy policy",
    "cust.profile.changeLanguage": "🌐 Language",
    "cust.recurring.title": "Set up recurring booking",
    "cust.recurring.selectPet": "Pet",
    "cust.recurring.selectService": "Service",
    "cust.recurring.selectStaff": "Groomer",
    "cust.recurring.selectDay": "Day of week",
    "cust.recurring.selectTime": "Time",
    "cust.recurring.interval": "Repeats",
    "cust.recurring.weekly": "Weekly",
    "cust.recurring.monthly": "Monthly",
    "cust.recurring.saveBtn": "Save recurring booking",
    "cust.recurring.savedToast": "Recurring booking saved ✓",
    "cust.cancel.reason": "Cancellation reason",
    "cust.cancel.reasonPh": "Let us know why (optional)",
    "cust.cancel.others": "Other",
    "cust.cancel.submit": "Confirm cancellation",
    "cust.cancel.refunded": "Refunded",
    "cust.rate.title": "Rate your groomer",
    "cust.rate.sub": "Your feedback helps other customers ❤️",
    "cust.rate.commentPh": "Leave a comment (optional)",
    "cust.rate.submit": "Submit rating",
    "cust.report.title": "Grooming report",
    "cust.report.close": "Close",
    // ── Iteration 3 — Owner-side ──
    "own.dash.appointmentsToday": "Today's bookings",
    "own.dash.revenueToday": "Today's revenue",
    "own.dash.lowStock": "Low stock alert",
    "own.dash.staleBanner": "📋 Unsettled bookings",
    "own.dash.staleBannerStale": " past bookings still not marked complete.",
    "own.dash.staleBannerUnpaid": " completed bookings with outstanding balance.",
    "own.dash.staleBannerTap": "Tap to resolve.",
    "own.dash.walkinBtn": "Walk-in",
    "own.schedule.day": "Day",
    "own.schedule.week": "Week",
    "own.schedule.month": "Month",
    "own.schedule.agenda": "Agenda",
    "own.schedule.swipeHint": "← Swipe to switch day · Pinch to zoom",
    "own.schedule.noStaff": "All staff hidden",
    "own.schedule.unassigned": "Unassigned",
    "own.schedule.prevWeek": "Previous week",
    "own.schedule.nextWeek": "Next week",
    "own.schedule.allEmpty": "All slots are full on this date",
    "own.schedule.conflict": "⚠ Conflict",
    "own.schedule.confirmReschedule": "Confirm reschedule?",
    "own.schedule.rescheduleSuccess": "Rescheduled ✓",
    "own.schedule.rescheduleFailed": "Reschedule failed",
    "own.schedule.bookSuccess": "Booking added ✓",
    "own.cal.todayMarker": "Today",
    "own.cal.hr": ":00",
    "own.cal.legend.pending": "Pending",
    "own.cal.legend.checkedin": "Checked in",
    "own.cal.legend.grooming": "Grooming",
    "own.cal.legend.done": "Done",
    "own.cal.legend.event": "Event",
    "own.cal.collected": "Collected",
    "own.cal.unpaid": "Unpaid",
    "own.cal.partial": "Partial",
    "own.cal.paid": "Paid",
    "own.cal.package": "Package",
    "own.walkin.title": "Walk-in",
    "own.walkin.selectCustomer": "Customer",
    "own.walkin.newCustomer": "New customer",
    "own.walkin.selectPet": "Pet",
    "own.walkin.newPet": "Add pet",
    "own.walkin.selectService": "Service",
    "own.walkin.selectStaff": "Groomer",
    "own.walkin.startTime": "Start time",
    "own.walkin.addons": "Add-ons (optional)",
    "own.walkin.spa": "SPA (optional)",
    "own.walkin.customDuration": "Custom duration",
    "own.walkin.discount": "Discount",
    "own.walkin.discountReason": "Discount reason",
    "own.walkin.staffRemark": "Staff note",
    "own.walkin.useWallet": "Deduct from wallet",
    "own.walkin.usePackage": "Use package",
    "own.walkin.startGrooming": "Start grooming",
    "own.walkin.savedAsPending": "Saved — pending",
    "own.walkin.savedAsCheckedin": "Saved — checked in",
    "own.walkin.savedAsGrooming": "Saved — grooming",
    "own.walkin.amountTotal": "Total",
    "own.walkin.allergyWarn": "⚠️ Allergy: ",
    "own.walkin.confirmAdd": "Add this walk-in booking?",
    "own.walkin.confirmAddBtn": "Add booking",
    "own.walkin.cancelBtn": "Cancel",
    "own.walkin.searchCustPh": "Search by name or phone",
    "own.walkin.noPet": "No pet on record — add one inline",
    "own.walkin.timeConflict": "This groomer is already booked at this time",
    "own.editApt.title": "Booking details",
    "own.editApt.editMode": "Edit",
    "own.editApt.saveEdit": "Save",
    "own.editApt.cancelEdit": "Discard",
    "own.editApt.unsavedConfirm": "Unsaved changes — leave anyway?",
    "own.editApt.label": "Label",
    "own.editApt.noLabel": "(no label)",
    "own.editApt.customLabelPrompt": "Enter custom label",
    "own.editApt.markAs": "Mark as ",
    "own.editApt.andNotify": " and notify customer",
    "own.editApt.pastLocked": "Past booking · status locked",
    "own.editApt.pastLockedToast": "Past booking — status can't be changed",
    "own.editApt.statusUpdated": "Status updated",
    "own.editApt.report": "Grooming report",
    "own.editApt.skin": "Skin",
    "own.editApt.ear": "Ear",
    "own.editApt.teeth": "Teeth",
    "own.editApt.mood": "Mood",
    "own.editApt.nails": "Nails",
    "own.editApt.analGlands": "Anal glands",
    "own.editApt.parasites": "Parasites",
    "own.editApt.eyeDischarge": "Eye discharge",
    "own.editApt.matting": "Matting",
    "own.editApt.notes": "Groomer note",
    "own.editApt.photoUpload": "Upload photo",
    "own.editApt.report.save": "Save report",
    "own.editApt.report.send": "Save & notify customer",
    "own.editApt.checkin": "Check in",
    "own.editApt.startGroom": "Start grooming",
    "own.editApt.markDone": "Mark done",
    "own.editApt.markNoShow": "No-show",
    "own.editApt.cancelBooking": "Cancel booking",
    // ── Iteration 4 ──
    "own.crm.title": "Customers",
    "own.crm.search": "Search customers…",
    "own.crm.empty": "No customers yet",
    "own.crm.emptySub": "Customers appear here after their first booking.",
    "own.crm.tab.info": "Profile",
    "own.crm.tab.records": "Bookings",
    "own.crm.tab.wallet": "Wallet",
    "own.crm.tab.ledger": "💰 Ledger",
    "own.crm.totalSpent": "Lifetime spend",
    "own.crm.groomCount": "Visits",
    "own.crm.contact": "📞 Contact",
    "own.crm.visitor": "Guest account (no email login)",
    "own.crm.editProfile": "Edit profile",
    "own.crm.blockBtn": "🚫 Block customer",
    "own.crm.unblockBtn": "🚫 Unblock",
    "own.crm.blocked": "🚫 Blocked",
    "own.crm.blockReason": "Reason: ",
    "own.crm.confirmBlock": "Block this customer?\nThey won't be able to book at this shop again. Other shops are unaffected.\n\nReason (optional):",
    "own.crm.confirmUnblock": "Unblock this customer?",
    "own.crm.blockedToast": "Customer blocked 🚫",
    "own.crm.unblockedToast": "Customer unblocked ✓",
    "own.crm.disableBtn": "Disable",
    "own.crm.deleteBtn": "Delete permanently",
    "own.crm.confirmDisable": "Disable customer \"{name}\"?\nBooking history is kept, but they can't sign in or book (you can re-enable any time).",
    "own.crm.disabledToast": "Customer disabled",
    "own.crm.addWallet": "Top up wallet",
    "own.crm.sellPackage": "📦 Sell / gift package",
    "own.crm.managePackage": "📋 Manage packages",
    "own.crm.noWalletTx": "No transactions yet",
    "own.crm.noWalletTxSub": "No wallet activity on record",
    "own.crm.walletDeducted": "Wallet used",
    "own.crm.pointsDeducted": "Points used",
    "own.crm.vouchersUsed": "Vouchers used",
    "own.crm.ledger.empty": "No ledger entries",
    "own.crm.ledger.emptySub": "Customer hasn't used wallet, points, or vouchers yet.",
    "own.topup.title": "Top up wallet",
    "own.topup.amount": "Amount",
    "own.topup.note": "Note",
    "own.topup.method": "Payment method",
    "own.topup.save": "Confirm top-up",
    "own.topup.savedToast": "Topped up ${amount} ✓",
    "own.topup.deductBtn": "Deduct wallet",
    "own.topup.deductConfirm": "Deduct from this customer's wallet?",
    "own.topup.deductReason": "Reason for deduction",
    "own.pkg.activeFilter": "Active",
    "own.pkg.expiredFilter": "Expired",
    "own.pkg.spentFilter": "Used up",
    "own.pkg.refundedFilter": "Refunded",
    "own.pkg.allFilter": "All",
    "own.pkg.empty": "No packages yet",
    "own.pkg.remaining": "",
    "own.pkg.visits": " visits left",
    "own.pkg.expires": "Expires ",
    "own.pkg.expired": "Expired",
    "own.pkg.refunded": "Refunded",
    "own.pkg.gift": "🎁 Free gift",
    "own.pkg.usageLog": "📜 History",
    "own.pkg.extend30": "📅 +30 days",
    "own.pkg.extendCustom": "📅 Custom extend",
    "own.pkg.restore1": "↩️ Restore 1",
    "own.pkg.deduct1": "➖ Deduct 1",
    "own.pkg.refundBtn": "💸 Refund",
    "own.pkg.confirmRestore": "Restore 1 visit? (Use to undo a wrong deduction.)",
    "own.pkg.confirmDeduct": "Deduct another visit? (For when one session covers multiple package services.)",
    "own.pkg.restoredToast": "Visit restored ✓",
    "own.pkg.deductedToast": "Visit deducted ✓",
    "own.pkg.extendedToast": "Extended by {days} days ✓",
    "own.pkg.extendPrompt": "How many days to extend?",
    "own.pkg.refundPrompt": "Refund amount?",
    "own.pkg.refundReason": "Refund reason",
    "own.pkg.refundedToast": "Refunded ${amount} ✓",
    "own.sell.title": "Sell / gift package",
    "own.sell.intro": "Add a package for {name}. They'll see remaining visits and expiry in the app.",
    "own.sell.selectPackage": "Choose package",
    "own.sell.selectPet": "Choose pet",
    "own.sell.totalVisits": "Total visits",
    "own.sell.expires": "Valid for",
    "own.sell.paymentMethod": "Payment method",
    "own.sell.amountPaid": "Amount collected",
    "own.sell.giftToggle": "Gift (no payment)",
    "own.sell.submitBtn": "Confirm",
    "own.sell.successPkg": "Package issued ✓",
    "own.sell.successPaid": "Collected ${amount} ✓",
    "own.voucher.title": "Vouchers",
    "own.voucher.empty": "No vouchers yet",
    "own.voucher.unused": "Unused",
    "own.voucher.used": "Used",
    "own.voucher.usedOn": "Used on ",
    // ── Iteration 5 — Settings ──
    "own.set.policy.wallet": "Enable wallet",
    "own.set.policy.walletSub": "When off, the customer app hides the Wallet tab and top-up button. Existing balances stay on record.",
    "own.set.policy.loyalty": "Enable loyalty points",
    "own.set.policy.loyaltySub": "When off, customers won't see points and bookings won't earn any.",
    "own.set.policy.earnRule": "Spend $X to earn 1 point",
    "own.set.policy.redeemRule": "1 point = $Y cash",
    "own.set.policy.example": "💡 Example: spend $100 → earn {n} points → redeem ${cash} cash",
    "own.set.policy.review": "Enable customer ratings",
    "own.set.policy.reviewSub": "When off, customers won't be asked to rate, and the Reviews tab is hidden.",
    "own.set.policy.healthReport": "Enable post-grooming health report",
    "own.set.policy.healthReportSub": "When on, tapping \"Done\" opens a health-report form.",
    "own.set.policy.maxWeight": "⚖️ Maximum accepted weight (kg)",
    "own.set.policy.maxWeightSub": "Pets above this weight can't book. Blank / 0 = no limit.",
    "own.set.policy.brandRemove": "🏷️ Remove PettoBook branding",
    "own.set.policy.brandRemoveSub": "When on, the customer booking page footer, before/after share image, and customer app no longer show \"Powered by PettoBook\".",
    "own.set.policy.brandRemoveProBadge": "👑 Requires Pro plan",
    "own.set.brand.title": "Shop theme 🎨",
    "own.set.brand.logo": "Logo",
    "own.set.brand.banner": "Banner",
    "own.set.brand.tagline": "Tagline",
    "own.set.brand.about": "About",
    "own.set.brand.website": "Website",
    "own.set.brand.instagram": "Instagram",
    "own.set.brand.facebook": "Facebook",
    "own.set.brand.gallery": "Photo gallery",
    "own.set.hours.businessHours": "Business hours",
    "own.set.hours.closed": "Closed",
    "own.set.hours.allDay": "All day",
    "own.set.hours.holidays": "Holidays 🏖️",
    "own.set.hours.holidaysSub": "Public holidays are shown on the calendar for reference but don't close the shop automatically.",
    "own.set.hours.addHoliday": "Add holiday",
    "own.set.hours.removeHoliday": "Remove",
    "own.set.msg.autoCommTitle": "💬 Auto-messaging",
    "own.set.msg.autoCommSub": "Booking confirmation, reschedule, and completion notifications are sent automatically via PettoBook.",
    "own.set.msg.channelEmail": "📧 Email",
    "own.set.msg.channelSms": "📱 SMS",
    "own.set.msg.emailDesc": "Recommended: receipts, reminders, reports (always free)",
    "own.set.msg.smsDesc": "Reaches older customers too; about $0.05 per message",
    "own.set.msg.smsPaywall": "🔒 Requires Basic or Pro plan",
    "own.set.msg.upgradeRequired": "Paid plan required — upgrade first",
    "own.set.msg.advanced": "🛠️ Advanced (BYO API)",
    "own.set.msg.advancedHint": "Email and SMS are sent through PettoBook — no setup needed. Below is your own WhatsApp Business number.",
    "own.set.msg.waBYOK": "💬 WhatsApp Business (BYOK)",
    "own.set.msg.waPhoneId": "WhatsApp Phone Number ID",
    "own.set.msg.waToken": "Access Token",
    "own.set.msg.waFromName": "Display name (optional)",
    "own.set.msg.waTemplate": "Default template name (optional)",
    "own.set.msg.saveBtn": "Save",
    "own.set.msg.sendTestBtn": "📲 Send test to self",
    "own.set.msg.savedToast": "Messaging settings saved",
    "own.set.msg.saveFailed": "Save failed",
    "own.set.msg.testSent": "✅ Test message sent",
    "own.set.msg.testFailed": "Send failed",
    "own.set.msg.perTriggerCard": "⚡ Per-event settings",
    "own.set.msg.perTriggerSub": "Pick channel and toggle each event (booking confirmation, reschedule, reminder…)",
    "own.set.msg.defaultChannel": "Default channel (all notifications)",
    "own.set.msg.perEvent": "Per event",
    "own.set.msg.perEventHint": "Toggle on the left: on/off; right: channel (blank = use default)",
    "own.set.msg.trig.booking": "📅 Booking confirmation",
    "own.set.msg.trig.reschedule": "🔁 Reschedule",
    "own.set.msg.trig.cancel": "❌ Cancellation",
    "own.set.msg.trig.dayBefore": "⏰ Day-before reminder",
    "own.set.msg.trig.groomingDone": "🎉 Grooming done",
    "own.set.msg.trig.useDefault": "(default)",
    "own.set.msg.trig.both": "📧 + 📱 Both",
    "own.set.tpl.title": "📝 Message templates",
    "own.set.tpl.intro": "Customize each event's auto-message. Leave blank to use the default.",
    "own.set.tpl.dynamicFields": "Available variables:",
    "own.set.tpl.customized": "Customized",
    "own.set.tpl.restoreDefault": "Restore default",
    "own.set.tpl.save": "Save",
    "own.set.tpl.savedToast": "Saved ✓",
    "own.set.tpl.emailSubject": "📧 Email subject",
    "own.set.tpl.messageBody": "💬 Message body",
    // ── Iteration 6 — Cross-cutting ──
    "tx.saved": "Saved ✓",
    "tx.saveFailed": "Save failed",
    "tx.deleted": "Deleted",
    "tx.deleteFailed": "Delete failed",
    "tx.copied": "Copied",
    "tx.uploadOk": "Uploaded",
    "tx.uploadFailed": "Upload failed",
    "tx.networkError": "Network error — try again",
    "tx.actionFailed": "Action failed",
    "tx.confirm": "Confirm",
    "tx.cancel": "Cancel",
    "tx.yes": "Yes",
    "tx.no": "No",
    "tx.permissionDenied": "Permission denied",
    "tx.required": "This field is required",
    "tx.invalidFormat": "Invalid format",
    "tx.emailInvalid": "Invalid email format",
    "tx.phoneInvalid": "Invalid phone format",
    "tx.passwordTooShort": "Password too short",
    "tx.csvNoData": "CSV is empty — add at least one row",
    "tx.csvParseFailed": "CSV parse failed",
    "tx.confirmDelete": "Delete?",
    "tx.confirmCancel": "Cancel?",
    "tx.unsavedChanges": "Unsaved changes — leave anyway?",
    "tx.staffSaved": "Staff updated ✓",
    "tx.shopDeleted": "Shop deleted",
    "tx.enableFailed": "Enable failed — try again",
    "tx.qrUploaded": "QR uploaded",
    "tx.emailSettingsSaved": "Email settings saved",
    "tx.smsSettingsSaved": "SMS settings saved",
    "tx.waSettingsSaved": "WhatsApp settings saved",
    "tx.testEmailSent": "✅ Test email sent",
    "tx.testSmsSent": "✅ Test SMS sent",
    "tx.testMsgSent": "✅ Test message sent",
    "tx.shareFailed": "Share failed",
    "tx.linkedSuccess": "✅ Linked successfully",
    "tx.disconnectFailed": "Disconnect failed",
    "tx.delPayoutConfirm": "Delete this payout record?",
    "tx.delDeductConfirm": "Delete this adjustment?",
    "tx.delEventTplConfirm": "Delete this event template?",
    "tx.delEventConfirm": "Delete this event?",
    "tx.delPayMethodConfirm": "Delete this payment method?",
    "tx.deleted": "Deleted",
    "tx.gcalDisconnectConfirm": "Disconnect Google Calendar? Existing synced events will not be deleted.",
    "tx.gcalDisconnectedToast": "Google Calendar disconnected",
    "tx.icalRevokeConfirm": "Revoking will invalidate the existing subscription link immediately. Continue?",
    "tx.icalGenToast": "Calendar subscription link generated ✅",
    "tx.icalRevokedToast": "Subscription link revoked",
    "tx.icalGenFailed": "Generate link failed",
    "tx.icalRevokeFailed": "Revoke failed",
    "tx.googleAuthFailed": "Google authorisation failed",
    "tx.payoutToast": "Payout ${amount} recorded ✓",
    "tx.payoutFailed": "Failed",
    "tx.gcalTitle": "🔗 Google Calendar live sync",
    "tx.gcalSub": "After authorising, PettoBook bookings are written into your Google Calendar in real time. Reschedules / cancellations update within 15 seconds (iCal subscriptions rely on Google's 4–24 hour poll).",
    "tx.gcalConnected": "✓ Connected",
    "tx.gcalDisconnect": "Disconnect",
    "tx.gcalConnect": "Connect Google Calendar →",
    "tx.staffLeaveLabelSick": "🤒 Sick leave",
    "tx.staffLeaveLabelPersonal": "📌 Personal leave",
    "tx.staffFallback": "Staff",
    "tx.custFallback": "Customer",
    "tx.unassigned": "Unassigned",
    "tx.staffOnLeaveConfirm": "Note: {name} is marked {label} today.\n\nStill create the booking?",
    "tx.delSvcAffectsApts": "Deleting \"{name}\" will affect {n} future bookings. Continue?",
    "tx.delSvcConfirm": "Delete \"{name}\"?",
    "tx.svcFallback": "this service",
    "tx.rescheduleConfirm": "Confirm reschedule {pet} ({cust})?\n\nFrom: {fromDate} {fromTime} · {fromStaff}\nTo: {toDate} {toTime} · {toStaff}",
    "tx.enableStaffConfirm": "Enable {name}? Staff will reappear in calendar, assignment and payroll.",
    "tx.disableStaffConfirm": "Disable {name}? Disabled staff stop receiving new bookings; history is kept.",
    "tx.updateFailed": "Update failed",
    "tx.pkgRestore1Confirm": "Restore 1 visit?",
    "tx.pkgDeduct1Confirm": "Deduct 1 more visit? (e.g. an extra add-on within the same service)",
    "tx.pkgExtend30Toast": "Extended 30 days ✓",
    "tx.pkgExtend30Btn": "+30 days",
    "tx.pkgRestoredToast": "Restored ✓",
    "tx.pkgDeductedToast": "1 visit deducted ✓",
    "tx.pkgRestoreBtn": "↩️ Restore",
    "tx.pkgDeductBtn": "➖ Deduct 1",
    "tx.pkgRestoreReason": "Manual restore (1 visit)",
    "tx.pkgDeductReason": "Manual deduct (1 visit)",
    "tx.opFailed": "Failed",
    "fpc.title": "Please set your password",
    "fpc.sub": "The shop created an account for you. Set a password only you know to protect your data.",
    "fpc.newPw": "New password (at least 6 chars)",
    "fpc.confirmPw": "Confirm new password",
    "fpc.pwTooShort": "Password must be at least 6 characters",
    "fpc.pwMismatch": "Passwords do not match",
    "fpc.updatedToast": "Password updated ✅",
    "fpc.updateFailed": "Update password failed",
    "fpc.saving": "Saving...",
    "fpc.confirmBtn": "Confirm and sign in",
    "fpc.later": "Later",
    "undo.btn": "Undo",
    "tx.popupBlocked": "Your browser blocked the pop-up. Please allow pop-ups and try again.",
    "settings.title": "Settings",
    "settings.group.shop": "Shop",
    "settings.group.ops": "Operations",
    "settings.group.reports": "Reports",
    "settings.group.stamp": "Stamp card",
    "settings.group.customerAssets": "Customer assets",
    "settings.packagesAdmin": "📦 Packages",
    "settings.walletsAdmin": "💳 Wallets",
    "settings.pointsAdmin": "⭐ Points",
    "settings.group.advanced": "Advanced",
    "settings.analytics": "Analytics",
    "settings.reviews": "Reviews",
    "settings.pointsSetup": "Stamp card setup",
    "settings.activity": "Activity log",
    "settings.support": "💬 Customer support",
    "settings.systemBackup": "System & backup",
    "settings.events": "📌 Event manager",
    "set.basic.langMovedHint": "💡 Interface language (中文 / English) lives in Settings → System & backup → 🌐 — toggle it there for instant effect. Currency saves instantly and updates every $ amount.",
    "tx.googleAuthFailed": "Google sign-in failed",
    "tx.eventDeleted": "Event deleted",
    "tx.eventUpdated": "Event updated 📌",
    "tx.eventSaveFailed": "Failed to save event",
    "tx.noteSaved": "Note saved",
    "tx.signed": "Waiver signed",
    "tx.announcementPublished": "Announcement published",
    "tx.refundFailed": "Refund failed",
    "tx.cancelFailed": "Cancel failed",
    "tx.refundCancelFailed": "Cancel-payment failed",
    "tx.refunded": "Refunded",
    "tx.topupFailed": "Top-up failed",
    "tx.savePhoneFailed": "Failed to save phone",
    "tx.uploadFailedToast": "Upload failed",
    "tx.walletDeductFailed": "Wallet deduction couldn't write to DB — adjust manually",
    "tx.packageDelisted": "Package is no longer available — please refresh",
    "tx.packageCashOnly": "Cash / percent vouchers can't be used with a package",
    "tx.confirmRemoveStaff": "Remove this staff member?\n• Their schedules and bookings stay on record\n• Future bookings need re-assigning\n• Their login will be disabled",
    "tx.confirmDeleteShop": "Permanently delete this shop? All customers, bookings, staff, and package records are gone forever.",
    "tx.confirmCancelBooking": "Cancel this booking?\n\n• Customer is notified\n• Package visits returned\n• Stamps / points revoked",
    // ── Iteration 7 ──
    "ea.title": "Booking details",
    "ea.editMode": "Edit",
    "ea.editSave": "Done editing",
    "ea.label": "Label",
    "ea.noLabel": "(no label)",
    "ea.staffNote": "Staff note",
    "ea.editStartTime": "Edit start time",
    "ea.bookingId": "Booking ID",
    "ea.deposit": "Deposit collected",
    "ea.depositPending": "⏳ Deposit pending",
    "ea.depositConfirm": "✓ Confirm deposit",
    "ea.depositUncancel": "Undo",
    "ea.depositCompose": "Breakdown: ",
    "ea.depositPaid": "✓ Deposit received",
    "ea.cash": "Cash",
    "ea.walletShort": "Wallet",
    "ea.pointsShort": "Points",
    "ea.voucher": "Voucher",
    "ea.totalCharge": "Total",
    "ea.originalPrice": "List price",
    "ea.actualReceive": "Collected",
    "ea.cashOwed": "Cash owed",
    "ea.discount": "Discount",
    "ea.discountAmount": "Discount amount",
    "ea.discountReason": "Discount reason",
    "ea.addDiscount": "Add discount",
    "ea.removeDiscount": "Remove discount",
    "ea.discountPercent": "Percent",
    "ea.discountFixed": "Fixed",
    "ea.addSurcharge": "Add surcharge",
    "ea.surchargeName": "Item",
    "ea.voucherUsed": "🎟️ Voucher discount",
    "ea.payButton": "Collect ",
    "ea.payDoneBtn": "✓ Paid in full · locked",
    "ea.refundReopen": "↩️ Refund / re-open",
    "ea.refundConfirm": "⚠️ This booking is fully paid. Refund and re-open?",
    "ea.refundedToast": "Refunded ${amount} · payment re-opened",
    "ea.payLockedToast": "Past booking · status locked",
    "ea.paidFullConfirm": "⚠️ This booking is fully paid.\n\nTap OK to record a refund and re-open as Unpaid.\nIf you don't intend to refund, tap Cancel.",
    "ea.cancelPayConfirm": "Cancel payment?\nWill record ${net} refund — revenue won't be double-counted.",
    "ea.cancelPayMarkToast": "Payment mark cleared",
    "ea.cancelDepositConfirm": "Cancel deposit-received status?",
    "ea.noshowConfirm": "Mark as No-show?\n\n{pet} · {date} {time}\n\n• Package count auto-refunded\n• Stamp / points auto-revoked\n• Deposit handled per shop policy",
    "ea.cancelAptPromptTitle": "Reason for cancelling (customer will be notified):",
    "ea.cancelAptPromptDefault": "Shop cancelled",
    "ea.cancelAptConfirm": "Confirm cancel booking?\n\n{pet} · {date} {time}\n\n• Customer will be notified\n• Package count refunded\n• Stamp / points revoked\n• {feeLine}",
    "ea.cancelAptFeeLine": "Charge ${fee} fee",
    "ea.cancelAptFullRefund": "Full refund",
    "ea.discardEditsBack": "Discard edits and go back?",
    "ea.backToDetail": "← Back to detail",
    "ea.unsavedBanner": "Unsaved changes — tap Save below.",
    "ea.markNoShowBtn": "⚠️ Mark no-show",
    "ea.cancelAptBtn": "❌ Cancel booking",
    "ea.statusBar": "Status",
    "ea.statusPending": "Pending",
    "ea.statusCheckedin": "Checked in",
    "ea.statusGrooming": "Grooming",
    "ea.statusDone": "Done",
    "ea.statusCancelled": "Cancelled",
    "ea.statusNoShow": "No-show",
    "ea.confirmStatus": "Mark as ",
    "ea.statusNotify": " and notify customer",
    "ea.report.title": "Grooming report",
    "ea.report.skin": "Skin",
    "ea.report.ear": "Ear",
    "ea.report.teeth": "Teeth",
    "ea.report.mood": "Mood",
    "ea.report.nails": "Nails",
    "ea.report.analGlands": "Anal glands",
    "ea.report.parasites": "Parasites",
    "ea.report.eyeDischarge": "Eye discharge",
    "ea.report.matting": "Matting",
    "ea.report.notes": "Groomer note",
    "ea.report.photos": "Photos",
    "ea.report.normal": "Normal",
    "ea.report.attention": "Watch",
    "ea.report.abnormal": "Abnormal",
    "ea.report.none": "None",
    "ea.report.mild": "Mild",
    "ea.report.severe": "Severe",
    "ea.report.uploadBtn": "Upload photo",
    "ea.report.saveBtn": "Save report",
    "ea.report.sendBtn": "Save & notify customer",
    "ea.report.savedToast": "Report saved",
    "ea.unlock.locked": "Past booking · status locked",
    "ea.unlock.unlocked": "Temporarily unlocked",
    "ea.unlock.lockedSub": "Locked to prevent accidental edits. Unlock to add a late payment or change status.",
    "ea.unlock.unlockedSub": "Will re-lock when the sheet closes",
    "ea.unlock.btn": "🔓 Unlock",
    "ea.unlock.relock": "Re-lock",
    "ea.unlock.unlockedToast": "Past booking unlocked · editable 🔓",
    "ea.unlock.relockedToast": "Re-locked 🔒",
    "ea.cancelBooking": "❌ Cancel booking",
    "ea.cancelReason": "Cancellation reason",
    "ea.cancelReasonPh": "Why?",
    "ea.cancelOther": "Other",
    "ea.cancelSubmit": "Confirm cancel",
    "ea.lateFee": "Late fee",
    "ea.refunded": "Refunded",
    "ea.checkinTime": "Check-in time",
    "ea.startTime": "Start time",
    "ea.completedTime": "Completed time",
    "wi.title": "Walk-in",
    "wi.searchCustPh": "Search name or phone",
    "wi.newCustomer": "New customer",
    "wi.newCustName": "Customer name",
    "wi.newCustPhone": "Phone",
    "wi.newPet": "Add pet",
    "wi.newPetName": "Pet name",
    "wi.newPetBreed": "Breed",
    "wi.newPetWeight": "Weight (kg)",
    "wi.newPetType": "Type",
    "wi.newPetCoat": "Coat length",
    "wi.allergies": "Allergies",
    "wi.allergiesPh": "None / e.g. shampoo allergy",
    "wi.service": "Service",
    "wi.servicePicked": "Selected service",
    "wi.staff": "Groomer",
    "wi.time": "Time",
    "wi.duration": "Duration (min)",
    "wi.customDuration": "Custom duration",
    "wi.addons": "Add-ons",
    "wi.spaServices": "SPA services",
    "wi.noAddons": "No add-ons",
    "wi.discount": "Discount",
    "wi.discountAmt": "Discount amount",
    "wi.discountType": "Discount type",
    "wi.discountReason": "Discount reason",
    "wi.discountReasonPh": "e.g. VIP / family / promo",
    "wi.staffRemark": "Staff note",
    "wi.staffRemarkPh": "Special note for staff",
    "wi.useWallet": "Deduct from wallet",
    "wi.usePackage": "Use package",
    "wi.total": "Total",
    "wi.startNowBtn": "Start grooming now",
    "wi.checkedInBtn": "Check in only",
    "wi.savedToast": "Walk-in saved",
    "wi.timeConflict": "Groomer is already booked at this time",
    "wi.serviceRequired": "Choose a service",
    "wi.petRequired": "Choose a pet",
    "wi.customerRequired": "Choose a customer",
    "wi.discountTooLarge": "Discount can't exceed the price",
    "wi.noStaff": "No groomers available",
    // ── Iteration 8 ──
    "st.title": "Staff",
    "st.addStaff": "Add staff",
    "st.searchPh": "Search staff…",
    "st.empty": "No staff yet",
    "st.emptySub": "Tap \"Add staff\" to add the first.",
    "st.editTitle": "Staff profile",
    "st.editNewTitle": "Add staff",
    "st.tabBasic": "Basics",
    "st.tabService": "Services",
    "st.tabTime": "Hours",
    "st.tabLeave": "Time off",
    "st.tabPermissions": "Permissions",
    "st.name": "Name",
    "st.phone": "Phone",
    "st.email": "Email",
    "st.password": "Password",
    "st.role": "Role",
    "st.roleOwner": "Owner",
    "st.roleAdmin": "Admin",
    "st.roleStaff": "Staff",
    "st.jobType": "Employment",
    "st.fullTime": "Full-time",
    "st.partTime": "Part-time",
    "st.settleMode": "Settle mode",
    "st.settleDaily": "Daily",
    "st.settleMonthly": "Monthly",
    "st.mpfEnabled": "Enable MPF",
    "st.joinDate": "Start date",
    "st.leaveDate": "End date",
    "st.baseSalary": "Base salary",
    "st.hourlyRate": "Hourly rate",
    "st.commissionMode": "Commission type",
    "st.commissionPercent": "Percent",
    "st.commissionFixed": "Fixed",
    "st.commissionRate": "Commission rate (%)",
    "st.commissionEnabled": "Enable commission",
    "st.commissionTiers": "Tiered commission",
    "st.commissionTiersHint": "Revenue above [from] is paid at [%]",
    "st.addTier": "+ Add tier",
    "st.tierFrom": "From",
    "st.tierRate": "%",
    "st.serviceCommission": "Per-service commission",
    "st.serviceCommissionHint": "Override the default commission rate per service.",
    "st.packageCommission": "Package sales commission (%)",
    "st.packageCommissionHint": "% of package sale price per package sold by this staff.",
    "st.packageRedemptionCommission": "Package grooming commission (%)",
    "st.packageRedemptionCommissionHint": "% of recognised revenue per package visit.",
    "st.canService": "Services offered",
    "st.canServiceHint": "Unchecked services don't show this staff on the customer booking page.",
    "st.weeklySchedule": "Weekly schedule",
    "st.workDay": "Working",
    "st.offDay": "Off",
    "st.dayHours": "Working hours",
    "st.startTime": "Start",
    "st.endTime": "End",
    "st.timeOff": "Time off",
    "st.addTimeOff": "+ Add time off",
    "st.timeOffType": "Type",
    "st.timeOffSick": "Sick",
    "st.timeOffPersonal": "Personal",
    "st.timeOffAnnual": "Annual leave",
    "st.timeOffOther": "Other",
    "st.timeOffStart": "Start date",
    "st.timeOffEnd": "End date",
    "st.timeOffReason": "Reason",
    "st.permissions": "Permissions",
    "st.permViewRevenue": "View revenue",
    "st.permManageCRM": "Manage customers",
    "st.permManageStaff": "Manage staff",
    "st.permManageSettings": "Manage settings",
    "st.permManagePackages": "Manage packages",
    "st.permManageReports": "View reports",
    "st.saveBtn": "Save",
    "st.deleteBtn": "Delete staff",
    "st.savedToast": "Staff saved ✓",
    "st.deleteConfirm": "Remove this staff member?",
    "st.deletedToast": "Staff removed",
    "st.permViewWage": "View wages",
    "st.permPosTab": "POS",
    "st.permRefund": "Refund",
    "st.staff.todayBookings": "Today's bookings",
    "st.staff.workHrs": "Hours worked",
    "st.staff.tipsToday": "Tips today",
    "st.staff.payroll.title": "Payroll",
    "st.staff.payroll.headerBasic": "Base",
    "st.staff.payroll.headerHourly": "Hourly",
    "st.staff.payroll.headerCommission": "Commission",
    "st.staff.payroll.headerTip": "Tip",
    "st.staff.payroll.headerDeduct": "Deduct",
    "st.staff.payroll.headerNet": "Net",
    "st.staff.payroll.recordWage": "Record today's wage",
    "st.staff.payroll.payoutOne": "Pay out (today)",
    "st.staff.payroll.payoutMonth": "Pay out (this month)",
    "st.staff.payroll.payMethod": "Payment method",
    "st.staff.payroll.cash": "Cash",
    "st.staff.payroll.transfer": "Transfer",
    "st.staff.payroll.paidDaily": "✅ Paid daily",
    "st.staff.payroll.paidMonthly": "Paid monthly",
    "st.staff.payroll.unpaid": "⏳ Unpaid",
    "st.staff.payroll.unpaidMonthly": "Unpaid (settles at month end)",
    "st.staff.payroll.empty": "No payroll record for this day",
    "st.staff.payroll.totalToday": "Today total",
    "st.staff.payroll.totalMonth": "Month total",
    "st.staff.payroll.from": "From",
    "st.staff.payroll.to": "To",
    "st.staff.payroll.dateRange": "Date range",
    "st.staff.payroll.saveDayBtn": "✓ Record today's wage",
    "st.staff.payroll.savedToast": "Wage recorded ✓",
    "st.staff.payroll.payoutToast": "Payout done ✓",
    "st.staff.payroll.confirmPayout": "Confirm payout?",
    "st.staff.payroll.bonus": "Bonus",
    "st.staff.payroll.deduction": "Deduction",
    "st.staff.payroll.addBonusBtn": "+ Bonus / Deduction",
    "st.staff.payroll.adjKind.bonus": "Bonus",
    "st.staff.payroll.adjKind.late": "Late",
    "st.staff.payroll.adjKind.accident": "Accident",
    "st.staff.payroll.adjKind.uniform": "Uniform",
    "st.staff.payroll.adjKind.advance": "Advance",
    "st.staff.payroll.adjKind.fine": "Fine",
    "st.staff.payroll.adjKind.other": "Other",
  },
  // Japanese — covers the common UI + customer-facing booking flow. Any key not
  // present here falls back to English (see t()), so the app stays coherent for
  // Japanese users while the dictionary is expanded over time.
  "ja": {
    "lang.name": "日本語",
    "common.cancel": "キャンセル",
    "common.confirm": "確認",
    "common.save": "保存",
    "common.delete": "削除",
    "common.edit": "編集",
    "common.back": "戻る",
    "common.next": "次へ",
    "common.loading": "読み込み中…",
    "common.error": "エラー",
    "common.retry": "再試行",
    "common.close": "閉じる",
    "common.search": "検索",
    "common.add": "追加",
    "common.copy": "コピー",
    "common.copied": "コピーしました",
    "common.logout": "ログアウト",
    "common.settings": "設定",
    "common.today": "今日",
    "auth.login": "ログイン",
    "auth.register": "新規登録",
    "auth.email": "メール",
    "auth.password": "パスワード",
    "auth.phone": "電話番号",
    "auth.continue": "続ける",
    "auth.googleSignin": "Googleで続ける",
    "tab.home": "ホーム",
    "tab.book": "予約",
    "tab.pets": "ペット",
    "tab.history": "履歴",
    "tab.profile": "プロフィール",
    "booking.selectPet": "ペットを選択",
    "booking.selectService": "サービスを選択",
    "booking.selectStaff": "トリマーを選択",
    "booking.selectTime": "時間を選択",
    "booking.confirm": "予約を確定",
    "booking.success": "予約が確定しました",
    "booking.total": "合計",
    "booking.deposit": "予約金",
    "status.pending": "受付待ち",
    "status.checkedin": "チェックイン済み",
    "status.grooming": "トリミング中",
    "status.done": "完了",
    "status.cancelled": "キャンセル済み",
    "status.noshow": "無断キャンセル",
    "home.wallet": "ウォレット",
    "home.points": "ポイント",
    "home.grooms": "トリミング回数",
    "home.upcoming": "予定",
    "home.history": "来店履歴",
    "home.noUpcoming": "予定の予約はありません",
    "home.bookNow": "今すぐ予約",
    "home.times": "",
    "book.pickPet": "ペットを選択",
    "book.pickService": "サービスを選択",
    "book.pickStaff": "トリマーを選択",
    "book.anyStaff": "指名なし",
    "book.pickTime": "時間を選択",
    "book.summary": "確認",
    "book.subtotal": "小計",
    "book.totalAmount": "合計",
    "book.usePoints": "ポイントを使う",
    "book.confirmBtn": "予約を確定",
    "book.addPet": "別のペットを追加",
    "profile.editProfile": "プロフィール編集",
    "profile.name": "名前",
    "profile.changeShop": "店舗を切り替え",
    "profile.signOut": "ログアウト",
    "hist.all": "すべて",
    "hist.viewReport": "レポートを見る",
    "hist.cancelBooking": "キャンセル",
    "hist.rate": "評価",
    "settings.title": "設定",
    "settings.language": "言語",
    "settings.shopBasic": "基本情報",
    "settings.shopBrand": "ブランド・テーマ",
    "settings.shopHours": "営業時間",
    "settings.shopPolicy": "ポリシー・機能",
    "settings.payment": "支払い",
    "settings.analytics": "分析",
    "settings.reviews": "レビュー",
    "settings.messaging": "メッセージ",
    "settings.terms": "法的事項・利用規約",
    "settings.activity": "操作履歴",
    "settings.system": "システム・バックアップ",
    "settings.group.shop": "店舗",
    "settings.group.ops": "運営",
    "owner.tab.dashboard": "ダッシュボード",
    "owner.tab.schedule": "スケジュール",
    "owner.tab.crm": "顧客",
    "owner.tab.staff": "スタッフ",
    "owner.tab.payroll": "給与",
    "owner.tab.reports": "レポート",
    "owner.tab.settings": "設定",
    "day.0": "日",
    "day.1": "月",
    "day.2": "火",
    "day.3": "水",
    "day.4": "木",
    "day.5": "金",
    "day.6": "土",
    "owner.appointmentsToday": "本日の予約",
    "owner.revenueToday": "本日の売上",
    "owner.shopOpen": "本日営業",
    "owner.shopClosed": "本日休業",
    "pet.name": "名前",
    "pet.weight": "体重",
    "pet.weightKg": "体重 (kg)",
    "pet.breed": "犬種・猫種",
    "pet.birthday": "誕生日",
    "pet.type": "種類",
    "pet.dog": "🐶 犬",
    "pet.cat": "🐱 猫",
    "pet.coat": "被毛",
    "pet.coatShort": "短毛",
    "pet.coatLong": "長毛",
    "pet.coatMedium": "中毛",
    "pet.allergies": "アレルギー",
    "pet.vaccineDate": "最終ワクチン接種",
    "pet.vetNote": "獣医メモ",
    "pet.add": "ペットを追加",
    "pet.edit": "ペットを編集",
    "summary.mainService": "サービス",
    "summary.addons": "オプション",
    "summary.spa": "スパ",
    "summary.surcharge": "追加料金",
    "summary.discount": "割引",
    "summary.paid": "支払い済み",
    "summary.due": "未払い",
    "summary.depositPaid": "予約金支払い済み",
    "summary.settled": "精算済み ✓",
    "summary.points": "ポイント",
    "summary.pointsEarn": "獲得予定",
    "walkin.title": "当日受付の登録",
    "walkin.newCustomer": "新規顧客",
    "walkin.searchCustomer": "顧客を検索（名前 / 電話番号）",
    "walkin.discount": "割引",
    "walkin.reason": "理由（任意）",
    "walkin.staffRemark": "スタッフメモ",
    "walkin.useWallet": "ウォレット残高を使用",
    "walkin.submit": "登録を確定",
    "walkin.submitStart": "登録してトリミング開始",
    "walkin.submitAndAnother": "保存して同じ顧客の別のペットを予約",
    "toast.saved": "保存しました",
    "toast.deleted": "削除しました",
    "toast.copyOk": "コピーしました",
    "toast.copyFail": "コピーに失敗しました",
    "toast.saveFail": "保存に失敗しました",
    "toast.networkErr": "ネットワークエラーです。もう一度お試しください",
    "report.skin": "皮膚 🩺",
    "report.ear": "耳 👂",
    "report.teeth": "歯 🦷",
    "report.mood": "機嫌 😊",
    "report.nails": "爪 💅",
    "report.analGlands": "肛門腺",
    "report.parasites": "寄生虫 🔍",
    "report.eyeDischarge": "目やに",
    "report.matting": "毛玉 🪮",
    "chip.normal": "正常",
    "chip.good": "良好",
    "chip.none": "なし",
    "chip.notNeeded": "不要",
    "chip.notFound": "見つからず",
    "chip.trimmed": "カット済み",
    "chip.expressed": "絞り出し済み",
    "chip.skin.dry": "乾燥",
    "chip.skin.flakes": "フケ",
    "chip.skin.rash": "発疹",
    "chip.skin.oily": "脂性",
    "chip.skin.inflamed": "炎症",
    "chip.ear.discharge": "分泌物",
    "chip.ear.odor": "におい",
    "chip.ear.red": "赤み・腫れ",
    "chip.ear.mites": "耳ダニの疑い",
    "chip.teeth.mild": "軽度の歯石",
    "chip.teeth.heavy": "重度の歯石",
    "chip.teeth.needsClean": "クリーニングが必要",
    "chip.teeth.missing": "欠損歯",
    "chip.mood.nervous": "緊張",
    "chip.mood.excited": "興奮",
    "chip.mood.scared": "怯え",
    "chip.mood.aggressive": "攻撃的",
    "chip.nails.long": "伸びすぎ",
    "chip.nails.broken": "折れ",
    "chip.ag.inflamed": "炎症",
    "chip.ag.blocked": "詰まり",
    "chip.para.fleas": "ノミ",
    "chip.para.ticks": "マダニ",
    "chip.para.mites": "ダニ",
    "chip.eye.little": "軽度",
    "chip.eye.much": "重度",
    "chip.eye.red": "赤み",
    "chip.eye.abnormal": "異常",
    "chip.mat.mild": "軽度",
    "chip.mat.medium": "中程度",
    "chip.mat.severe": "重度（バリカン必要）",
    "svc.sheet.edit": "サービス — 編集",
    "svc.sheet.add": "サービス — 追加",
    "svc.sheet.spaEdit": "スパ — 編集",
    "svc.sheet.spaAdd": "スパ — 追加",
    "svc.del": "サービスを削除",
    "svc.name": "サービス名",
    "svc.basePrice": "基本料金 ($)",
    "svc.description": "説明",
    "svc.duration": "所要時間（分）",
    "addon.edit": "オプション — 編集",
    "addon.add": "オプション — 追加",
    "addon.name": "オプション名",
    "addon.price": "料金 ($)",
    "addon.del": "オプションを削除",
    "staff.edit": "スタッフ — 編集",
    "staff.add": "スタッフ — 追加",
    "staff.name": "名前",
    "staff.role": "役割",
    "staff.commissionMode": "歩合方式",
    "staff.joinDate": "入社日",
    "staff.leaveDate": "退社日",
    "staff.del": "スタッフを削除",
    "common.required": "必須",
    "ph.searchApt": "予約を検索…",
    "ph.searchCustomer": "顧客名 / 電話番号を検索…",
    "ph.searchNamePhone": "名前または電話番号を検索…",
    "ph.searchGlobal": "顧客・予約・ペットを検索…",
    "ph.searchTenant": "店舗名を検索…",
    "apt.detail": "予約詳細",
    "apt.edit": "予約を編集",
    "apt.surcharge": "追加料金",
    "apt.surchargeAdd": "追加料金を追加",
    "apt.discount": "割引",
    "apt.discountOpt": "割引（任意）",
    "apt.staffNote": "スタッフメモ（予約カードに表示）",
    "apt.del": "この予約を完全に削除",
    "apt.delConfirm": "この予約を完全に削除しますか？取り消せません。記録を残すにはキャンセルをご利用ください。",
    "apt.unsaved": "保存していない変更があります。移動しますか？",
    "book.pickDate": "日付を選択",
    "book.availableSlots": "予約可能な時間",
    "book.noSlotsToday": "本日は満員です — 別の日を選択",
    "book.confirm": "予約を確定",
    "book.depositNow": "今すぐ予約金を支払う",
    "book.usePointsLabel": "ポイントを使う",
    "book.confirmBookingBtn": "今すぐ予約",
    "cust.edit": "顧客を編集",
    "cust.name": "名前",
    "cust.phone": "電話番号",
    "cust.phone2": "予備の電話",
    "cust.email": "メール",
    "cust.address": "住所",
    "cust.tags": "タグ",
    "val.enterName": "名前を入力してください",
    "val.enterEmail": "メールアドレスを入力してください",
    "val.enterPhone": "有効な電話番号を入力してください",
    "val.enterFirstNameBreed": "名前と犬種・猫種を入力してください",
    "val.enterPetName": "ペットの名前を入力してください",
    "val.enterBreed": "犬種・猫種を入力してください",
    "val.enterWeight": "体重を入力してください",
    "val.enterAmount": "有効な金額を入力してください",
    "val.enterNewCustName": "新規顧客の名前を入力してください",
    "val.pickCustomer": "顧客を選択または追加してください",
    "val.fillPetInfo": "ペットの名前・犬種・体重を入力してください",
    "val.pickPet": "ペットを選択または追加してください",
    "val.pickService": "サービスを選択してください",
    "val.pickStaff": "トリマーを選択してください",
    "val.pickTime": "開始時間を選択してください",
    "val.pickDate": "日付を選択してください",
    "val.enterServiceName": "サービス名を入力してください",
    "val.enterAddonName": "オプション名を入力してください",
    "val.enterInvName": "品目名を入力してください",
    "val.enterPromoCode": "プロモコードを入力してください",
    "val.enterPackageName": "名前を入力してください",
    "val.enterPetNameRequired": "ペットの名前を入力してください",
    "val.petBirthdayFuture": "誕生日は今日より後にできません。日付をご確認ください",
    "val.petBirthdayTooOld": "ペットの年齢が高すぎるようです。誕生年をご確認ください",
    "btn.next": "次へ",
    "btn.back": "戻る",
    "btn.bookNow": "今すぐ予約",
    "btn.continueGoogle": "Googleで続行",
    "pay.title": "お会計 💰",
    "pay.notFound": "予約が見つかりません",
    "pay.pet": "ペット",
    "pay.svcDefault": "トリミングサービス",
    "pay.remove": "削除",
    "pay.surchargePh": "追加料金名（例：毛玉、大型犬、鎮静ケア）",
    "pay.addSpaAddon": "+ スパ / オプションを追加",
    "pay.addSurcharge": "+ 追加料金を追加",
    "pay.noAvailable": "追加できる項目はありません",
    "pay.addonLabel": "オプション",
    "pay.subtotal": "小計",
    "pay.discount": "割引",
    "pay.depositPaid": "予約金支払い済み",
    "pay.balanceDue": "残額",
    "pay.cancel": "キャンセル",
    "pay.continue": "続ける →",
    "pay.methodFallback": "支払い方法",
    "pay.cash": "現金 💵",
    "pay.back": "← 戻る",
    "pay.pickMethod": "支払い方法を選択",
    "pay.noMethods": "有効な支払い方法がありません — 現金を使用します。設定 → 支払いで方法を有効にできます。",
    "pay.due": "支払額",
    "pay.failed": "支払いに失敗しました: ",
    "pay.dup": "支払いを記録しました（重複除外）",
    "pay.collected": "受領済み",
    "pay.paidTick": "支払い済み ✓",
    "pay.confirm": "支払いを確定",
    "edit.notSaved": "保存していない変更があります。このまま移動しますか？",
    "edit.lockedPast": "過去の予約 · ステータスはロック済み（レポートの編集・写真のアップロードは可能）",
    "edit.time": "時間",
    "edit.date": "日付",
    "edit.slot": "枠",
    "edit.staff": "トリマー",
    "edit.service": "サービス",
    "edit.changeSvc": "メインサービスを変更",
    "edit.note": "スタッフメモ",
    "edit.del": "この予約を完全に削除",
    "edit.cancelApt": "予約をキャンセル",
    "edit.restoreApt": "予約を復元",
    "edit.history": "履歴",
    "edit.records": "支払い",
    "edit.no記錄": "支払い記録はまだありません",
    "wi.title": "ウォークイン",
    "wi.newCust": "新規のお客様",
    "wi.pickCust": "お客様を選択",
    "wi.pickPet": "ペットを選択",
    "wi.addPet": "ペットを追加",
    "wi.svc": "メインサービス",
    "wi.spa": "スパ",
    "wi.addons": "オプション",
    "wi.package": "パッケージを使用",
    "wi.walletUse": "ウォレットから差し引く",
    "wi.deposit": "予約金",
    "wi.start": "登録してトリミング開始",
    "wi.regOnly": "登録のみ",
    "mk.nav.login": "ログイン",
    "mk.nav.startFree": "無料で始める →",
    "mk.hero.badge": "ペットトリマーのために",
    "mk.hero.titleLine1": "紙とExcelはもう不要。",
    "mk.hero.titleLine2": "サロン運営をアプリ1つで。",
    "mk.hero.subtitle": "セルフ予約 · ドラッグ&ドロップ管理 · WhatsApp自動通知 · スマート料金 · パッケージ&ウォレット · 給与自動計算。",
    "mk.hero.subtitleLine2": "香港・台湾・マカオのペットサロンのために。",
    "mk.hero.ctaPrimary": "無料で始める",
    "mk.hero.ctaSecondary": "アカウントをお持ちですか？ログイン",
    "mk.hero.bullets": "✓ クレジットカード不要 · ✓ 30秒で設定 · ✓ 無料プランずっと",
    "mk.hero.mockToday": "本日の予約",
    "mk.painsHeading.label": "PettoBookに乗り換える理由は？",
    "mk.painsHeading.title": "日々のお悩み — すべて解決済み ✨",
    "mk.painsHeading.sub": "業界のプロと共同設計。6つのよくある悩み → 6つの標準機能で解決。",
    "mk.pain1.tag": "スケジュール",
    "mk.pain1.pain": "ホワイトボード + Excel = ダブルブッキング",
    "mk.pain1.detail": "お客様が直前に予約変更。ノートの古い枠を消しても、他のトリマーには伝わらず、2匹同時に来店してしまう。",
    "mk.pain1.fix": "ドラッグ&ドロップのカレンダー、競合を自動検出",
    "mk.pain1.b1": "予約をドラッグして変更・再割り当て",
    "mk.pain1.b2": "時間の重複を検知して保存をブロック",
    "mk.pain1.b3": "お客様に予約変更のWhatsApp通知を自動送信",
    "mk.pain2.tag": "予約",
    "mk.pain2.pain": "電話とWhatsAppの対応が終わらない",
    "mk.pain2.detail": "毎日1〜2時間を手作業の予約確認に。一度電話を逃せば、お客様は競合店へ。",
    "mk.pain2.fix": "24時間365日のセルフ予約",
    "mk.pain2.b1": "リンク1つをIG / FB / WhatsAppに貼るだけ",
    "mk.pain2.b2": "お客様がサービス・トリマー・時間を選択 — 予約がカレンダーに自動反映",
    "mk.pain2.b3": "新規予約のたびにスマホへ即通知",
    "mk.pain3.tag": "料金",
    "mk.pain3.pain": "料金表 = 新人のミス",
    "mk.pain3.detail": "同じ犬でも3kgと8kgで料金が違う。長毛、毛玉、犬種ごとの追加料金。全部を覚えさせるのはクレームの元。",
    "mk.pain3.fix": "スマート料金マトリクス — 体重を入力すれば料金が表示",
    "mk.pain3.b1": "犬種 / 体重 / 被毛で自動料金",
    "mk.pain3.b2": "スパ / オプション / 追加料金をワンタップ",
    "mk.pain3.b3": "割引やパッケージ利用を自動適用",
    "mk.pain4.tag": "給与",
    "mk.pain4.pain": "給与計算が深夜まで",
    "mk.pain4.detail": "トリマーごとに歩合率が違い、サービス別料金、パッケージ歩合、控除も。月末の給与締めに毎月2〜3日かかる。",
    "mk.pain4.fix": "給与・歩合・シフトを自動計算",
    "mk.pain4.b1": "サービス / パッケージごとに歩合率を設定",
    "mk.pain4.b2": "勤務時間、残業、控除、振込 — すべて1ページに",
    "mk.pain4.b3": "給与明細をいつでも出力してスタッフ確認",
    "mk.pain5.tag": "パッケージ",
    "mk.pain5.pain": "紙のポイントカード = 紛失 = 売上ロス",
    "mk.pain5.detail": "お客様がカードを失くしたと言い、利用回数も覚えていない。記録がなければ結局チャラにするしかない。",
    "mk.pain5.fix": "デジタルパッケージ + ポイント + ウォレット残高",
    "mk.pain5.b1": "利用回数を自動で差し引き、クラウド記録は消えない",
    "mk.pain5.b2": "ポイントを自動付与、割引やギフトに交換可能",
    "mk.pain5.b3": "お客様は自分のアプリでいつでも残高を確認",
    "mk.pain6.tag": "プライバシーとセキュリティ",
    "mk.pain6.pain": "お客様情報がチャットや表計算に散在",
    "mk.pain6.detail": "WhatsApp、メモ、Excel、紙の台帳。スタッフが辞めるとデータも一緒に消え、GDPRにも非対応。",
    "mk.pain6.fix": "クラウドに集約、役割別アクセス、自動バックアップ",
    "mk.pain6.b1": "スタッフごとの役割 + サービス権限",
    "mk.pain6.b2": "毎日の自動バックアップ + 7日間のポイントインタイム復元",
    "mk.pain6.b3": "GDPRのエクスポート / 削除をワンタップ",
    "mk.cmp.label": "比較",
    "mk.cmp.title": "紙や汎用SaaSよりPettoBookを選ぶ理由は？",
    "mk.cmp.colPaper": "📓 紙 / Excel",
    "mk.cmp.colGeneric": "🌐 汎用SaaS",
    "mk.cmp.colUs": "🐾 PettoBook",
    "mk.cmp.r1": "被毛 / 体重によるスマート料金",
    "mk.cmp.r2": "ペットプロフィール + 健康記録",
    "mk.cmp.r3": "パッケージ / ポイント / ウォレット残高",
    "mk.cmp.r4": "給与 + 歩合の自動計算",
    "mk.cmp.r5": "WhatsApp予約通知",
    "mk.cmp.r6": "セルフ予約リンク",
    "mk.cmp.r7": "GDPRデータ保護",
    "mk.cmp.r8": "繁体字UI + 香港 / 台湾 / マカオ対応",
    "mk.cmp.legend": "✓ 標準搭載 · △ 一部 / 有料 · ✗ なし",
    "mk.how.label": "3ステップで設定",
    "mk.how.title": "ゼロから最初の予約まで — お茶を淹れるより早く 🧋",
    "mk.how.s1.title": "30秒で設定",
    "mk.how.s1.body": "プランを選び、店名と予約リンクを設定。サービス4つ、スパ3つ、オプション3つを初期登録 — すぐ稼働。",
    "mk.how.s2.title": "予約リンクを共有",
    "mk.how.s2.body": "pettobook.com/shop/your-shop をWhatsApp、IG、Facebookに貼るだけ。お客様がクリックして自分で予約。",
    "mk.how.s3.title": "スケジュールとお客様を管理",
    "mk.how.s3.body": "予約はカレンダーに同期され、お客様へ自動通知。ペットごとにプロフィール、写真、健康メモを保管。",
    "mk.price.label": "プラン",
    "mk.price.title": "個人店からチェーンまでのプラン 💰",
    "mk.price.sub": "いつでもアップ・ダウングレード可能。縛りなし。",
    "mk.price.popular": "一番人気 🌟",
    "mk.price.free": "無料",
    "mk.price.permonth": "/月",
    "mk.price.unlimitedStaff": "スタッフ無制限",
    "mk.price.maxStaffPrefix": "最大 ",
    "mk.price.maxStaffSuffix": " 名のトリマー",
    "mk.price.btnFree": "無料で始める",
    "mk.price.btnChoose": "プランを選ぶ",
    "mk.price.f.scheduling": "ドラッグ&ドロップ管理",
    "mk.price.f.crm": "顧客CRM",
    "mk.price.f.whatsapp": "WhatsApp / SMS / メール",
    "mk.price.f.analytics": "レポートと分析",
    "mk.price.f.payroll": "給与と歩合",
    "mk.price.f.posLite": "POSレジ（商品100点まで）",
    "mk.price.f.posStandard": "POS：在庫・返金・シフト管理（商品500点まで）",
    "mk.price.f.posFull": "POS全機能：仕入れ・AI（商品数無制限）",
    "mk.price.f.smartPricing": "スマート料金マトリクス",
    "mk.price.f.recurring": "定期予約",
    "mk.cta.title": "サロンをレベルアップする準備は？",
    "mk.cta.sub": "30秒で登録。ベーシックプランはずっと無料。100以上のペットサロンが導入。",
    "mk.cta.btn": "今すぐ無料で始める",
    "mk.foot.copy": "© PettoBook · ペットのために設計",
    "mk.foot.privacy": "プライバシー",
    "mk.foot.terms": "利用規約",
    "mk.foot.contact": "お問い合わせ",
    "mk.mock.headerToday": "📅 本日のカレンダー",
    "mk.mock.headerTodaySub": "本日 · 5件の予約",
    "mk.mock.bookingHeader": "🐾 Furmily ペットサロン",
    "mk.mock.bookingHeaderSub": "お客様セルフ予約ページ",
    "mk.mock.step1": "1. サービスを選択",
    "mk.mock.step2": "2. 時間を選択",
    "mk.mock.confirmBtn": "確定",
    "mk.mock.pricingHeader": "✂️ シャンプー&カット — Momo",
    "mk.mock.pricingSub": "スマート料金を適用",
    "mk.mock.pricingDog": "プードル · 4kg · 長毛",
    "mk.mock.autoDetect": "自動",
    "mk.mock.basePrice": "基本",
    "mk.mock.longCoatFee": "長毛",
    "mk.mock.mattingFee": "毛玉",
    "mk.mock.spaA": "スパ追加 - A",
    "mk.mock.balanceDue": "お支払い残額",
    "mk.mock.collect": "💵 受け取る",
    "mk.mock.payrollHeader": "💰 6月給与 — Jason",
    "mk.mock.payrollSub": "6月1日 - 30日",
    "mk.mock.basePay": "基本給",
    "mk.mock.svcComm": "サービス歩合（15%）",
    "mk.mock.pkgComm": "パッケージ歩合（8%）",
    "mk.mock.ot": "残業 12h × $80",
    "mk.mock.deduct": "控除（1日休み）",
    "mk.mock.netPay": "差引支給額",
    "mk.mock.walletHeader": "🎁 お客様 — Momo",
    "mk.mock.walletSub": "プードル · 4kg",
    "mk.mock.pkgVisitsCards": "6回 / 2枚",
    "mk.mock.pkgList": "パッケージ",
    "mk.mock.pkg10": "10回パッケージ",
    "mk.mock.left4": "残り4回",
    "mk.mock.expires1": "有効期限 2026-12-31",
    "mk.mock.pkgSpa6": "スパ6回カード",
    "mk.mock.left2": "残り2回",
    "mk.mock.expires2": "有効期限 2027-03-15",
    "mk.mock.walletEarn": "💎 今回の来店で +35 ポイント獲得",
    "mk.mock.secHeader": "🛡️ システムとバックアップ",
    "mk.mock.secSub": "クラウドセキュリティ · GDPR",
    "mk.mock.backupOn": "✓ クラウド自動バックアップ オン",
    "mk.mock.backupSub": "毎日バックアップ · 7日間PITR",
    "mk.mock.staffPerms": "スタッフ権限",
    "mk.mock.roleMgr": "マネージャー · フルアクセス",
    "mk.mock.roleGroom": "トリマー · 自分の予約のみ",
    "mk.mock.roleFront": "受付 · スケジュール + 支払い",
    "mk.mock.exportData": "📥 データをエクスポート",
    "mk.mock.deleteAcc": "🗑️ アカウントを削除",
    "pub.bookNow": "今すぐ予約",
    "pub.about": "店舗紹介",
    "pub.contact": "お問い合わせ",
    "pub.team": "トリマー紹介",
    "pub.services": "サービス",
    "pub.spaSection": "スパ",
    "pub.addonsSection": "オプション",
    "pub.hours": "営業時間",
    "pub.closedToday": "本日休業",
    "pub.anyStaff": "指名なし",
    "pub.review": "確認",
    "pub.signupOrGuest": "ログイン / 新規登録 / ゲスト",
    "pub.guestName": "お名前",
    "pub.guestPhone": "電話番号 (+852)",
    "pub.haveAccount": "アカウントをお持ちの方",
    "pub.newAccount": "初めての方",
    "pub.googleContinue": "Googleで続ける",
    "pub.emailLogin": "メールでログイン",
    "pub.visitor": "ゲストとして予約",
    "pub.confirmed": "予約完了 🎉",
    "pub.viewBooking": "予約を見る",
    "cust.tabHome": "ホーム",
    "cust.tabBook": "予約",
    "cust.tabPets": "ペット",
    "cust.tabHist": "履歴",
    "cust.tabMe": "マイページ",
    "cust.helloMorning": "おはようございます",
    "cust.helloAfternoon": "こんにちは",
    "cust.helloEvening": "こんばんは",
    "cust.nextVisit": "次回の予約",
    "cust.viewAll": "すべて見る",
    "cust.bookAgain": "再予約",
    "cust.pkgTitle": "マイパッケージ",
    "cust.pkgRemain": "残り",
    "cust.pkgVisits": "回",
    "cust.pkgExpiry": "有効期限",
    "cust.buyPkg": "パッケージを購入",
    "cust.changeShop": "店舗を切り替え",
    "cust.signOut": "ログアウト",
    "cust.editName": "名前を編集",
    "common.other": "その他",
    "common.free": "無料",
    "common.unnamed": "（名称未設定）",
    "common.unassigned": "未割り当て",
    "common.saved": "保存しました ✓",
    "common.savedShort": "保存しました",
    "common.optional": "（任意）",
    "common.pickDate": "日付を選択",
    "common.pickTime": "時間を選択",
    "common.zoomIn": "拡大",
    "common.or": "または",
    "edit.editBtn": "編集",
    "edit.bookAgain": "再予約",
    "edit.report": "レポート",
    "edit.fillReport": "レポートを追加",
    "edit.noLabel": "🏷️ ラベルなし",
    "edit.customLabel": "✏️ カスタム…",
    "edit.customLabelPrompt": "カスタムラベル（空欄でクリア）",
    "edit.editPet": "ペットを編集",
    "edit.editCust": "お客様を編集",
    "edit.addNote": "+ 注意メモ",
    "edit.addNotePrompt": "注意メモを追加（例：噛む、皮膚アレルギー）",
    "edit.addNoteTitle": "ペットの注意メモを追加",
    "edit.removeNote": "注意を削除",
    "edit.noteAdded": "注意メモを追加しました",
    "edit.avatarUploaded": "ペットの写真をアップロードしました 📷",
    "edit.uploadFail": "アップロードに失敗しました: ",
    "edit.pastLocked": "過去の予約 · ステータスはロック済み",
    "edit.pastLockedToast": "過去の予約 — ステータスは変更できません",
    "edit.markAs": "「",
    "edit.andNotify": "」にしてお客様に通知",
    "edit.statusBackPrompt1": "ステータスを「",
    "edit.statusBackPrompt2": "」に戻しますか？\n（以降のタイムスタンプをクリア。お客様には通知されません。）",
    "edit.statusBackPaidWarn": "⚠️ この予約はすでに支払い済みです（${amt}）。\n戻しても自動返金はされません — 支払い記録は残り、日次集計にも計上され続けます。\n返金する場合は、まず「支払いをキャンセル」で取り消してください。\n\n戻す処理を続けますか？",
    "edit.fillReportToast": "健康レポートを入力してください",
    "edit.statusUpdated1": "「",
    "edit.statusUpdated2": "」に更新し、お客様に通知しました",
    "edit.checkin": "受付済み",
    "edit.grooming": "トリミング中",
    "edit.done": "完了",
    "edit.doneWithReport": "完了してレポート作成",
    "edit.cancelled": "キャンセル済み",
    "edit.noshow": "無断キャンセル",
    "edit.discardPrompt": "すべての変更を破棄しますか？",
    "edit.packageFail": "パッケージの利用に失敗しました",
    "edit.retryRedeem": "🔄 利用を再試行",
    "edit.clearAlert": "✕ 閉じる",
    "edit.notBoundToPkg": "この予約に紐づくパッケージはありません",
    "edit.retryRedeemOk": "パッケージを利用しました ✓",
    "edit.retryFail": "再試行に失敗しました: ",
    "edit.noOtherHist": "他の予約履歴はありません",
    "edit.healthReport": "健康レポート",
    "edit.col.date": "日付",
    "edit.col.slot": "枠",
    "edit.col.svc": "サービス",
    "edit.col.staff": "スタッフ",
    "edit.col.groomingReq": "ご要望",
    "edit.col.intNote": "社内メモ",
    "edit.col.groomerNote": "トリマーメモ",
    "edit.col.cancelReason": "キャンセル理由",
    "edit.beforeShot": "📷 ビフォー",
    "edit.afterShot": "✨ アフター",
    "edit.spaShot": "💧 スパ",
    "edit.discountOverCap1": "割引 ",
    "edit.discountOverCap2": "% は上限 ",
    "edit.discountOverCap3": "% を超えています。オーナーに承認を依頼してください",
    "edit.staffConflict1": "スタッフは ",
    "edit.staffConflict2": " にすでに予約あり — 変更できません",
    "edit.manualAdj": "手動調整",
    "edit.svcChanged1": "メインサービス $",
    "edit.discAdj": "割引を調整しました",
    "edit.reschedTo": "予約変更 → ",
    "edit.staffChanged": "スタッフを変更しました",
    "edit.petNoteUpdated": "ペットメモを更新しました",
    "edit.rescheduledTitle": "予約を変更しました 📅",
    "edit.rescheduledBody": "ご予約は次の時間になりました ",
    "edit.preFilled": "自動入力 ",
    "wi.title2": "ウォークイン登録",
    "wi.startNow": "登録 → 今すぐトリミング開始",
    "wi.walletDeduct": "ウォレット差し引き",
    "wi.subtotal": "小計",
    "wi.balance": "残額",
    "wi.duration": "所要時間（分）",
    "wi.customDur": "カスタム",
    "wi.svcName": "サービス名",
    "wi.spaName": "スパ名",
    "wi.basePrice": "基本料金 / 未設定時に表示（$）",
    "wi.priceMode": "料金設定モード",
    "wi.priceBySize": "サイズ別料金",
    "wi.priceMatrix": "サイズ × 毛の長さ別料金",
    "wi.weightFrom": "体重（kg）から",
    "wi.newSize": "サイズを追加",
    "wi.delete": "削除",
    "wi.addonsRow": "オプション ×",
    "wi.allergiesNone": "アレルギー（なし）",
    "wi.allergiesEg": "例：活発な犬 — 追加のケアが必要…",
    "wi.spaEg": "例：鎮静スキンスパ：温塩浴＋ディープ保湿、約30分。",
    "wi.sizeEg": "例：小型",
    "wi.coatShort": "短毛",
    "wi.coatLong": "長毛",
    "wi.coatMed": "中毛",
    "wi.sizeSmall": "小型",
    "wi.sizeMed": "中型",
    "wi.sizeLarge": "大型",
    "wi.sizeXL": "XL",
    "wi.sizeGiant": "超大型",
    "wi.dog": "🐶 犬",
    "wi.cat": "🐱 猫",
    "wi.small": "🐇 小動物",
    "wi.name": "名前 *",
    "wi.breed": "犬種・猫種 *",
    "wi.phone": "電話番号 *",
    "wi.fullName": "名前 *",
    "wi.weight": "体重 kg *",
    "wi.newBreed": "新しい品種",
    "wi.tagVip": "VIP",
    "wi.tagFriend": "スタッフ・家族",
    "wi.tagCompensation": "補償",
    "wi.tagTrial": "お試し",
    "wi.tagEvent": "イベント",
    "wi.tagReferral": "紹介",
    "wi.custom": "カスタム",
    "wi.row": "行",
    "wi.no": "#",
    "wi.minGtMax": "\" 最小値が最大値より大きい — 入れ替えてください",
    "wi.negWeight": "\" 体重をマイナスにはできません",
    "wi.kgNoMatch": "kg は該当する区分がないため、基本料金 $ を使用します",
    "wi.slotTaken": "埋まっています",
    "wi.overlap1": "）と「",
    "wi.overlap2": "）が重複しています",
    "wi.petsCount": " 匹",
    "wi.surchargeAdd": "+ 追加料金",
    "wi.surchargeName": "名称（例：毛玉処理料）",
    "wi.preset": "プリセット",
    "pub.welcome": "ようこそ 🐾",
    "pub.signupPrompt": "ログインまたは登録して予約を開始",
    "pub.loginContinue": "続けるにはログインしてください",
    "pub.loginOrSignup": "ログイン / 新規登録",
    "pub.loginFail": "ログインに失敗しました",
    "pub.googleFail": "Googleログインに失敗しました",
    "pub.appleFail": "Appleログインに失敗しました",
    "pub.appleSignin": "Appleでサインイン",
    "pub.continueWith": "続ける：",
    "pub.emailGoogle": "メール / Google · 30秒",
    "pub.newAccountStart": "新規アカウントを作成して予約",
    "pub.shopSchedule": "お店のスケジュールを一目で確認",
    "pub.multiPet": "複数のペットに対応 🐾",
    "pub.bookSuccess": "完了！お預かりをお待ちください",
    "pub.maxReward": "特典が最大に達しました 🎉",
    "pub.rewards": "特典",
    "pub.svcRemoved": "このサービスは現在ご利用いただけません — 別のものをお選びください",
    "pub.savePetFail": "ペット情報の保存に失敗しました",
    "pub.bookFail": "予約に失敗しました",
    "pub.bookNotSaved": "予約が保存されませんでした — もう一度お試しいただくか、お店にご連絡ください",
    "pub.promoFail": "プロモコードを適用できませんでした — ご来店時にお店で提示してください。",
    "pub.loginBeforeBook": "予約の前にログインしてください",
    "pub.elderRequired": "シニアペットは確認画面で同意書が必要です",
    "pub.discount50": "$50割引",
    "pub.newCustOnly": "　·　新規のお客様限定",
    "pub.todayOpen": " · 本日",
    "pub.included": " 込み",
    "pub.daysAgo": " 日前",
    "pub.lastVisit": "前回のご来店 ",
    "pub.fillForPrice": "フォームを入力すると正確な料金を表示します",
    "pub.confirmBtn": "予約を確定",
    "pub.confirmDeposit": "確認 + 予約金を支払う",
    "pub.payDeposit": "予約金を支払う",
    "pub.tax": "税",
    "pub.submitting": "送信中…",
    "pub.pickSvc": "メインサービスを選択してください",
    "pub.pickPet": "ペットを選択または追加してください",
    "pub.pickDate": "日付を選択してください",
    "pub.pickTime": "時間を選択してください",
    "pub.enterName": "名前を入力してください",
    "pub.enterBreed": "犬種・猫種を入力してください",
    "pub.enterWeight": "体重を入力してください",
    "pub.stepPet": "ペット + サービス",
    "pub.stepTime": "トリマー + 時間",
    "pub.choosePet": "ペットを選択または新規追加",
    "pub.namePh": "例：もこ",
    "pub.weightPh": "例：5.5",
    "pub.phonePh": "電話番号（例：9123 4567）",
    "pub.privacyAccept": "プライバシーポリシー",
    "pub.none": "なし",
    "pub.holiday": "休業",
    "pub.dog": "犬",
    "pub.cat": "猫",
    "pub.rabbit": "ウサギ",
    "pub.hamster": "ハムスター",
    "pub.otherPet": "その他",
    "pub.coatShort": "短毛",
    "pub.coatLong": "長毛",
    "pub.coatMed": "中毛",
    "pub.langTrad": "中",
    "cust.helloMorning2": "おはようございます",
    "cust.bookFail": "予約に失敗しました — お店でご登録ください",
    "cust.svcDownloaded": "プロフィールをダウンロードしました ✓",
    "cust.downloadData": "データをダウンロード",
    "cust.deleteAccount": "アカウントを削除",
    "cust.logout": "ログアウト",
    "cust.downloadFail": "ダウンロードに失敗しました",
    "cust.savePhoneFail": "電話番号の保存に失敗しました：",
    "cust.savedShort": "保存しました",
    "cust.saveFail": "保存に失敗しました",
    "cust.deleteFail": "削除に失敗しました — サポートにご連絡ください",
    "cust.wallet": "ウォレット残高",
    "cust.walletLow": "ウォレット残高が不足しています",
    "cust.signSig": "同意書に署名済み",
    "cust.elderSign": "シニアペットの同意書が必要です",
    "cust.shareFail": "共有に失敗しました：",
    "cust.viewAgain": "もう一度見る",
    "cust.topupWallet": "こんにちは、ウォレットをチャージしたいです：",
    "cust.buyPkgMsg": "こんにちは、パッケージを購入したいです：",
    "cust.recentBook": "最近の予約",
    "cust.transactions": "取引履歴",
    "cust.myRewards": "🎟️ マイクーポン",
    "cust.privacyRights": "🔐 プライバシーとデータ",
    "cust.recurringBook": "定期予約",
    "cust.depositHistory": "予約金・利用履歴",
    "cust.spaSection": "スパサービス 💆",
    "cust.luxAddons": "自分へのご褒美オプション ✨",
    "cust.beforeAfter": " ビフォー＆アフター ✨",
    "cust.styleHint": "✂️ ご希望のスタイル：",
    "cust.vetNote": "📋 獣医メモ",
    "cust.allergyInfo": "アレルギー",
    "cust.lastVaccine": "前回のワクチン",
    "cust.depositTopup": "ウォレットをチャージすると、ここに履歴が表示されます",
    "cust.regPetFirst": "まずペットを登録してください 🐾",
    "cust.notifyShop": "✅ お店に通知しました。お支払い案内をお待ちください",
    "cust.waitingPay": "WhatsAppを開きました — お支払い確認をお待ちください",
    "cust.contactShop": "WhatsAppでお店に連絡",
    "cust.linkedOk": "✅ 連携に成功しました ",
    "cust.shareReview": "ご感想をシェア…",
    "cust.encMsg": "応援メッセージ",
    "cust.deposit": "予約金",
    "cust.svc": "サービス",
    "cust.feeLabel": "（手数料 $",
    "cust.expires": " 有効期限",
    "cust.daysValid": " 日間有効",
    "cust.weeks": " 週間",
    "cust.times": " 回",
    "cust.deletedPet": "（ペット削除済み）",
    "cust.encNicknamePh": "例：ミルキー",
    "cust.usePointsRedeem": "ポイントを使う（",
    "cust.pointsLabel": " ポイント）",
    "cust.exchangePts": " ポイントで交換",
    "cust.rewardPts": " ポイント 🎉",
    "cust.copyPaste": "前へ／次へ",
    "cust.lastGroom": "前回のトリミング ",
    "cust.styleEg": "例：全体的に短くカット、足はふんわり残す、丸顔に",
    "cust.kgPriceHint": "kg）を入力すると正確な料金が出ます",
    "cust.priceFrom": "$X〜",
    "cust.fpsLabel": "FPS / FastPay ⚡",
    "cust.spaFree": "スパ無料",
    "cust.spaFreePkg": "無料（パッケージ）",
    "cust.spaFreeUpsell": "スパ／オプション無料",
    "cust.spaFreeHigh": "プレミアムスパ無料",
    "cust.rewardCoupon": "クーポン",
    "cust.transferred": " 振替 $",
    "cust.contactPhone": "電話番号 *",
    "cust.companion": "（同伴）",
    "cust.refillSoon": "残りわずか？チャージ ",
    "cust.maxWeight1": "受け入れ可能な最大体重は ",
    "cust.maxWeight2": "kg です、",
    "cust.elderSignReq": "シニアペットの同意書が必要です",
    "cust.appointmentReschd": "さんの予約を変更しました：",
    "cust.updateOk": "さんの情報を更新しました！",
    "cust.addedOk": " を追加しました ✓",
    "cust.addedPickSvc": " を追加しました — サービスをお選びください",
    "cust.svcAddedOk": " を正常に追加しました！",
    "cust.oldPet": " 匹の以前のデータ",
    "cust.nicknameEg": "例：ミルキー",
    "cust.confirmAdd": "✨ 追加を確定",
    "cust.addCount": " を追加しました ✓",
    "cust.medCoat": "中毛",
    "cust.midDog": "中型犬",
    "cust.nicePoint": "いいね 🙂",
    "cust.encPh": "例：ミルキー",
    "cust.encEg2": "例：ミルキー",
    "apt.viewMore": "もっと見る",
    "apt.surchargePh": "名称（例：毛玉処理料）",
    "apt.labelCustom": "カスタム",
    "apt.labelPending": "保留中",
    "apt.labelConfirmed": "確定済み",
    "apt.labelPaid": "支払い済み",
    "apt.labelLate": "遅刻",
    "apt.labelWait": "待機中",
    "apt.labelVip": "VIP",
    "apt.labelRush": "急ぎ",
    "apt.labelOther": "その他",
    "apt.statusPending": "保留中",
    "apt.statusCheckedin": "チェックイン済み",
    "apt.statusGrooming": "トリミング中",
    "apt.statusDone": "完了",
    "apt.statusCancelled": "キャンセル済み",
    "apt.statusNoshow": "無断キャンセル",
    "apt.yourPet": "あなたのペット",
    "apt.payRecord": "まだ支払い記録がありません",
    "apt.otherMethod": "その他",
    "apt.via": "方法：",
    "apt.cash": "現金 💵",
    "apt.card": "カード 💳",
    "apt.fps": "FPS / PayMe 📱",
    "apt.walletM": "ウォレット 💰",
    "apt.pkgRemain": " パッケージ",
    "apt.pkgVisits": " 回分 / ",
    "apt.noPkg": "パッケージなし",
    "apt.editStart": "開始時刻を編集",
    "apt.walkin": "飛び込み",
    "apt.unclaimed": "未割当",
    "cust.book.title": "サービスを予約",
    "cust.book.summary": "確認して確定",
    "cust.book.depositDue": "予約金のお支払い",
    "cust.book.totalDue": "合計金額",
    "cust.book.walletDeduct": "ウォレットからの差引",
    "cust.book.useWallet": "ウォレット残高を使う",
    "cust.book.walletBalance": "ウォレット残高",
    "cust.book.usePoints": "ポイントを使う",
    "cust.book.pointsDiscount": "ポイント割引",
    "cust.book.voucherDiscount": "クーポン割引",
    "cust.book.actualDeposit": "お支払いの予約金",
    "cust.book.depositMethod": "予約金の支払い方法",
    "cust.book.markPaid": "✅ 支払い済み — 予約を送信",
    "cust.book.payLater": "後払い — お店で",
    "cust.book.wallet.notEnough": "ウォレット残高が不足しています",
    "cust.book.qrInstructions": "いずれかでお支払い後、「支払い済み — 予約を送信」をタップしてください。",
    "cust.book.useVoucher": "🎟️ クーポンを使う（任意）",
    "cust.book.usePackage": "📦 パッケージを使う（任意）",
    "cust.book.notUsing": "使わない",
    "cust.book.success": "予約が確定しました！",
    "cust.book.successSub": "{petName}とのご来店をお待ちしています 🐾",
    "cust.book.rescheduleSuccess": "予約を変更しました 🎉",
    "cust.book.cancelSuccess": "予約をキャンセルしました",
    "cust.book.bookNow": "今すぐ予約",
    "cust.book.confirmAndPay": "確認して支払う",
    "cust.book.markComplete": "支払いました — お店に通知",
    "cust.book.alreadyNotified": "お店に通知済み — 確認をお待ちください",
    "cust.book.waNotConfigured": "お店がWhatsAppを設定していません — 直接ご連絡ください",
    "cust.book.policyAccepted": "利用規約を読み、同意します",
    "cust.book.sendReceipt": "WhatsAppで領収書を送る",
    "cust.book.petAge.senior": "シニアペットには責任免除の同意書が必要です",
    "cust.book.petAge.tooYoung": "生後4か月未満のペットは予約できません",
    "cust.book.overWeight": "体重制限を超えています",
    "cust.book.breedBlocked": "この品種は受け付けていません",
    "cust.history.title": "予約履歴",
    "cust.history.upcoming": "今後の予約",
    "cust.history.past": "過去のご来店",
    "cust.history.empty": "まだ予約がありません",
    "cust.history.emptySub": "ご予約はここに表示されます。",
    "cust.history.rate": "評価する",
    "cust.history.viewReport": "レポートを見る",
    "cust.history.reschedule": "予約変更",
    "cust.history.cancel": "予約をキャンセル",
    "cust.rewards.title": "特典",
    "cust.rewards.stamps": "スタンプ",
    "cust.rewards.vouchers": "クーポン",
    "cust.rewards.points": "ポイント",
    "cust.rewards.empty": "まだ何もありません",
    "cust.rewards.emptySub": "予約を完了すると貯まり始めます。",
    "cust.rewards.unlocked": "獲得した特典",
    "cust.rewards.useNow": "使う →",
    "cust.rewards.shopGifts": "🎟️ お店発行のクーポン",
    "cust.rewards.useAtBooking": "次回の予約時に「クーポンを使う」をお選びください。",
    "cust.wallet.balance": "ウォレット残高",
    "cust.wallet.topup": "チャージ 💰",
    "cust.wallet.history": "取引履歴",
    "cust.wallet.empty": "まだ取引がありません",
    "cust.profile.contact": "連絡先",
    "cust.profile.security": "アカウントのセキュリティ",
    "cust.profile.passwordChange": "パスワードを変更",
    "cust.profile.dataRights": "🔐 データに関する権利",
    "cust.profile.exportData": "📥 自分のデータをエクスポート",
    "cust.profile.deleteAccount": "🗑️ アカウントを削除",
    "cust.profile.deleteWarn": "アカウントを完全に削除しますか？この操作は取り消せません。",
    "cust.profile.deletedToast": "アカウントを完全に削除しました",
    "cust.profile.dataDownloaded": "データをダウンロードしました ✓",
    "cust.toast.saved": "保存しました ✓",
    "cust.toast.savedShort": "保存しました",
    "cust.toast.saveFailed": "保存に失敗しました",
    "cust.toast.copied": "コピーしました ✓",
    "cust.toast.deleted": "削除しました",
    "cust.toast.rateThanks": "評価ありがとうございます ⭐",
    "cust.toast.welcome": "ようこそ",
    "cust.confirm.cancelBooking": "この予約をキャンセルしますか？\n• お店に通知されます\n• 予約金はお店の規定に従って返金されます",
    "cust.confirm.deleteAccount": "アカウントを完全に削除しますか？すべてのデータが永久に失われます。",
    "cust.label.contact": "📞 連絡先",
    "cust.label.address": "住所",
    "cust.label.email": "メール",
    "cust.label.phone": "電話番号",
    "cust.label.editProfile": "プロフィール編集",
    "cust.label.recentBookings": "最近の予約",
    "cust.label.depositHistory": "予約金・利用履歴",
    "cust.label.recurringSettings": "定期予約",
    "cust.label.noRecurring": "定期予約は未設定です",
    "cust.label.dataExport": "データをエクスポート",
    "cust.label.dataDelete": "アカウントを完全に削除",
    "cust.empty.noPets": "ペットがまだいません",
    "cust.empty.noPetsSub": "「追加」をタップして最初のペットを登録してください。",
    "cust.hist.recentBookings": "最近の予約",
    "cust.hist.recurring": "定期",
    "cust.hist.noMatch": "該当する予約はありません",
    "cust.hist.noMatchSub": "下の予約ボタンをタップして作成してください。",
    "cust.hist.deletedPet": "（削除されたペット）",
    "cust.hist.addons": "✨ オプション: ",
    "cust.hist.cancelBtn": "キャンセル",
    "cust.hist.rescheduleBtn": "日時変更",
    "cust.hist.rateBtn": "トリマーを評価",
    "cust.hist.viewReport": "レポートを見る",
    "cust.hist.cancelReason": "理由: ",
    "cust.pets.addFirst": "最初のペットを追加 🐾",
    "cust.pets.addFirstSub": "詳細を入力して予約を始めましょう。",
    "cust.pets.type": "種類",
    "cust.pets.typeDog": "犬",
    "cust.pets.typeCat": "猫",
    "cust.pets.typeRabbit": "ウサギ",
    "cust.pets.typeHamster": "ハムスター",
    "cust.pets.weight": "体重 (kg)",
    "cust.pets.weightPh": "例: 5.5",
    "cust.pets.breed": "品種",
    "cust.pets.coat": "被毛の長さ",
    "cust.pets.short": "短い",
    "cust.pets.long": "長い",
    "cust.pets.allergyInfo": "アレルギー",
    "cust.pets.allergyPh": "例: なし / ラベンダー",
    "cust.pets.vaccineDate": "最終ワクチン接種",
    "cust.pets.vetNote": "獣医メモ",
    "cust.pets.vetNotePh": "特別なケアのメモ",
    "cust.pets.editBtn": "編集",
    "cust.pets.deleteBtn": "削除",
    "cust.pets.confirmDelete": "このペットを削除しますか？",
    "cust.pets.addBtn": "ペットを追加",
    "cust.pets.saveBtn": "💾 変更を保存",
    "cust.pets.newBtn": "✨ 追加",
    "cust.pets.profilePhoto": "プロフィール写真 📸",
    "cust.pets.allergy": "アレルギー",
    "cust.pets.vaccine": "ワクチン",
    "cust.pets.notRecorded": "記録なし",
    "cust.pets.noAllergy": "なし",
    "cust.profile.accountId": "アカウントID",
    "cust.profile.shopBranch": "ホーム店舗",
    "cust.profile.tags": "タグ",
    "cust.profile.notSet": "—",
    "cust.profile.viewPolicy": "プライバシーポリシーを見る",
    "cust.profile.changeLanguage": "🌐 言語",
    "cust.recurring.title": "定期予約を設定",
    "cust.recurring.selectPet": "ペット",
    "cust.recurring.selectService": "サービス",
    "cust.recurring.selectStaff": "トリマー",
    "cust.recurring.selectDay": "曜日",
    "cust.recurring.selectTime": "時間",
    "cust.recurring.interval": "繰り返し",
    "cust.recurring.weekly": "毎週",
    "cust.recurring.monthly": "毎月",
    "cust.recurring.saveBtn": "定期予約を保存",
    "cust.recurring.savedToast": "定期予約を保存しました ✓",
    "cust.cancel.reason": "キャンセル理由",
    "cust.cancel.reasonPh": "理由をお聞かせください（任意）",
    "cust.cancel.others": "その他",
    "cust.cancel.submit": "キャンセルを確定",
    "cust.cancel.refunded": "返金済み",
    "cust.rate.title": "トリマーを評価",
    "cust.rate.sub": "あなたの評価が他のお客様の参考になります ❤️",
    "cust.rate.commentPh": "コメントを入力（任意）",
    "cust.rate.submit": "評価を送信",
    "cust.report.title": "トリミングレポート",
    "cust.report.close": "閉じる",
    "own.dash.appointmentsToday": "本日の予約",
    "own.dash.revenueToday": "本日の売上",
    "own.dash.lowStock": "在庫不足アラート",
    "own.dash.staleBanner": "📋 未処理の予約",
    "own.dash.staleBannerStale": " 件の過去の予約が未完了のままです。",
    "own.dash.staleBannerUnpaid": " 件の完了予約に未払い残高があります。",
    "own.dash.staleBannerTap": "タップして処理。",
    "own.dash.walkinBtn": "当日来店",
    "own.schedule.day": "日",
    "own.schedule.week": "週",
    "own.schedule.month": "月",
    "own.schedule.agenda": "一覧",
    "own.schedule.swipeHint": "← スワイプで日を切替 · ピンチでズーム",
    "own.schedule.noStaff": "全スタッフ非表示",
    "own.schedule.unassigned": "未割当",
    "own.schedule.prevWeek": "前の週",
    "own.schedule.nextWeek": "次の週",
    "own.schedule.allEmpty": "この日は全枠が埋まっています",
    "own.schedule.conflict": "⚠ 重複",
    "own.schedule.confirmReschedule": "日時を変更しますか？",
    "own.schedule.rescheduleSuccess": "日時を変更しました ✓",
    "own.schedule.rescheduleFailed": "日時変更に失敗しました",
    "own.schedule.bookSuccess": "予約を追加しました ✓",
    "own.cal.todayMarker": "今日",
    "own.cal.hr": ":00",
    "own.cal.legend.pending": "保留中",
    "own.cal.legend.checkedin": "受付済み",
    "own.cal.legend.grooming": "施術中",
    "own.cal.legend.done": "完了",
    "own.cal.legend.event": "イベント",
    "own.cal.collected": "回収済み",
    "own.cal.unpaid": "未払い",
    "own.cal.partial": "一部支払い",
    "own.cal.paid": "支払済み",
    "own.cal.package": "パッケージ",
    "own.walkin.title": "当日来店",
    "own.walkin.selectCustomer": "お客様",
    "own.walkin.newCustomer": "新規のお客様",
    "own.walkin.selectPet": "ペット",
    "own.walkin.newPet": "ペットを追加",
    "own.walkin.selectService": "サービス",
    "own.walkin.selectStaff": "トリマー",
    "own.walkin.startTime": "開始時刻",
    "own.walkin.addons": "オプション（任意）",
    "own.walkin.spa": "スパ（任意）",
    "own.walkin.customDuration": "所要時間を指定",
    "own.walkin.discount": "割引",
    "own.walkin.discountReason": "割引理由",
    "own.walkin.staffRemark": "スタッフメモ",
    "own.walkin.useWallet": "ウォレットから差し引く",
    "own.walkin.usePackage": "パッケージを使用",
    "own.walkin.startGrooming": "施術を開始",
    "own.walkin.savedAsPending": "保存しました — 保留中",
    "own.walkin.savedAsCheckedin": "保存しました — 受付済み",
    "own.walkin.savedAsGrooming": "保存しました — 施術中",
    "own.walkin.amountTotal": "合計",
    "own.walkin.allergyWarn": "⚠️ アレルギー: ",
    "own.walkin.confirmAdd": "この当日来店予約を追加しますか？",
    "own.walkin.confirmAddBtn": "予約を追加",
    "own.walkin.cancelBtn": "キャンセル",
    "own.walkin.searchCustPh": "名前または電話番号で検索",
    "own.walkin.noPet": "登録ペットなし — その場で追加",
    "own.walkin.timeConflict": "このトリマーはこの時間にすでに予約があります",
    "own.editApt.title": "予約詳細",
    "own.editApt.editMode": "編集",
    "own.editApt.saveEdit": "保存",
    "own.editApt.cancelEdit": "破棄",
    "own.editApt.unsavedConfirm": "未保存の変更があります — このまま離れますか？",
    "own.editApt.label": "ラベル",
    "own.editApt.noLabel": "（ラベルなし）",
    "own.editApt.customLabelPrompt": "カスタムラベルを入力",
    "own.editApt.markAs": "次に変更: ",
    "own.editApt.andNotify": " してお客様に通知",
    "own.editApt.pastLocked": "過去の予約 · ステータス固定",
    "own.editApt.pastLockedToast": "過去の予約 — ステータスは変更できません",
    "own.editApt.statusUpdated": "ステータスを更新しました",
    "own.editApt.report": "トリミングレポート",
    "own.editApt.skin": "皮膚",
    "own.editApt.ear": "耳",
    "own.editApt.teeth": "歯",
    "own.editApt.mood": "機嫌",
    "own.editApt.nails": "爪",
    "own.editApt.analGlands": "肛門腺",
    "own.editApt.parasites": "寄生虫",
    "own.editApt.eyeDischarge": "目やに",
    "own.editApt.matting": "毛玉",
    "own.editApt.notes": "トリマーメモ",
    "own.editApt.photoUpload": "写真をアップロード",
    "own.editApt.report.save": "レポートを保存",
    "own.editApt.report.send": "保存してお客様に通知",
    "own.editApt.checkin": "受付",
    "own.editApt.startGroom": "施術を開始",
    "own.editApt.markDone": "完了にする",
    "own.editApt.markNoShow": "無断キャンセル",
    "own.editApt.cancelBooking": "予約をキャンセル",
    "own.crm.title": "お客様",
    "own.crm.search": "お客様を検索…",
    "own.crm.empty": "お客様がまだいません",
    "own.crm.emptySub": "初回予約後にお客様がここに表示されます。",
    "own.crm.tab.info": "プロフィール",
    "own.crm.tab.records": "予約",
    "own.crm.tab.wallet": "ウォレット",
    "own.crm.tab.ledger": "💰 取引履歴",
    "own.crm.totalSpent": "累計利用額",
    "own.crm.groomCount": "来店回数",
    "own.crm.contact": "📞 連絡先",
    "own.crm.visitor": "ゲストアカウント（メールログインなし）",
    "own.crm.editProfile": "プロフィール編集",
    "own.crm.blockBtn": "🚫 お客様をブロック",
    "own.crm.unblockBtn": "🚫 ブロック解除",
    "own.crm.blocked": "🚫 ブロック中",
    "own.crm.blockReason": "理由: ",
    "own.crm.confirmBlock": "このお客様をブロックしますか？\nこの店舗では再び予約できなくなります。他店舗には影響しません。\n\n理由（任意）:",
    "own.crm.confirmUnblock": "このお客様のブロックを解除しますか？",
    "own.crm.blockedToast": "お客様をブロックしました 🚫",
    "own.crm.unblockedToast": "ブロックを解除しました ✓",
    "own.crm.disableBtn": "無効化",
    "own.crm.deleteBtn": "完全に削除",
    "own.crm.confirmDisable": "お客様「{name}」を無効化しますか？\n予約履歴は保持されますが、ログインや予約はできなくなります（いつでも再有効化できます）。",
    "own.crm.disabledToast": "お客様を無効化しました",
    "own.crm.addWallet": "ウォレットにチャージ",
    "own.crm.sellPackage": "📦 パッケージを販売 / 贈呈",
    "own.crm.managePackage": "📋 パッケージ管理",
    "own.crm.noWalletTx": "取引がまだありません",
    "own.crm.noWalletTxSub": "ウォレットの利用記録はありません",
    "own.crm.walletDeducted": "ウォレット利用",
    "own.crm.pointsDeducted": "ポイント利用",
    "own.crm.vouchersUsed": "クーポン利用",
    "own.crm.ledger.empty": "取引履歴がありません",
    "own.crm.ledger.emptySub": "お客様はまだウォレット・ポイント・クーポンを利用していません。",
    "own.topup.title": "ウォレットにチャージ",
    "own.topup.amount": "金額",
    "own.topup.note": "メモ",
    "own.topup.method": "支払い方法",
    "own.topup.save": "チャージを確定",
    "own.topup.savedToast": "${amount} をチャージしました ✓",
    "own.topup.deductBtn": "ウォレットから差引",
    "own.topup.deductConfirm": "このお客様のウォレットから差し引きますか？",
    "own.topup.deductReason": "差引の理由",
    "own.pkg.activeFilter": "有効",
    "own.pkg.expiredFilter": "期限切れ",
    "own.pkg.spentFilter": "使い切り",
    "own.pkg.refundedFilter": "返金済み",
    "own.pkg.allFilter": "すべて",
    "own.pkg.empty": "パッケージがまだありません",
    "own.pkg.remaining": "",
    "own.pkg.visits": " 回分残り",
    "own.pkg.expires": "有効期限 ",
    "own.pkg.expired": "期限切れ",
    "own.pkg.refunded": "返金済み",
    "own.pkg.gift": "🎁 無料贈呈",
    "own.pkg.usageLog": "📜 履歴",
    "own.pkg.extend30": "📅 +30日",
    "own.pkg.extendCustom": "📅 任意で延長",
    "own.pkg.restore1": "↩️ 1回復元",
    "own.pkg.deduct1": "➖ 1回差引",
    "own.pkg.refundBtn": "💸 返金",
    "own.pkg.confirmRestore": "1回分を復元しますか？（誤った差引を取り消す際に使用）",
    "own.pkg.confirmDeduct": "もう1回分を差し引きますか？（1回の施術で複数のパッケージサービスを利用した場合）",
    "own.pkg.restoredToast": "1回分を復元しました ✓",
    "own.pkg.deductedToast": "1回分を差し引きました ✓",
    "own.pkg.extendedToast": "{days}日延長しました ✓",
    "own.pkg.extendPrompt": "何日延長しますか？",
    "own.pkg.refundPrompt": "返金額は？",
    "own.pkg.refundReason": "返金理由",
    "own.pkg.refundedToast": "${amount} を返金しました ✓",
    "own.sell.title": "パッケージを販売 / 贈呈",
    "own.sell.intro": "{name} さんにパッケージを追加します。残り回数と有効期限がアプリに表示されます。",
    "own.sell.selectPackage": "パッケージを選択",
    "own.sell.selectPet": "ペットを選択",
    "own.sell.totalVisits": "利用回数",
    "own.sell.expires": "有効期限",
    "own.sell.paymentMethod": "支払い方法",
    "own.sell.amountPaid": "受領金額",
    "own.sell.giftToggle": "ギフト（支払いなし）",
    "own.sell.submitBtn": "確定",
    "own.sell.successPkg": "パッケージを発行しました ✓",
    "own.sell.successPaid": "${amount} を受領しました ✓",
    "own.voucher.title": "クーポン",
    "own.voucher.empty": "クーポンはまだありません",
    "own.voucher.unused": "未使用",
    "own.voucher.used": "使用済み",
    "own.voucher.usedOn": "使用日 ",
    "own.set.policy.wallet": "ウォレットを有効化",
    "own.set.policy.walletSub": "オフにすると、お客様アプリでウォレットタブとチャージボタンが非表示になります。既存の残高は記録として保持されます。",
    "own.set.policy.loyalty": "ポイントを有効化",
    "own.set.policy.loyaltySub": "オフにすると、お客様にポイントが表示されず、予約でポイントも付与されません。",
    "own.set.policy.earnRule": "$X 利用ごとに1ポイント付与",
    "own.set.policy.redeemRule": "1ポイント = $Y 相当",
    "own.set.policy.example": "💡 例：$100 利用 → {n} ポイント付与 → ${cash} 分を利用",
    "own.set.policy.review": "お客様の評価を有効化",
    "own.set.policy.reviewSub": "オフにすると、お客様に評価を求めず、レビュータブも非表示になります。",
    "own.set.policy.healthReport": "トリミング後の健康レポートを有効化",
    "own.set.policy.healthReportSub": "オンにすると、「完了」をタップした際に健康レポートのフォームが開きます。",
    "own.set.policy.maxWeight": "⚖️ 受け入れ可能な最大体重（kg）",
    "own.set.policy.maxWeightSub": "この体重を超えるペットは予約できません。空欄 / 0 で制限なし。",
    "own.set.policy.brandRemove": "🏷️ PettoBook ブランド表示を削除",
    "own.set.policy.brandRemoveSub": "オンにすると、お客様予約ページのフッター、ビフォーアフター共有画像、お客様アプリから「Powered by PettoBook」が表示されなくなります。",
    "own.set.policy.brandRemoveProBadge": "👑 Pro プランが必要",
    "own.set.brand.title": "店舗テーマ 🎨",
    "own.set.brand.logo": "ロゴ",
    "own.set.brand.banner": "バナー",
    "own.set.brand.tagline": "キャッチコピー",
    "own.set.brand.about": "店舗紹介",
    "own.set.brand.website": "ウェブサイト",
    "own.set.brand.instagram": "Instagram",
    "own.set.brand.facebook": "Facebook",
    "own.set.brand.gallery": "写真ギャラリー",
    "own.set.hours.businessHours": "営業時間",
    "own.set.hours.closed": "休業",
    "own.set.hours.allDay": "終日",
    "own.set.hours.holidays": "祝日 🏖️",
    "own.set.hours.holidaysSub": "祝日は参考用にカレンダーに表示されますが、自動的に休業にはなりません。",
    "own.set.hours.addHoliday": "祝日を追加",
    "own.set.hours.removeHoliday": "削除",
    "own.set.msg.autoCommTitle": "💬 自動メッセージ",
    "own.set.msg.autoCommSub": "予約確認、変更、完了の通知は PettoBook から自動送信されます。",
    "own.set.msg.channelEmail": "📧 メール",
    "own.set.msg.channelSms": "📱 SMS",
    "own.set.msg.emailDesc": "おすすめ：領収書、リマインダー、レポート（常に無料）",
    "own.set.msg.smsDesc": "年配のお客様にも届きます。1通あたり約 $0.05",
    "own.set.msg.smsPaywall": "🔒 Basic または Pro プランが必要",
    "own.set.msg.upgradeRequired": "有料プランが必要です — まずアップグレードしてください",
    "own.set.msg.advanced": "🛠️ 詳細設定（独自 API）",
    "own.set.msg.advancedHint": "メールと SMS は PettoBook から送信され、設定は不要です。以下はご自身の WhatsApp Business 番号です。",
    "own.set.msg.waBYOK": "💬 WhatsApp Business（BYOK）",
    "own.set.msg.waPhoneId": "WhatsApp 電話番号 ID",
    "own.set.msg.waToken": "アクセストークン",
    "own.set.msg.waFromName": "表示名（任意）",
    "own.set.msg.waTemplate": "デフォルトテンプレート名（任意）",
    "own.set.msg.saveBtn": "保存",
    "own.set.msg.sendTestBtn": "📲 自分にテスト送信",
    "own.set.msg.savedToast": "メッセージ設定を保存しました",
    "own.set.msg.saveFailed": "保存に失敗しました",
    "own.set.msg.testSent": "✅ テストメッセージを送信しました",
    "own.set.msg.testFailed": "送信に失敗しました",
    "own.set.msg.perTriggerCard": "⚡ イベント別設定",
    "own.set.msg.perTriggerSub": "各イベント（予約確認、変更、リマインダーなど）でチャネルを選択し、オン/オフを切り替えます",
    "own.set.msg.defaultChannel": "デフォルトチャネル（全通知）",
    "own.set.msg.perEvent": "イベント別",
    "own.set.msg.perEventHint": "左で切替：オン/オフ、右：チャネル（空欄でデフォルト）",
    "own.set.msg.trig.booking": "📅 予約確認",
    "own.set.msg.trig.reschedule": "🔁 予約変更",
    "own.set.msg.trig.cancel": "❌ キャンセル",
    "own.set.msg.trig.dayBefore": "⏰ 前日リマインダー",
    "own.set.msg.trig.groomingDone": "🎉 トリミング完了",
    "own.set.msg.trig.useDefault": "（デフォルト）",
    "own.set.msg.trig.both": "📧 + 📱 両方",
    "own.set.tpl.title": "📝 メッセージテンプレート",
    "own.set.tpl.intro": "各イベントの自動メッセージをカスタマイズできます。空欄でデフォルトを使用します。",
    "own.set.tpl.dynamicFields": "利用可能な変数：",
    "own.set.tpl.customized": "カスタマイズ済み",
    "own.set.tpl.restoreDefault": "デフォルトに戻す",
    "own.set.tpl.save": "保存",
    "own.set.tpl.savedToast": "保存しました ✓",
    "own.set.tpl.emailSubject": "📧 メール件名",
    "own.set.tpl.messageBody": "💬 本文",
    "tx.saved": "保存しました ✓",
    "tx.saveFailed": "保存に失敗しました",
    "tx.deleted": "削除しました",
    "tx.deleteFailed": "削除に失敗しました",
    "tx.copied": "コピーしました",
    "tx.uploadOk": "アップロードしました",
    "tx.uploadFailed": "アップロードに失敗しました",
    "tx.networkError": "ネットワークエラー — もう一度お試しください",
    "tx.actionFailed": "操作に失敗しました",
    "tx.confirm": "確定",
    "tx.cancel": "キャンセル",
    "tx.yes": "はい",
    "tx.no": "いいえ",
    "tx.permissionDenied": "権限がありません",
    "tx.required": "この項目は必須です",
    "tx.invalidFormat": "形式が正しくありません",
    "tx.emailInvalid": "メールアドレスの形式が正しくありません",
    "tx.phoneInvalid": "電話番号の形式が正しくありません",
    "tx.passwordTooShort": "パスワードが短すぎます",
    "tx.csvNoData": "CSV が空です — 少なくとも1行追加してください",
    "tx.csvParseFailed": "CSV の解析に失敗しました",
    "tx.confirmDelete": "削除しますか？",
    "tx.confirmCancel": "キャンセルしますか？",
    "tx.unsavedChanges": "未保存の変更があります — このまま離れますか？",
    "tx.staffSaved": "スタッフを更新しました ✓",
    "tx.shopDeleted": "店舗を削除しました",
    "tx.enableFailed": "有効化に失敗しました — もう一度お試しください",
    "tx.qrUploaded": "QR をアップロードしました",
    "tx.emailSettingsSaved": "メール設定を保存しました",
    "tx.smsSettingsSaved": "SMS 設定を保存しました",
    "tx.waSettingsSaved": "WhatsApp 設定を保存しました",
    "tx.testEmailSent": "✅ テストメールを送信しました",
    "tx.testSmsSent": "✅ テスト SMS を送信しました",
    "tx.testMsgSent": "✅ テストメッセージを送信しました",
    "tx.shareFailed": "共有に失敗しました",
    "tx.linkedSuccess": "✅ 連携しました",
    "tx.disconnectFailed": "連携解除に失敗しました",
    "tx.delPayoutConfirm": "この支払い記録を削除しますか？",
    "tx.delDeductConfirm": "この調整を削除しますか？",
    "tx.delEventTplConfirm": "このイベントテンプレートを削除しますか？",
    "tx.delEventConfirm": "このイベントを削除しますか？",
    "tx.delPayMethodConfirm": "この支払い方法を削除しますか？",
    "tx.gcalDisconnectConfirm": "Google カレンダーの連携を解除しますか？同期済みのイベントは削除されません。",
    "tx.gcalDisconnectedToast": "Google カレンダーの連携を解除しました",
    "tx.icalRevokeConfirm": "取り消すと既存の購読リンクが直ちに無効になります。続行しますか？",
    "tx.icalGenToast": "カレンダー購読リンクを生成しました ✅",
    "tx.icalRevokedToast": "購読リンクを取り消しました",
    "tx.icalGenFailed": "リンクの生成に失敗しました",
    "tx.icalRevokeFailed": "取り消しに失敗しました",
    "tx.googleAuthFailed": "Google ログインに失敗しました",
    "tx.payoutToast": "支払い ${amount} を記録しました ✓",
    "tx.payoutFailed": "失敗しました",
    "tx.gcalTitle": "🔗 Google カレンダー リアルタイム同期",
    "tx.gcalSub": "認証後、PettoBook の予約がリアルタイムで Google カレンダーに書き込まれます。変更・キャンセルは15秒以内に反映されます（iCal 購読は Google の4〜24時間ごとの取得に依存します）。",
    "tx.gcalConnected": "✓ 連携済み",
    "tx.gcalDisconnect": "連携解除",
    "tx.gcalConnect": "Google カレンダーを連携 →",
    "tx.staffLeaveLabelSick": "🤒 病欠",
    "tx.staffLeaveLabelPersonal": "📌 私用休暇",
    "tx.staffFallback": "スタッフ",
    "tx.custFallback": "お客様",
    "tx.unassigned": "未割り当て",
    "tx.staffOnLeaveConfirm": "注意：{name} は本日 {label} です。\n\nそれでも予約を作成しますか？",
    "tx.delSvcAffectsApts": "「{name}」を削除すると今後の予約 {n} 件に影響します。続行しますか？",
    "tx.delSvcConfirm": "「{name}」を削除しますか？",
    "tx.svcFallback": "このサービス",
    "tx.rescheduleConfirm": "{pet}（{cust}）の予約変更を確定しますか？\n\n変更前：{fromDate} {fromTime} · {fromStaff}\n変更後：{toDate} {toTime} · {toStaff}",
    "tx.enableStaffConfirm": "{name} を有効にしますか？カレンダー、割り当て、給与に再表示されます。",
    "tx.disableStaffConfirm": "{name} を無効にしますか？無効なスタッフは新規予約を受け付けなくなりますが、履歴は保持されます。",
    "tx.updateFailed": "更新に失敗しました",
    "tx.pkgRestore1Confirm": "1回分を戻しますか？",
    "tx.pkgDeduct1Confirm": "さらに1回分を差し引きますか？（例：同一サービス内での追加オプション）",
    "tx.pkgExtend30Toast": "30日延長しました ✓",
    "tx.pkgExtend30Btn": "+30日",
    "tx.pkgRestoredToast": "戻しました ✓",
    "tx.pkgDeductedToast": "1回分を差し引きました ✓",
    "tx.pkgRestoreBtn": "↩️ 戻す",
    "tx.pkgDeductBtn": "➖ 1回差し引く",
    "tx.pkgRestoreReason": "手動で戻す（1回分）",
    "tx.pkgDeductReason": "手動で差し引く（1回分）",
    "tx.opFailed": "失敗しました",
    "fpc.title": "パスワードを設定してください",
    "fpc.sub": "店舗があなたのアカウントを作成しました。データを保護するため、自分だけが知るパスワードを設定してください。",
    "fpc.newPw": "新しいパスワード（6文字以上）",
    "fpc.confirmPw": "新しいパスワード（確認）",
    "fpc.pwTooShort": "パスワードは6文字以上にしてください",
    "fpc.pwMismatch": "パスワードが一致しません",
    "fpc.updatedToast": "パスワードを更新しました ✅",
    "fpc.updateFailed": "パスワードの更新に失敗しました",
    "fpc.saving": "保存中...",
    "fpc.confirmBtn": "確定してログイン",
    "fpc.later": "あとで",
    "undo.btn": "元に戻す",
    "tx.popupBlocked": "ブラウザがポップアップをブロックしました。ポップアップを許可してもう一度お試しください。",
    "settings.group.reports": "レポート",
    "settings.group.stamp": "スタンプカード",
    "settings.group.customerAssets": "お客様の資産",
    "settings.packagesAdmin": "📦 パッケージ",
    "settings.walletsAdmin": "💳 ウォレット",
    "settings.pointsAdmin": "⭐ ポイント",
    "settings.group.advanced": "詳細設定",
    "settings.pointsSetup": "スタンプカード設定",
    "settings.support": "💬 カスタマーサポート",
    "settings.systemBackup": "システムとバックアップ",
    "settings.events": "📌 イベント管理",
    "set.basic.langMovedHint": "💡 表示言語（中文 / English）は 設定 → システムとバックアップ → 🌐 にあります。そこで切り替えると即座に反映されます。通貨は即時保存され、すべての $ 金額に反映されます。",
    "tx.eventDeleted": "イベントを削除しました",
    "tx.eventUpdated": "イベントを更新しました 📌",
    "tx.eventSaveFailed": "イベントの保存に失敗しました",
    "tx.noteSaved": "メモを保存しました",
    "tx.signed": "同意書に署名しました",
    "tx.announcementPublished": "お知らせを公開しました",
    "tx.refundFailed": "返金に失敗しました",
    "tx.cancelFailed": "キャンセルに失敗しました",
    "tx.refundCancelFailed": "支払いの取り消しに失敗しました",
    "tx.refunded": "返金しました",
    "tx.topupFailed": "チャージに失敗しました",
    "tx.savePhoneFailed": "電話番号の保存に失敗しました",
    "tx.uploadFailedToast": "アップロードに失敗しました",
    "tx.walletDeductFailed": "ウォレットの差し引きを DB に書き込めませんでした — 手動で調整してください",
    "tx.packageDelisted": "このパッケージは現在利用できません — 更新してください",
    "tx.packageCashOnly": "現金/割合クーポンはパッケージと併用できません",
    "tx.confirmRemoveStaff": "このスタッフを削除しますか？\n• スケジュールと予約は記録として保持されます\n• 今後の予約は再割り当てが必要です\n• ログインは無効になります",
    "tx.confirmDeleteShop": "この店舗を完全に削除しますか？すべてのお客様、予約、スタッフ、パッケージの記録が永久に失われます。",
    "tx.confirmCancelBooking": "この予約をキャンセルしますか？\n\n• お客様に通知されます\n• パッケージの利用回数が戻ります\n• スタンプ/ポイントが取り消されます",
    "ea.title": "予約詳細",
    "ea.editMode": "編集",
    "ea.editSave": "編集完了",
    "ea.label": "ラベル",
    "ea.noLabel": "（ラベルなし）",
    "ea.staffNote": "スタッフメモ",
    "ea.editStartTime": "開始時刻を編集",
    "ea.bookingId": "予約 ID",
    "ea.deposit": "予約金を受領",
    "ea.depositPending": "⏳ 予約金 保留中",
    "ea.depositConfirm": "✓ 予約金を確定",
    "ea.depositUncancel": "元に戻す",
    "ea.depositCompose": "内訳：",
    "ea.depositPaid": "✓ 予約金 受領済み",
    "ea.cash": "現金",
    "ea.walletShort": "ウォレット",
    "ea.pointsShort": "ポイント",
    "ea.voucher": "クーポン",
    "ea.totalCharge": "合計",
    "ea.originalPrice": "定価",
    "ea.actualReceive": "受領額",
    "ea.cashOwed": "未収現金",
    "ea.discount": "割引",
    "ea.discountAmount": "割引額",
    "ea.discountReason": "割引理由",
    "ea.addDiscount": "割引を追加",
    "ea.removeDiscount": "割引を削除",
    "ea.discountPercent": "割合",
    "ea.discountFixed": "定額",
    "ea.addSurcharge": "追加料金を追加",
    "ea.surchargeName": "項目",
    "ea.voucherUsed": "🎟️ クーポン割引",
    "ea.payButton": "受領 ",
    "ea.payDoneBtn": "✓ 全額支払い済み · ロック",
    "ea.refundReopen": "↩️ 返金 / 再開",
    "ea.refundConfirm": "⚠️ この予約は全額支払い済みです。返金して再開しますか？",
    "ea.refundedToast": "${amount} を返金しました · 支払いを再開しました",
    "ea.payLockedToast": "過去の予約 · ステータスはロック中",
    "ea.paidFullConfirm": "⚠️ この予約は全額支払い済みです。\n\nOKをタップすると返金を記録し、未払いとして再オープンします。\n返金しない場合はキャンセルをタップしてください。",
    "ea.cancelPayConfirm": "支払いをキャンセルしますか？\n${net}の返金を記録します — 売上は二重計上されません。",
    "ea.cancelPayMarkToast": "支払いマークを解除しました",
    "ea.cancelDepositConfirm": "予約金受領ステータスを取り消しますか？",
    "ea.noshowConfirm": "ノーショーにしますか？\n\n{pet} · {date} {time}\n\n• パッケージ回数を自動返却\n• スタンプ／ポイントを自動取消\n• 予約金は店舗ポリシーに従い処理",
    "ea.cancelAptPromptTitle": "キャンセル理由（お客様に通知されます）：",
    "ea.cancelAptPromptDefault": "店舗都合によるキャンセル",
    "ea.cancelAptConfirm": "予約をキャンセルしますか？\n\n{pet} · {date} {time}\n\n• お客様に通知されます\n• パッケージ回数を返却\n• スタンプ／ポイントを取消\n• {feeLine}",
    "ea.cancelAptFeeLine": "${fee}のキャンセル料を請求",
    "ea.cancelAptFullRefund": "全額返金",
    "ea.discardEditsBack": "編集を破棄して戻りますか？",
    "ea.backToDetail": "← 詳細に戻る",
    "ea.unsavedBanner": "未保存の変更があります — 下の保存をタップ。",
    "ea.markNoShowBtn": "⚠️ ノーショーにする",
    "ea.cancelAptBtn": "❌ 予約をキャンセル",
    "ea.statusBar": "ステータス",
    "ea.statusPending": "受付前",
    "ea.statusCheckedin": "チェックイン済み",
    "ea.statusGrooming": "トリミング中",
    "ea.statusDone": "完了",
    "ea.statusCancelled": "キャンセル済み",
    "ea.statusNoShow": "ノーショー",
    "ea.confirmStatus": "次に変更：",
    "ea.statusNotify": " してお客様に通知",
    "ea.report.title": "トリミングレポート",
    "ea.report.skin": "皮膚",
    "ea.report.ear": "耳",
    "ea.report.teeth": "歯",
    "ea.report.mood": "機嫌",
    "ea.report.nails": "爪",
    "ea.report.analGlands": "肛門腺",
    "ea.report.parasites": "寄生虫",
    "ea.report.eyeDischarge": "目やに",
    "ea.report.matting": "毛玉",
    "ea.report.notes": "トリマーメモ",
    "ea.report.photos": "写真",
    "ea.report.normal": "正常",
    "ea.report.attention": "要観察",
    "ea.report.abnormal": "異常",
    "ea.report.none": "なし",
    "ea.report.mild": "軽度",
    "ea.report.severe": "重度",
    "ea.report.uploadBtn": "写真をアップロード",
    "ea.report.saveBtn": "レポートを保存",
    "ea.report.sendBtn": "保存してお客様に通知",
    "ea.report.savedToast": "レポートを保存しました",
    "ea.unlock.locked": "過去の予約 · ステータスはロック中",
    "ea.unlock.unlocked": "一時的にロック解除",
    "ea.unlock.lockedSub": "誤編集防止のためロックされています。追加の支払い記録やステータス変更にはロック解除してください。",
    "ea.unlock.unlockedSub": "シートを閉じると再ロックされます",
    "ea.unlock.btn": "🔓 ロック解除",
    "ea.unlock.relock": "再ロック",
    "ea.unlock.unlockedToast": "過去の予約をロック解除 · 編集可能 🔓",
    "ea.unlock.relockedToast": "再ロックしました 🔒",
    "ea.cancelBooking": "❌ 予約をキャンセル",
    "ea.cancelReason": "キャンセル理由",
    "ea.cancelReasonPh": "理由は？",
    "ea.cancelOther": "その他",
    "ea.cancelSubmit": "キャンセルを確定",
    "ea.lateFee": "遅延料金",
    "ea.refunded": "返金済み",
    "ea.checkinTime": "チェックイン時刻",
    "ea.startTime": "開始時刻",
    "ea.completedTime": "完了時刻",
    "wi.searchCustPh": "名前または電話番号で検索",
    "wi.newCustomer": "新規顧客",
    "wi.newCustName": "顧客名",
    "wi.newCustPhone": "電話番号",
    "wi.newPet": "ペットを追加",
    "wi.newPetName": "ペット名",
    "wi.newPetBreed": "品種",
    "wi.newPetWeight": "体重 (kg)",
    "wi.newPetType": "種類",
    "wi.newPetCoat": "被毛の長さ",
    "wi.allergies": "アレルギー",
    "wi.allergiesPh": "なし／例：シャンプーアレルギー",
    "wi.service": "サービス",
    "wi.servicePicked": "選択中のサービス",
    "wi.staff": "トリマー",
    "wi.time": "時間",
    "wi.customDuration": "所要時間をカスタム",
    "wi.spaServices": "スパサービス",
    "wi.noAddons": "オプションなし",
    "wi.discount": "割引",
    "wi.discountAmt": "割引額",
    "wi.discountType": "割引タイプ",
    "wi.discountReason": "割引理由",
    "wi.discountReasonPh": "例：VIP／家族／キャンペーン",
    "wi.staffRemark": "スタッフメモ",
    "wi.staffRemarkPh": "スタッフ向け特記事項",
    "wi.useWallet": "ウォレットから差し引く",
    "wi.usePackage": "パッケージを使用",
    "wi.total": "合計",
    "wi.startNowBtn": "今すぐトリミング開始",
    "wi.checkedInBtn": "チェックインのみ",
    "wi.savedToast": "ウォークインを保存しました",
    "wi.timeConflict": "トリマーはこの時間すでに予約済みです",
    "wi.serviceRequired": "サービスを選択してください",
    "wi.petRequired": "ペットを選択してください",
    "wi.customerRequired": "顧客を選択してください",
    "wi.discountTooLarge": "割引が価格を超えることはできません",
    "wi.noStaff": "対応可能なトリマーがいません",
    "st.title": "スタッフ",
    "st.addStaff": "スタッフを追加",
    "st.searchPh": "スタッフを検索…",
    "st.empty": "スタッフがいません",
    "st.emptySub": "「スタッフを追加」をタップして最初のスタッフを追加。",
    "st.editTitle": "スタッフプロフィール",
    "st.editNewTitle": "スタッフを追加",
    "st.tabBasic": "基本",
    "st.tabService": "サービス",
    "st.tabTime": "勤務時間",
    "st.tabLeave": "休暇",
    "st.tabPermissions": "権限",
    "st.name": "名前",
    "st.phone": "電話番号",
    "st.email": "メール",
    "st.password": "パスワード",
    "st.role": "役割",
    "st.roleOwner": "オーナー",
    "st.roleAdmin": "管理者",
    "st.roleStaff": "スタッフ",
    "st.jobType": "雇用形態",
    "st.fullTime": "正社員",
    "st.partTime": "パート",
    "st.settleMode": "精算方法",
    "st.settleDaily": "日次",
    "st.settleMonthly": "月次",
    "st.mpfEnabled": "MPFを有効化",
    "st.joinDate": "入社日",
    "st.leaveDate": "退社日",
    "st.baseSalary": "基本給",
    "st.hourlyRate": "時給",
    "st.commissionMode": "歩合タイプ",
    "st.commissionPercent": "歩合率",
    "st.commissionFixed": "固定",
    "st.commissionRate": "歩合率 (%)",
    "st.commissionEnabled": "歩合を有効化",
    "st.commissionTiers": "段階歩合",
    "st.commissionTiersHint": "[from]を超える売上は[%]で支給",
    "st.addTier": "+ 段階を追加",
    "st.tierFrom": "開始額",
    "st.tierRate": "%",
    "st.serviceCommission": "サービス別歩合",
    "st.serviceCommissionHint": "サービスごとにデフォルト歩合率を上書きします。",
    "st.packageCommission": "パッケージ販売歩合 (%)",
    "st.packageCommissionHint": "このスタッフが販売したパッケージ1件あたりの販売価格に対する%。",
    "st.packageRedemptionCommission": "パッケージトリミング歩合 (%)",
    "st.packageRedemptionCommissionHint": "パッケージ来店1回あたりの計上売上に対する%。",
    "st.canService": "対応サービス",
    "st.canServiceHint": "チェックを外したサービスでは、顧客の予約ページにこのスタッフが表示されません。",
    "st.weeklySchedule": "週間スケジュール",
    "st.workDay": "勤務",
    "st.offDay": "休み",
    "st.dayHours": "勤務時間",
    "st.startTime": "開始",
    "st.endTime": "終了",
    "st.timeOff": "休暇",
    "st.addTimeOff": "+ 休暇を追加",
    "st.timeOffType": "種類",
    "st.timeOffSick": "病欠",
    "st.timeOffPersonal": "私用",
    "st.timeOffAnnual": "年次休暇",
    "st.timeOffOther": "その他",
    "st.timeOffStart": "開始日",
    "st.timeOffEnd": "終了日",
    "st.timeOffReason": "理由",
    "st.permissions": "権限",
    "st.permViewRevenue": "売上を表示",
    "st.permManageCRM": "顧客を管理",
    "st.permManageStaff": "スタッフを管理",
    "st.permManageSettings": "設定を管理",
    "st.permManagePackages": "パッケージを管理",
    "st.permManageReports": "レポートを表示",
    "st.saveBtn": "保存",
    "st.deleteBtn": "スタッフを削除",
    "st.savedToast": "スタッフを保存しました ✓",
    "st.deleteConfirm": "このスタッフを削除しますか？",
    "st.deletedToast": "スタッフを削除しました",
    "st.permViewWage": "給与を表示",
    "st.permPosTab": "POS",
    "st.permRefund": "返金",
    "st.staff.todayBookings": "本日の予約",
    "st.staff.workHrs": "労働時間",
    "st.staff.tipsToday": "本日のチップ",
    "st.staff.payroll.title": "給与",
    "st.staff.payroll.headerBasic": "基本給",
    "st.staff.payroll.headerHourly": "時給",
    "st.staff.payroll.headerCommission": "歩合",
    "st.staff.payroll.headerTip": "チップ",
    "st.staff.payroll.headerDeduct": "控除",
    "st.staff.payroll.headerNet": "差引支給",
    "st.staff.payroll.recordWage": "本日の給与を記録",
    "st.staff.payroll.payoutOne": "支払い（本日分）",
    "st.staff.payroll.payoutMonth": "支払い（今月分）",
    "st.staff.payroll.payMethod": "支払い方法",
    "st.staff.payroll.cash": "現金",
    "st.staff.payroll.transfer": "振込",
    "st.staff.payroll.paidDaily": "✅ 日次支払い済み",
    "st.staff.payroll.paidMonthly": "月次支払い済み",
    "st.staff.payroll.unpaid": "⏳ 未払い",
    "st.staff.payroll.unpaidMonthly": "未払い（月末精算）",
    "st.staff.payroll.empty": "この日の給与記録はありません",
    "st.staff.payroll.totalToday": "本日合計",
    "st.staff.payroll.totalMonth": "今月合計",
    "st.staff.payroll.from": "開始",
    "st.staff.payroll.to": "終了",
    "st.staff.payroll.dateRange": "期間",
    "st.staff.payroll.saveDayBtn": "✓ 本日の給与を記録",
    "st.staff.payroll.savedToast": "給与を記録しました ✓",
    "st.staff.payroll.payoutToast": "支払いが完了しました ✓",
    "st.staff.payroll.confirmPayout": "支払いを確定しますか？",
    "st.staff.payroll.bonus": "ボーナス",
    "st.staff.payroll.deduction": "控除",
    "st.staff.payroll.addBonusBtn": "+ ボーナス／控除",
    "st.staff.payroll.adjKind.bonus": "ボーナス",
    "st.staff.payroll.adjKind.late": "遅刻",
    "st.staff.payroll.adjKind.accident": "事故",
    "st.staff.payroll.adjKind.uniform": "制服",
    "st.staff.payroll.adjKind.advance": "前借り",
    "st.staff.payroll.adjKind.fine": "罰金",
    "st.staff.payroll.adjKind.other": "その他"
  },
};

const SUPPORTED = Object.keys(STRINGS);
const DEFAULT_LOCALE = "zh-HK";
const STORAGE_KEY = "petos:locale";

let currentLocale = (() => {
  if (typeof localStorage === "undefined") return DEFAULT_LOCALE;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch { /* ignore */ }
  if (typeof navigator !== "undefined") {
    // Auto-detect from the browser/OS language on first visit (no stored pref).
    const langs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ""]);
    for (const raw of langs) {
      const lang = String(raw || "").toLowerCase();
      if (lang.startsWith("ja")) return "ja";
      if (lang.startsWith("en")) return "en";
      if (lang.startsWith("zh")) return "zh-HK";
    }
    // A language we don't have (Korean, French, …): English, not Chinese —
    // most people can read it, and the Chinese UI is the Hong Kong default
    // only for phones set to Chinese. (The string tables still fall back to
    // zh-HK for any key English lacks.)
    return "en";
  }
  return DEFAULT_LOCALE;
})();

const listeners = new Set();

export function t(key, fallback = null) {
  const dict = STRINGS[currentLocale] || STRINGS[DEFAULT_LOCALE];
  if (dict[key] != null) return dict[key];
  // Fallback chain: current → English → zh-HK → caller fallback → key.
  // English before zh-HK so a partially-translated locale (e.g. ja) shows
  // English for missing keys rather than Chinese.
  if (STRINGS.en && STRINGS.en[key] != null) return STRINGS.en[key];
  const def = STRINGS[DEFAULT_LOCALE][key];
  if (def != null) return def;
  return fallback != null ? fallback : key;
}

export function getLocale() { return currentLocale; }
export function listLocales() {
  return SUPPORTED.map(code => ({ code, name: STRINGS[code]["lang.name"] }));
}
export function setLocale(next) {
  if (!SUPPORTED.includes(next) || next === currentLocale) return;
  currentLocale = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  listeners.forEach(fn => { try { fn(next); } catch { /* ignore */ } });
  // The POS holds translated labels in module-level constants; reload once so
  // they pick up the new language (only when the POS has been opened).
  if (typeof window !== "undefined" && window.__petosPosLoaded) setTimeout(() => { try { window.location.reload(); } catch { /* ignore */ } }, 80);
}

// Advance to the next supported locale (zh-HK → en → ja → …). Used by the
// quick toggle pills (the full picker uses listLocales()).
export function cycleLocale() {
  const i = SUPPORTED.indexOf(currentLocale);
  setLocale(SUPPORTED[(i + 1) % SUPPORTED.length]);
}
// Short label for a locale code, for compact toggle pills.
export function localeShort(code = currentLocale) {
  return ({ "zh-HK": "繁", "zh-TW": "繁", "en": "EN", "ja": "日" })[code] || code;
}

// React hook — components re-render on locale change.
export function useLocale() {
  const [loc, setLoc] = useState(currentLocale);
  useEffect(() => {
    const fn = (next) => setLoc(next);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  return loc;
}

// Currency + language constants shared by the app. Real i18n string-extraction
// is a follow-up; for now this just keeps the dropdowns and `fmtCurrency()`
// in one place so the UI can be re-pointed at a translation layer later.

export const CURRENCIES = [
  { code: "HKD", name: "港幣 HKD", symbol: "HK$" },
  { code: "TWD", name: "新台幣 TWD", symbol: "NT$" },
  { code: "CNY", name: "人民幣 CNY", symbol: "¥" },
  { code: "USD", name: "美元 USD", symbol: "US$" },
  { code: "SGD", name: "新加坡幣 SGD", symbol: "S$" },
  { code: "MYR", name: "馬幣 MYR", symbol: "RM" },
  { code: "JPY", name: "日圓 JPY", symbol: "¥" },
  { code: "KRW", name: "韓圜 KRW", symbol: "₩" },
  { code: "THB", name: "泰銖 THB", symbol: "฿" },
  { code: "PHP", name: "菲律賓披索 PHP", symbol: "₱" },
  { code: "VND", name: "越南盾 VND", symbol: "₫" },
  { code: "IDR", name: "印尼盾 IDR", symbol: "Rp" },
  { code: "AUD", name: "澳元 AUD", symbol: "A$" },
  { code: "NZD", name: "紐元 NZD", symbol: "NZ$" },
  { code: "GBP", name: "英鎊 GBP", symbol: "£" },
  { code: "EUR", name: "歐元 EUR", symbol: "€" },
  { code: "CAD", name: "加幣 CAD", symbol: "C$" },
];

export const SYMBOL_BY_CODE = Object.fromEntries(CURRENCIES.map(c => [c.code, c.symbol]));

// fmt: prefix + formatted integer with thousands separator.
export const fmtCurrency = (amount, code = "HKD") => {
  const sym = SYMBOL_BY_CODE[code] || "$";
  const n = Number(amount) || 0;
  return sym + Math.round(n).toLocaleString("en-US");
};

// Languages — stored on the tenant; the dropdown displays them but UI
// translation is a separate (parked) milestone.
export const LANGUAGES = [
  { code: "zh-HK", name: "繁體中文（香港）" },
  { code: "zh-TW", name: "繁體中文（台灣）" },
  { code: "zh-CN", name: "简体中文" },
  { code: "en", name: "English" },
  { code: "ja", name: "日本語" },
  { code: "ko", name: "한국어" },
  { code: "th", name: "ภาษาไทย" },
  { code: "vi", name: "Tiếng Việt" },
  { code: "id", name: "Bahasa Indonesia" },
  { code: "ms", name: "Bahasa Melayu" },
];
